// AUTO-GENERATED FILE – DO NOT EDIT BY HAND
// This file is generated by scripts/buildToolSpecs.ts
// Run 'npm run build:tool-specs' to regenerate

import type { RawToolSpec } from "../harmonizer/specLoader";

/**
 * All TOOL_SPEC files discovered and bundled at build time.
 * This array contains the raw, unnormalized tool specifications.
 * Use loadAllToolSpecs() from specLoader.ts to get normalized specs.
 */
export const ALL_TOOL_SPECS: RawToolSpec[] = [
  {
    "tool_spec_version": "1.0",
    "identity": {
      "tool_name": "AHP",
      "short_label": "Analytic Hierarchy Process",
      "description": "Multi-criteria decision-making tool using pairwise comparisons to derive criterion weights via eigenvector calculation, with consistency validation and project scoring.",
      "category": "multi_criteria_decision_making",
      "status": "stable"
    },
    "semantics": {
      "purpose": "Arrange-phase prioritization by decomposing decisions into a hierarchy (Goal → Criteria → Projects), mathematically weighting criteria through pairwise comparisons, and scoring projects against weighted criteria to produce ranked results.",
      "requires_comparable_items": true,
      "notes_on_comparability": "Items (projects) must be evaluated against the same set of criteria. Criteria weights are derived from pairwise comparisons using Saaty's 1-9 scale. Items are scored on a 0-1 scale per criterion, then aggregated using global criterion weights."
    },
    "inputs": {
      "required_item_fields": [
        "id",
        "label"
      ],
      "required_item_attributes": [
        {
          "key": "cost",
          "type": "numeric",
          "description": "Cost or effort estimate for the project/item. Used for efficiency calculation (score/cost) and cost-value matrix visualization."
        }
      ],
      "optional_item_attributes": [],
      "criteria_set_contract": {
        "allowed_criteria_ids": "dynamic",
        "requires_explicit_criteria_set": true,
        "notes": "Criteria are user-defined during setup phase. Must have 2-9 criteria (recommended 5-9 per Miller's Law). Each criterion requires: id, name, description (optional). Criteria weights are calculated via pairwise comparisons, not provided directly."
      },
      "lens_support": {
        "requires_lens": false,
        "notes": "If a lens is provided, the tool only evaluates items within that lens. The hierarchy and criteria weights are calculated once and applied to all items in the lens."
      },
      "tool_config": {
        "fields": [
          {
            "name": "consistency_threshold",
            "type": "numeric",
            "allowed_values": null,
            "default": 0.1,
            "description": "Maximum acceptable Consistency Ratio (CR). If CR exceeds this threshold, user is warned to review comparisons. Standard AHP threshold is 0.10."
          },
          {
            "name": "eigenvector_method",
            "type": "enum",
            "allowed_values": [
              "mean_of_normalized",
              "power_iteration"
            ],
            "default": "mean_of_normalized",
            "description": "Method for calculating priority vector. 'mean_of_normalized' is O(n²) and sufficient for n<10. 'power_iteration' provides higher precision for larger matrices."
          },
          {
            "name": "allow_inconsistent_proceed",
            "type": "boolean",
            "allowed_values": null,
            "default": false,
            "description": "Whether to allow proceeding to evaluation phase when consistency ratio exceeds threshold. Default false requires user to fix inconsistencies."
          }
        ],
        "supports_defaults": true
      }
    },
    "algorithm": {
      "score_model_type": "weighted_sum",
      "formula_description": "For each item: final_score = Σ(criterion.global_weight × item.score[criterion.id]). Efficiency = final_score / cost.",
      "formula_variables": {
        "criterion.global_weight": "Calculated via eigenvector of pairwise comparison matrix. Sum of all global weights = 1.0.",
        "item.score[criterion.id]": "Numeric score 0-1 for item's performance on criterion. User-provided during evaluation phase."
      },
      "weight_derivation": {
        "method": "pairwise_comparison_eigenvector",
        "scale": "Saaty 1-9 scale with reciprocals",
        "process": [
          "User performs pairwise comparisons for all criterion pairs using Saaty scale (1=equal, 3=moderate, 5=strong, 7=very strong, 9=extreme)",
          "Build reciprocal comparison matrix A where a_ij = user_value and a_ji = 1/user_value",
          "Normalize matrix column-wise (divide each column by its sum)",
          "Calculate priority vector by averaging rows of normalized matrix (Mean of Normalized Values method)",
          "Calculate lambda_max: (A × priority_vector) / priority_vector, then average",
          "Calculate Consistency Index: CI = (lambda_max - n) / (n - 1)",
          "Calculate Consistency Ratio: CR = CI / RI, where RI is Random Index from Saaty's table",
          "If CR < threshold (default 0.10), weights are valid. Otherwise, warn user."
        ],
        "random_index_table": {
          "1": 0,
          "2": 0,
          "3": 0.58,
          "4": 0.9,
          "5": 1.12,
          "6": 1.24,
          "7": 1.32,
          "8": 1.41,
          "9": 1.45
        }
      },
      "scaling_and_normalization": {
        "normalizes_weights": "Priority vector sums to 1.0. Global weights for top-level criteria equal local weights.",
        "normalizes_item_scores": "User provides scores on 0-1 scale per criterion. No automatic normalization applied.",
        "normalizes_final_scores": "Final scores are not normalized; they represent weighted sums. Efficiency scores are final_score / cost."
      },
      "handles_missing_values": {
        "strategy": "reject_item",
        "rules": [
          "If an item is missing a score for any required criterion, it cannot be fully evaluated.",
          "Items with missing scores are excluded from final ranking but may appear in results with a warning.",
          "Missing pairwise comparisons prevent weight calculation; user must complete all comparisons."
        ]
      },
      "tie_breaking": {
        "primary": "higher final_score wins",
        "secondary": "higher efficiency_score (final_score / cost) wins",
        "tertiary": "alphabetical by item.label",
        "quaternary": "stable by original import order"
      },
      "determinism": true,
      "consistency_validation": {
        "enabled": true,
        "method": "consistency_ratio",
        "threshold": 0.1,
        "behavior": "warn_and_block_if_above_threshold",
        "notes": "AHP validates that user's pairwise comparisons are logically consistent. Inconsistencies (e.g., A>B, B>C, but C>A) result in CR > 0.10. User must review and adjust comparisons."
      }
    },
    "outputs": {
      "score_set_schema": {
        "per_item_fields": [
          {
            "name": "item_id",
            "type": "string"
          },
          {
            "name": "final_score",
            "type": "numeric",
            "description": "Weighted sum score: Σ(criterion.global_weight × item.score[criterion.id])"
          },
          {
            "name": "efficiency_score",
            "type": "numeric",
            "description": "Value-for-money ratio: final_score / cost. Higher is better."
          },
          {
            "name": "rank",
            "type": "integer",
            "description": "1 = highest priority within the evaluated set, based on final_score"
          },
          {
            "name": "cost",
            "type": "numeric",
            "description": "Cost/effort estimate for the item"
          },
          {
            "name": "criterion_scores",
            "type": "object",
            "description": "Map of criterion_id to score (0-1) for this item"
          },
          {
            "name": "criterion_contributions",
            "type": "object",
            "description": "Map of criterion_id to contribution (criterion.global_weight × item.score[criterion.id])"
          }
        ],
        "annotations": [
          {
            "name": "tool_name",
            "type": "string",
            "value": "AHP"
          },
          {
            "name": "consistency_ratio",
            "type": "numeric",
            "description": "CR value from criteria pairwise comparison matrix"
          },
          {
            "name": "lambda_max",
            "type": "numeric",
            "description": "Principal eigenvalue from criteria comparison matrix"
          },
          {
            "name": "criteria_weights",
            "type": "object",
            "description": "Map of criterion_id to global_weight (calculated via eigenvector)"
          }
        ]
      },
      "tool_run_metadata": {
        "records_raw_inputs": true,
        "raw_inputs_schema": {
          "pairwise_comparisons": "Array of {row: index, col: index, value: Saaty_scale_value}",
          "item_scores": "Map of item_id to Map of criterion_id to score (0-1)",
          "criteria_definitions": "Array of {id, name, description, level, parentId}",
          "goal": "string"
        },
        "includes_consistency_metrics": true,
        "includes_sensitivity_analysis": false,
        "notes": "Metadata includes full pairwise comparison matrix, calculated weights, and consistency metrics for audit trail."
      }
    },
    "interaction_model": {
      "ui_pattern": "multi_phase_wizard",
      "supports_inline_edit": true,
      "supports_batch_edit": false,
      "step_flow": [
        {
          "phase": "setup",
          "steps": [
            "define_goal",
            "add_criteria",
            "validate_criteria_count"
          ],
          "validation": "Requires goal text and 2-9 criteria"
        },
        {
          "phase": "weighting",
          "steps": [
            "pairwise_comparison_interface",
            "real_time_consistency_feedback",
            "review_and_adjust_if_inconsistent"
          ],
          "validation": "All pairwise comparisons complete, CR < threshold"
        },
        {
          "phase": "evaluation",
          "steps": [
            "add_projects_items",
            "score_items_against_criteria",
            "validate_all_scores_provided"
          ],
          "validation": "All items have scores for all criteria"
        },
        {
          "phase": "results",
          "steps": [
            "view_ranked_list",
            "view_cost_value_matrix",
            "view_criterion_contributions",
            "export_or_save"
          ],
          "validation": "None required"
        }
      ],
      "validation_behavior": {
        "on_invalid_item": "mark_missing_fields_and_exclude_from_scoring",
        "on_inconsistent_comparisons": "block_progression_show_warning_with_specific_conflicts",
        "on_any_error": "allow_review_with_clear_error_messages"
      },
      "real_time_feedback": {
        "consistency_meter": "Updates after each pairwise comparison, shows CR value and color-coded status (green < 0.10, red >= 0.10)",
        "progress_tracking": "Shows completion percentage for pairwise comparisons",
        "score_preview": "Not applicable - scores calculated only after all inputs complete"
      }
    },
    "constraints_and_assumptions": {
      "max_items_tested": 1000,
      "max_criteria": 9,
      "min_criteria": 2,
      "performance_notes": "O(n²) for pairwise comparisons (n = number of criteria). O(n×m) for scoring (n = criteria, m = items). Eigenvector calculation is O(n²) using Mean of Normalized Values method.",
      "assumes_attribute_types": "Numeric fields (cost, scores) must be parsed by platform. Criteria are user-defined strings.",
      "mathematical_assumptions": [
        "Pairwise comparison matrix is reciprocal (a_ji = 1/a_ij)",
        "Diagonal elements are 1 (a_ii = 1)",
        "User judgments follow Saaty scale semantics",
        "Item scores are on consistent 0-1 scale across all criteria"
      ]
    },
    "toolRequirements": {
      "prerequisites": [
        "CRITERIA_LOCKED",
        "SCALES_DEFINED",
        "POOL_NONEMPTY"
      ],
      "requiredCriteria": [
        {
          "tag": "rubric:*",
          "criterionTypes": [
            "numeric",
            "ordinal"
          ],
          "minCount": 2
        }
      ],
      "poolRequirements": {
        "minItems": 2,
        "requireLocked": false
      }
    },
    "ui_assumptions": [
      "User can perform pairwise comparisons sequentially",
      "User understands Saaty scale (1-9) semantics",
      "User will review and adjust if consistency ratio is high"
    ],
    "test_vectors": [
      {
        "name": "simple_three_criteria_two_items",
        "goal": "Select best project for Q1",
        "criteria": [
          {
            "id": "financial_value",
            "name": "Financial Value",
            "description": "Expected revenue impact"
          },
          {
            "id": "strategic_alignment",
            "name": "Strategic Alignment",
            "description": "Alignment with company strategy"
          },
          {
            "id": "risk",
            "name": "Risk",
            "description": "Implementation risk (lower is better, but we'll invert in scoring)"
          }
        ],
        "pairwise_comparisons": [
          {
            "row": 0,
            "col": 1,
            "value": 3
          },
          {
            "row": 0,
            "col": 2,
            "value": 5
          },
          {
            "row": 1,
            "col": 2,
            "value": 2
          }
        ],
        "items": [
          {
            "id": "project_a",
            "label": "Project A",
            "attributes": {
              "cost": 10
            },
            "scores": {
              "financial_value": 0.8,
              "strategic_alignment": 0.9,
              "risk": 0.3
            }
          },
          {
            "id": "project_b",
            "label": "Project B",
            "attributes": {
              "cost": 5
            },
            "scores": {
              "financial_value": 0.6,
              "strategic_alignment": 0.7,
              "risk": 0.2
            }
          }
        ],
        "tool_config": {
          "consistency_threshold": 0.1,
          "eigenvector_method": "mean_of_normalized",
          "allow_inconsistent_proceed": false
        },
        "expected_weights": {
          "financial_value": 0.648,
          "strategic_alignment": 0.23,
          "risk": 0.122
        },
        "expected_scores": [
          {
            "item_id": "project_a",
            "final_score": 0.696,
            "efficiency_score": 0.0696,
            "rank": 1
          },
          {
            "item_id": "project_b",
            "final_score": 0.549,
            "efficiency_score": 0.1098,
            "rank": 2
          }
        ],
        "notes": "Weights are approximate due to rounding. Project B has higher efficiency but lower final score. Actual implementation may vary slightly in precision."
      },
      {
        "name": "fruit_example_validation",
        "goal": "Select best fruit",
        "criteria": [
          {
            "id": "apple",
            "name": "Apple"
          },
          {
            "id": "banana",
            "name": "Banana"
          },
          {
            "id": "cherry",
            "name": "Cherry"
          }
        ],
        "pairwise_comparisons": [
          {
            "row": 0,
            "col": 1,
            "value": 0.333
          },
          {
            "row": 0,
            "col": 2,
            "value": 5
          },
          {
            "row": 1,
            "col": 2,
            "value": 7
          }
        ],
        "items": [],
        "tool_config": {
          "consistency_threshold": 0.1
        },
        "expected_weights": {
          "apple": 0.283,
          "banana": 0.643,
          "cherry": 0.074
        },
        "expected_consistency_ratio": 0.056,
        "notes": "This is the standard AHP validation test case from Saaty's work. Apple vs Banana = 1/3, Apple vs Cherry = 5, Banana vs Cherry = 7. Expected weights: Apple ~0.28, Banana ~0.64, Cherry ~0.07. CR should be < 0.10."
      }
    ]
  },
  {
    "tool_spec_version": "1.0",
    "identity": {
      "tool_name": "Buy A Feature",
      "short_label": "Buy A Feature",
      "description": "An innovation game where stakeholders are given a set amount of 'money' to 'purchase' features they believe are most important. Forces trade-offs and provides direct insight into preferences.",
      "category": "simulation_game",
      "status": "stable"
    },
    "semantics": {
      "purpose": "Arrange-phase prioritization through simulated purchasing. Participants allocate a constrained budget to purchase features, with the most-purchased features receiving highest priority.",
      "requires_comparable_items": true,
      "notes_on_comparability": "Items must have a price attribute. Items are compared by number of purchases across all participants."
    },
    "inputs": {
      "required_item_fields": [
        "id",
        "label"
      ],
      "required_item_attributes": [
        {
          "key": "price",
          "type": "numeric",
          "description": "Price of the feature in budget currency units. Must be > 0. Prices should reflect actual effort, value, or priority to simulate real decision-making."
        }
      ],
      "optional_item_attributes": [
        {
          "key": "description",
          "type": "string",
          "description": "Optional description of the feature to help participants make purchasing decisions."
        }
      ],
      "criteria_set_contract": {
        "allowed_criteria_ids": [],
        "requires_explicit_criteria_set": false,
        "notes": "Buy A Feature does not use a CriteriaSet. Pricing is embedded in item attributes."
      },
      "lens_support": {
        "requires_lens": false,
        "notes": "If a lens is provided, only items within that lens are available for purchase."
      },
      "tool_config": {
        "fields": [
          {
            "name": "num_participants",
            "type": "integer",
            "allowed_values": null,
            "default": 1,
            "description": "Number of participants who will purchase features. Must be >= 1 and <= 20."
          },
          {
            "name": "budget_percentage",
            "type": "numeric",
            "allowed_values": null,
            "default": 0.4,
            "description": "Percentage of total price of all items that becomes the total budget. Default is 0.4 (40%)."
          },
          {
            "name": "budget_distribution",
            "type": "enum",
            "allowed_values": [
              "equal"
            ],
            "default": "equal",
            "description": "How budget is distributed among participants. Currently only 'equal' is supported."
          }
        ],
        "supports_defaults": true
      }
    },
    "algorithm": {
      "score_model_type": "purchase_count",
      "formula_description": "Priority = number of purchases across all participants. Total budget = sum(item.price) * budget_percentage. Budget per participant = total_budget / num_participants.",
      "formula_variables": {
        "price": "numeric, > 0, per item",
        "total_budget": "sum of all item prices * budget_percentage",
        "budget_per_participant": "total_budget / num_participants",
        "purchases": "count of purchases per item across all participants"
      },
      "scaling_and_normalization": {
        "normalizes_confidence": null,
        "normalizes_other_fields": "none"
      },
      "handles_missing_values": {
        "strategy": "reject_item",
        "rules": [
          "If an item is missing the 'price' attribute or price is <= 0, the item is excluded from the purchasing session.",
          "If a participant's remaining budget is insufficient for a purchase, the purchase is rejected."
        ]
      },
      "tie_breaking": {
        "primary": "higher purchase count wins",
        "secondary": "higher total_spent wins (if purchase counts are equal)",
        "tertiary": "alphabetical by item.label"
      },
      "determinism": false,
      "notes": "Results depend on participant choices, which are non-deterministic. However, given the same participant choices, results are deterministic."
    },
    "outputs": {
      "score_set_schema": {
        "per_item_fields": [
          {
            "name": "item_id",
            "type": "string"
          },
          {
            "name": "purchases",
            "type": "integer",
            "description": "Total number of times this item was purchased across all participants."
          },
          {
            "name": "total_spent",
            "type": "numeric",
            "description": "Total amount of budget spent on this item (purchases * price)."
          },
          {
            "name": "rank",
            "type": "integer",
            "description": "Rank based on purchase count (1 = most purchased, highest priority)."
          }
        ],
        "annotations": [
          {
            "name": "tool_name",
            "type": "string",
            "value": "Buy A Feature"
          },
          {
            "name": "total_budget",
            "type": "numeric",
            "description": "Total budget allocated across all participants."
          },
          {
            "name": "total_spent",
            "type": "numeric",
            "description": "Total amount of budget actually spent."
          },
          {
            "name": "num_participants",
            "type": "integer",
            "description": "Number of participants in the session."
          }
        ]
      },
      "tool_run_metadata": {
        "records_raw_inputs": true,
        "raw_inputs_schema": {
          "item_prices": "object mapping item_id to price",
          "participant_budgets": "array of participant budgets",
          "participant_purchases": "array of purchase records per participant"
        },
        "additional_fields": [
          {
            "name": "participant_breakdown",
            "type": "array",
            "description": "Per-participant purchase history showing which items each participant purchased and in what quantities."
          }
        ]
      }
    },
    "interaction_model": {
      "ui_pattern": "sequential_turn_based_purchasing",
      "supports_inline_edit": true,
      "supports_batch_edit": false,
      "step_flow": [
        "input_items_with_prices",
        "configure_participants",
        "review_budget_summary",
        "participant_turn_purchasing",
        "next_participant",
        "review_results"
      ],
      "validation_behavior": {
        "on_invalid_item": "exclude_from_session_with_error",
        "on_any_error": "prevent_purchase_and_show_feedback"
      },
      "interaction_notes": "Participants take turns purchasing features. Each participant can purchase multiple quantities of the same feature. Participants can remove purchases before moving to the next participant. The tool enforces budget constraints in real-time."
    },
    "constraints_and_assumptions": {
      "max_items_tested": 100,
      "max_participants": 20,
      "performance_notes": "O(n * p) where n = number of items and p = number of participants. Real-time budget validation on each purchase.",
      "assumes_attribute_types": "Price must be numeric and > 0. Platform must parse numeric values before tool execution.",
      "assumptions": [
        "Prices reflect actual effort, value, or priority",
        "Total budget (40% of total price) creates scarcity and forces trade-offs",
        "Equal budget distribution ensures fairness",
        "Participants make independent purchasing decisions"
      ]
    },
    "toolRequirements": {
      "prerequisites": [
        "CRITERIA_LOCKED",
        "SCALES_DEFINED",
        "POOL_NONEMPTY",
        "VALUES_SUFFICIENT"
      ],
      "requiredCriteria": [
        {
          "tag": "term:price",
          "criterionTypes": [
            "numeric"
          ],
          "minCount": 1
        }
      ],
      "poolRequirements": {
        "minItems": 2,
        "requireLocked": false
      }
    },
    "test_vectors": [
      {
        "name": "simple_three_feature_example",
        "items": [
          {
            "id": "feature_a",
            "label": "Feature A",
            "attributes": {
              "price": 10
            }
          },
          {
            "id": "feature_b",
            "label": "Feature B",
            "attributes": {
              "price": 20
            }
          },
          {
            "id": "feature_c",
            "label": "Feature C",
            "attributes": {
              "price": 15
            }
          }
        ],
        "tool_config": {
          "num_participants": 2,
          "budget_percentage": 0.4,
          "budget_distribution": "equal"
        },
        "participant_actions": [
          {
            "participant_id": "participant_0",
            "purchases": [
              {
                "feature_id": "feature_a",
                "quantity": 1
              },
              {
                "feature_id": "feature_c",
                "quantity": 1
              }
            ]
          },
          {
            "participant_id": "participant_1",
            "purchases": [
              {
                "feature_id": "feature_a",
                "quantity": 2
              }
            ]
          }
        ],
        "expected_scores": [
          {
            "item_id": "feature_a",
            "purchases": 3,
            "total_spent": 30,
            "rank": 1
          },
          {
            "item_id": "feature_c",
            "purchases": 1,
            "total_spent": 15,
            "rank": 2
          },
          {
            "item_id": "feature_b",
            "purchases": 0,
            "total_spent": 0,
            "rank": 3
          }
        ],
        "expected_metadata": {
          "total_budget": 18,
          "total_spent": 45,
          "num_participants": 2,
          "budget_per_participant": 9
        }
      },
      {
        "name": "tie_breaking_example",
        "items": [
          {
            "id": "feature_x",
            "label": "Feature X",
            "attributes": {
              "price": 10
            }
          },
          {
            "id": "feature_y",
            "label": "Feature Y",
            "attributes": {
              "price": 5
            }
          }
        ],
        "tool_config": {
          "num_participants": 2,
          "budget_percentage": 0.4,
          "budget_distribution": "equal"
        },
        "participant_actions": [
          {
            "participant_id": "participant_0",
            "purchases": [
              {
                "feature_id": "feature_x",
                "quantity": 1
              },
              {
                "feature_id": "feature_y",
                "quantity": 2
              }
            ]
          },
          {
            "participant_id": "participant_1",
            "purchases": [
              {
                "feature_id": "feature_x",
                "quantity": 1
              },
              {
                "feature_id": "feature_y",
                "quantity": 2
              }
            ]
          }
        ],
        "expected_scores": [
          {
            "item_id": "feature_y",
            "purchases": 4,
            "total_spent": 20,
            "rank": 1
          },
          {
            "item_id": "feature_x",
            "purchases": 2,
            "total_spent": 20,
            "rank": 2
          }
        ],
        "notes": "Both features have same total_spent (20.0), but feature_y has more purchases (4 vs 2), so it ranks higher."
      }
    ]
  },
  {
    "tool_spec_version": "1.0",
    "identity": {
      "tool_name": "Buy A Feature",
      "short_label": "Buy A Feature",
      "description": "An innovation game where stakeholders are given a set amount of 'money' to 'purchase' features they believe are most important. Forces trade-offs and provides direct insight into preferences.",
      "category": "simulation_game",
      "status": "stable"
    },
    "semantics": {
      "purpose": "Arrange-phase prioritization through simulated purchasing. Participants allocate a constrained budget to purchase features, with the most-purchased features receiving highest priority.",
      "requires_comparable_items": true,
      "notes_on_comparability": "Items must have a price attribute. Items are compared by number of purchases across all participants."
    },
    "inputs": {
      "required_item_fields": [
        "id",
        "label"
      ],
      "required_item_attributes": [
        {
          "key": "price",
          "type": "numeric",
          "description": "Price of the feature in budget currency units. Must be > 0. Prices should reflect actual effort, value, or priority to simulate real decision-making."
        }
      ],
      "optional_item_attributes": [
        {
          "key": "description",
          "type": "string",
          "description": "Optional description of the feature to help participants make purchasing decisions."
        }
      ],
      "criteria_set_contract": {
        "allowed_criteria_ids": [],
        "requires_explicit_criteria_set": false,
        "notes": "Buy A Feature does not use a CriteriaSet. Pricing is embedded in item attributes."
      },
      "lens_support": {
        "requires_lens": false,
        "notes": "If a lens is provided, only items within that lens are available for purchase."
      },
      "tool_config": {
        "fields": [
          {
            "name": "num_participants",
            "type": "integer",
            "allowed_values": null,
            "default": 1,
            "description": "Number of participants who will purchase features. Must be >= 1 and <= 20."
          },
          {
            "name": "budget_percentage",
            "type": "numeric",
            "allowed_values": null,
            "default": 0.4,
            "description": "Percentage of total price of all items that becomes the total budget. Default is 0.4 (40%)."
          },
          {
            "name": "budget_distribution",
            "type": "enum",
            "allowed_values": [
              "equal"
            ],
            "default": "equal",
            "description": "How budget is distributed among participants. Currently only 'equal' is supported."
          }
        ],
        "supports_defaults": true
      }
    },
    "algorithm": {
      "score_model_type": "purchase_count",
      "formula_description": "Priority = number of purchases across all participants. Total budget = sum(item.price) * budget_percentage. Budget per participant = total_budget / num_participants.",
      "formula_variables": {
        "price": "numeric, > 0, per item",
        "total_budget": "sum of all item prices * budget_percentage",
        "budget_per_participant": "total_budget / num_participants",
        "purchases": "count of purchases per item across all participants"
      },
      "scaling_and_normalization": {
        "normalizes_confidence": null,
        "normalizes_other_fields": "none"
      },
      "handles_missing_values": {
        "strategy": "reject_item",
        "rules": [
          "If an item is missing the 'price' attribute or price is <= 0, the item is excluded from the purchasing session.",
          "If a participant's remaining budget is insufficient for a purchase, the purchase is rejected."
        ]
      },
      "tie_breaking": {
        "primary": "higher purchase count wins",
        "secondary": "higher total_spent wins (if purchase counts are equal)",
        "tertiary": "alphabetical by item.label"
      },
      "determinism": false,
      "notes": "Results depend on participant choices, which are non-deterministic. However, given the same participant choices, results are deterministic."
    },
    "outputs": {
      "score_set_schema": {
        "per_item_fields": [
          {
            "name": "item_id",
            "type": "string"
          },
          {
            "name": "purchases",
            "type": "integer",
            "description": "Total number of times this item was purchased across all participants."
          },
          {
            "name": "total_spent",
            "type": "numeric",
            "description": "Total amount of budget spent on this item (purchases * price)."
          },
          {
            "name": "rank",
            "type": "integer",
            "description": "Rank based on purchase count (1 = most purchased, highest priority)."
          }
        ],
        "annotations": [
          {
            "name": "tool_name",
            "type": "string",
            "value": "Buy A Feature"
          },
          {
            "name": "total_budget",
            "type": "numeric",
            "description": "Total budget allocated across all participants."
          },
          {
            "name": "total_spent",
            "type": "numeric",
            "description": "Total amount of budget actually spent."
          },
          {
            "name": "num_participants",
            "type": "integer",
            "description": "Number of participants in the session."
          }
        ]
      },
      "tool_run_metadata": {
        "records_raw_inputs": true,
        "raw_inputs_schema": {
          "item_prices": "object mapping item_id to price",
          "participant_budgets": "array of participant budgets",
          "participant_purchases": "array of purchase records per participant"
        },
        "additional_fields": [
          {
            "name": "participant_breakdown",
            "type": "array",
            "description": "Per-participant purchase history showing which items each participant purchased and in what quantities."
          }
        ]
      }
    },
    "interaction_model": {
      "ui_pattern": "sequential_turn_based_purchasing",
      "supports_inline_edit": true,
      "supports_batch_edit": false,
      "step_flow": [
        "input_items_with_prices",
        "configure_participants",
        "review_budget_summary",
        "participant_turn_purchasing",
        "next_participant",
        "review_results"
      ],
      "validation_behavior": {
        "on_invalid_item": "exclude_from_session_with_error",
        "on_any_error": "prevent_purchase_and_show_feedback"
      },
      "interaction_notes": "Participants take turns purchasing features. Each participant can purchase multiple quantities of the same feature. Participants can remove purchases before moving to the next participant. The tool enforces budget constraints in real-time."
    },
    "constraints_and_assumptions": {
      "max_items_tested": 100,
      "max_participants": 20,
      "performance_notes": "O(n * p) where n = number of items and p = number of participants. Real-time budget validation on each purchase.",
      "assumes_attribute_types": "Price must be numeric and > 0. Platform must parse numeric values before tool execution.",
      "assumptions": [
        "Prices reflect actual effort, value, or priority",
        "Total budget (40% of total price) creates scarcity and forces trade-offs",
        "Equal budget distribution ensures fairness",
        "Participants make independent purchasing decisions"
      ]
    },
    "test_vectors": [
      {
        "name": "simple_three_feature_example",
        "items": [
          {
            "id": "feature_a",
            "label": "Feature A",
            "attributes": {
              "price": 10
            }
          },
          {
            "id": "feature_b",
            "label": "Feature B",
            "attributes": {
              "price": 20
            }
          },
          {
            "id": "feature_c",
            "label": "Feature C",
            "attributes": {
              "price": 15
            }
          }
        ],
        "tool_config": {
          "num_participants": 2,
          "budget_percentage": 0.4,
          "budget_distribution": "equal"
        },
        "participant_actions": [
          {
            "participant_id": "participant_0",
            "purchases": [
              {
                "feature_id": "feature_a",
                "quantity": 1
              },
              {
                "feature_id": "feature_c",
                "quantity": 1
              }
            ]
          },
          {
            "participant_id": "participant_1",
            "purchases": [
              {
                "feature_id": "feature_a",
                "quantity": 2
              }
            ]
          }
        ],
        "expected_scores": [
          {
            "item_id": "feature_a",
            "purchases": 3,
            "total_spent": 30,
            "rank": 1
          },
          {
            "item_id": "feature_c",
            "purchases": 1,
            "total_spent": 15,
            "rank": 2
          },
          {
            "item_id": "feature_b",
            "purchases": 0,
            "total_spent": 0,
            "rank": 3
          }
        ],
        "expected_metadata": {
          "total_budget": 18,
          "total_spent": 45,
          "num_participants": 2,
          "budget_per_participant": 9
        }
      },
      {
        "name": "tie_breaking_example",
        "items": [
          {
            "id": "feature_x",
            "label": "Feature X",
            "attributes": {
              "price": 10
            }
          },
          {
            "id": "feature_y",
            "label": "Feature Y",
            "attributes": {
              "price": 5
            }
          }
        ],
        "tool_config": {
          "num_participants": 2,
          "budget_percentage": 0.4,
          "budget_distribution": "equal"
        },
        "participant_actions": [
          {
            "participant_id": "participant_0",
            "purchases": [
              {
                "feature_id": "feature_x",
                "quantity": 1
              },
              {
                "feature_id": "feature_y",
                "quantity": 2
              }
            ]
          },
          {
            "participant_id": "participant_1",
            "purchases": [
              {
                "feature_id": "feature_x",
                "quantity": 1
              },
              {
                "feature_id": "feature_y",
                "quantity": 2
              }
            ]
          }
        ],
        "expected_scores": [
          {
            "item_id": "feature_y",
            "purchases": 4,
            "total_spent": 20,
            "rank": 1
          },
          {
            "item_id": "feature_x",
            "purchases": 2,
            "total_spent": 20,
            "rank": 2
          }
        ],
        "notes": "Both features have same total_spent (20.0), but feature_y has more purchases (4 vs 2), so it ranks higher."
      }
    ]
  },
  {
    "tool_spec_version": "1.0",
    "identity": {
      "tool_name": "CostOfDelay",
      "short_label": "Cost of Delay",
      "description": "Assesses the cost of deferring attention, a decision, or action on an item. Cost of Delay = Value Lost Due to Delay + Cost to Restart or Rework. Introduces time value to prioritization, helping identify items that get more 'expensive' over time.",
      "category": "scoring_model",
      "status": "stable"
    },
    "semantics": {
      "purpose": "Arrange-phase prioritization by quantifying the economic impact and restart costs of delaying items. Items with higher Cost of Delay become more expensive over time and should be prioritized to avoid opportunity costs.",
      "requires_comparable_items": true,
      "notes_on_comparability": "Items must be evaluated using the same two dimensions: value lost due to delay and cost to restart or rework. All items are scored on the same scale."
    },
    "inputs": {
      "required_item_fields": [
        "id",
        "label"
      ],
      "required_item_attributes": [
        {
          "key": "value_lost",
          "type": "numeric",
          "description": "The potential value (monetary or otherwise) that would be lost if this item is delayed. Includes opportunity costs, missed deadlines, lost revenue, or other value degradation over time."
        },
        {
          "key": "cost_to_restart",
          "type": "numeric",
          "description": "The cost (time, money, or effort) required to restart or rework this item if it is delayed. Accounts for context switching, re-learning, fixing issues that arise from delay, or any overhead to resume work."
        }
      ],
      "optional_item_attributes": [],
      "criteria_set_contract": {
        "allowed_criteria_ids": [
          "value_lost",
          "cost_to_restart"
        ],
        "requires_explicit_criteria_set": false,
        "notes": "The tool uses two fixed criteria: value_lost and cost_to_restart. If a CriteriaSet is provided, it must contain exactly these two criteria with matching IDs."
      },
      "lens_support": {
        "requires_lens": false,
        "notes": "If a lens is provided, the tool only scores items within that lens. Useful for filtering to time-sensitive items or specific categories."
      },
      "tool_config": {
        "fields": [
          {
            "name": "value_scale",
            "type": "enum",
            "allowed_values": [
              "relative",
              "absolute"
            ],
            "default": "relative",
            "description": "Whether value_lost and cost_to_restart are relative (e.g., 1-10 scale) or absolute (e.g., actual monetary values). Does not affect calculation, only interpretation."
          },
          {
            "name": "allow_zero_values",
            "type": "boolean",
            "default": true,
            "description": "Whether items with zero value_lost or cost_to_restart are allowed. Zero values are valid (e.g., no value lost, no restart cost)."
          }
        ],
        "supports_defaults": true
      }
    },
    "algorithm": {
      "score_model_type": "formula",
      "formula_description": "Cost of Delay = Value Lost Due to Delay + Cost to Restart or Rework",
      "formula_variables": {
        "value_lost": "numeric, >= 0",
        "cost_to_restart": "numeric, >= 0"
      },
      "scaling_and_normalization": {
        "normalizes_value_lost": "none - uses raw input value",
        "normalizes_cost_to_restart": "none - uses raw input value",
        "normalizes_final_score": "none - final score is sum of components"
      },
      "handles_missing_values": {
        "strategy": "reject_item",
        "rules": [
          "If value_lost is missing or null, the item is excluded from scoring and returned with an error reason.",
          "If cost_to_restart is missing or null, the item is excluded from scoring and returned with an error reason.",
          "Both attributes must be present and numeric (>= 0) for scoring to proceed."
        ]
      },
      "tie_breaking": {
        "primary": "higher score wins (higher Cost of Delay = higher priority)",
        "secondary": "alphabetical by item.label",
        "tertiary": "stable by original import order"
      },
      "determinism": true,
      "notes": "The formula is purely additive. Items with higher combined value_lost + cost_to_restart have higher priority. This reflects that items with greater time sensitivity should be addressed sooner."
    },
    "outputs": {
      "score_set_schema": {
        "per_item_fields": [
          {
            "name": "item_id",
            "type": "string"
          },
          {
            "name": "score",
            "type": "numeric",
            "description": "Cost of Delay score (value_lost + cost_to_restart). Higher scores indicate higher priority."
          },
          {
            "name": "rank",
            "type": "integer",
            "description": "1 = highest priority (highest Cost of Delay) within the evaluated set."
          }
        ],
        "annotations": [
          {
            "name": "tool_name",
            "type": "string",
            "value": "CostOfDelay"
          },
          {
            "name": "value_lost",
            "type": "numeric",
            "description": "Original value_lost input for reference"
          },
          {
            "name": "cost_to_restart",
            "type": "numeric",
            "description": "Original cost_to_restart input for reference"
          }
        ]
      },
      "tool_run_metadata": {
        "records_raw_inputs": true,
        "raw_inputs_schema": {
          "value_lost": "numeric",
          "cost_to_restart": "numeric"
        },
        "additional_fields": {
          "total_items_scored": "integer",
          "items_excluded": "integer",
          "exclusion_reasons": "array of strings"
        }
      }
    },
    "interaction_model": {
      "ui_pattern": "sequential_item_scoring",
      "supports_inline_edit": true,
      "supports_batch_edit": false,
      "step_flow": [
        "input_items",
        "score_value_lost_per_item",
        "score_cost_to_restart_per_item",
        "validate_all_items",
        "calculate_cost_of_delay",
        "rank_items",
        "review_results"
      ],
      "validation_behavior": {
        "on_invalid_item": "mark_item_error_and_exclude_from_scoring",
        "on_any_error": "allow_run_with_warnings",
        "validation_rules": [
          "value_lost must be numeric and >= 0",
          "cost_to_restart must be numeric and >= 0",
          "Both values must be provided (cannot be null or empty)"
        ]
      },
      "ui_notes": "The tool presents items one at a time in a two-phase scoring process. First collects value_lost, then cost_to_restart for each item. Progress is tracked visually. Users can navigate back to edit previous items before finalizing."
    },
    "constraints_and_assumptions": {
      "max_items_tested": 1000,
      "performance_notes": "O(n) over number of items for scoring. Sequential UI flow means user interaction time scales linearly with item count.",
      "assumes_attribute_types": "Numeric fields must already be parsed by platform. Values are assumed to be on a consistent scale (relative or absolute) as specified in tool_config.",
      "assumptions": [
        "Items are independent (no dependencies between items)",
        "Value lost and restart costs are additive (no interaction effects)",
        "Time sensitivity is captured in the value_lost component",
        "Restart costs are independent of delay duration (fixed cost to resume)"
      ]
    },
    "toolRequirements": {
      "prerequisites": [
        "CRITERIA_LOCKED",
        "SCALES_DEFINED",
        "POOL_NONEMPTY",
        "VALUES_SUFFICIENT"
      ],
      "requiredCriteria": [
        {
          "tag": "term:value_lost",
          "criterionTypes": [
            "numeric"
          ],
          "minCount": 1
        },
        {
          "tag": "term:cost_to_restart",
          "criterionTypes": [
            "numeric"
          ],
          "minCount": 1
        }
      ],
      "poolRequirements": {
        "minItems": 2,
        "requireLocked": false
      }
    },
    "test_vectors": [
      {
        "name": "simple_three_item_example",
        "items": [
          {
            "id": "item-1",
            "label": "Airline ticket purchase",
            "attributes": {
              "value_lost": 8,
              "cost_to_restart": 2
            }
          },
          {
            "id": "item-2",
            "label": "Quarterly report preparation",
            "attributes": {
              "value_lost": 5,
              "cost_to_restart": 4
            }
          },
          {
            "id": "item-3",
            "label": "Long-term research project",
            "attributes": {
              "value_lost": 3,
              "cost_to_restart": 1
            }
          }
        ],
        "tool_config": {
          "value_scale": "relative",
          "allow_zero_values": true
        },
        "expected_scores": [
          {
            "item_id": "item-1",
            "score": 10,
            "rank": 1,
            "value_lost": 8,
            "cost_to_restart": 2
          },
          {
            "item_id": "item-2",
            "score": 9,
            "rank": 2,
            "value_lost": 5,
            "cost_to_restart": 4
          },
          {
            "item_id": "item-3",
            "score": 4,
            "rank": 3,
            "value_lost": 3,
            "cost_to_restart": 1
          }
        ]
      },
      {
        "name": "zero_value_lost_example",
        "items": [
          {
            "id": "item-a",
            "label": "Low priority task",
            "attributes": {
              "value_lost": 0,
              "cost_to_restart": 5
            }
          },
          {
            "id": "item-b",
            "label": "High value task",
            "attributes": {
              "value_lost": 10,
              "cost_to_restart": 0
            }
          }
        ],
        "tool_config": {
          "value_scale": "relative",
          "allow_zero_values": true
        },
        "expected_scores": [
          {
            "item_id": "item-b",
            "score": 10,
            "rank": 1,
            "value_lost": 10,
            "cost_to_restart": 0
          },
          {
            "item_id": "item-a",
            "score": 5,
            "rank": 2,
            "value_lost": 0,
            "cost_to_restart": 5
          }
        ]
      },
      {
        "name": "tie_breaking_example",
        "items": [
          {
            "id": "item-x",
            "label": "Alpha task",
            "attributes": {
              "value_lost": 5,
              "cost_to_restart": 5
            }
          },
          {
            "id": "item-y",
            "label": "Beta task",
            "attributes": {
              "value_lost": 5,
              "cost_to_restart": 5
            }
          }
        ],
        "tool_config": {
          "value_scale": "relative",
          "allow_zero_values": true
        },
        "expected_scores": [
          {
            "item_id": "item-x",
            "score": 10,
            "rank": 1,
            "value_lost": 5,
            "cost_to_restart": 5,
            "tie_break_reason": "alphabetical_by_label"
          },
          {
            "item_id": "item-y",
            "score": 10,
            "rank": 2,
            "value_lost": 5,
            "cost_to_restart": 5,
            "tie_break_reason": "alphabetical_by_label"
          }
        ]
      }
    ]
  },
  {
    "tool_spec_version": "1.0",
    "identity": {
      "tool_name": "CostOfDelay",
      "short_label": "Cost of Delay",
      "description": "Assesses the cost of deferring attention, a decision, or action on an item. Cost of Delay = Value Lost Due to Delay + Cost to Restart or Rework. Introduces time value to prioritization, helping identify items that get more 'expensive' over time.",
      "category": "scoring_model",
      "status": "stable"
    },
    "semantics": {
      "purpose": "Arrange-phase prioritization by quantifying the economic impact and restart costs of delaying items. Items with higher Cost of Delay become more expensive over time and should be prioritized to avoid opportunity costs.",
      "requires_comparable_items": true,
      "notes_on_comparability": "Items must be evaluated using the same two dimensions: value lost due to delay and cost to restart or rework. All items are scored on the same scale."
    },
    "inputs": {
      "required_item_fields": [
        "id",
        "label"
      ],
      "required_item_attributes": [
        {
          "key": "value_lost",
          "type": "numeric",
          "description": "The potential value (monetary or otherwise) that would be lost if this item is delayed. Includes opportunity costs, missed deadlines, lost revenue, or other value degradation over time."
        },
        {
          "key": "cost_to_restart",
          "type": "numeric",
          "description": "The cost (time, money, or effort) required to restart or rework this item if it is delayed. Accounts for context switching, re-learning, fixing issues that arise from delay, or any overhead to resume work."
        }
      ],
      "optional_item_attributes": [],
      "criteria_set_contract": {
        "allowed_criteria_ids": [
          "value_lost",
          "cost_to_restart"
        ],
        "requires_explicit_criteria_set": false,
        "notes": "The tool uses two fixed criteria: value_lost and cost_to_restart. If a CriteriaSet is provided, it must contain exactly these two criteria with matching IDs."
      },
      "lens_support": {
        "requires_lens": false,
        "notes": "If a lens is provided, the tool only scores items within that lens. Useful for filtering to time-sensitive items or specific categories."
      },
      "tool_config": {
        "fields": [
          {
            "name": "value_scale",
            "type": "enum",
            "allowed_values": [
              "relative",
              "absolute"
            ],
            "default": "relative",
            "description": "Whether value_lost and cost_to_restart are relative (e.g., 1-10 scale) or absolute (e.g., actual monetary values). Does not affect calculation, only interpretation."
          },
          {
            "name": "allow_zero_values",
            "type": "boolean",
            "default": true,
            "description": "Whether items with zero value_lost or cost_to_restart are allowed. Zero values are valid (e.g., no value lost, no restart cost)."
          }
        ],
        "supports_defaults": true
      }
    },
    "algorithm": {
      "score_model_type": "formula",
      "formula_description": "Cost of Delay = Value Lost Due to Delay + Cost to Restart or Rework",
      "formula_variables": {
        "value_lost": "numeric, >= 0",
        "cost_to_restart": "numeric, >= 0"
      },
      "scaling_and_normalization": {
        "normalizes_value_lost": "none - uses raw input value",
        "normalizes_cost_to_restart": "none - uses raw input value",
        "normalizes_final_score": "none - final score is sum of components"
      },
      "handles_missing_values": {
        "strategy": "reject_item",
        "rules": [
          "If value_lost is missing or null, the item is excluded from scoring and returned with an error reason.",
          "If cost_to_restart is missing or null, the item is excluded from scoring and returned with an error reason.",
          "Both attributes must be present and numeric (>= 0) for scoring to proceed."
        ]
      },
      "tie_breaking": {
        "primary": "higher score wins (higher Cost of Delay = higher priority)",
        "secondary": "alphabetical by item.label",
        "tertiary": "stable by original import order"
      },
      "determinism": true,
      "notes": "The formula is purely additive. Items with higher combined value_lost + cost_to_restart have higher priority. This reflects that items with greater time sensitivity should be addressed sooner."
    },
    "outputs": {
      "score_set_schema": {
        "per_item_fields": [
          {
            "name": "item_id",
            "type": "string"
          },
          {
            "name": "score",
            "type": "numeric",
            "description": "Cost of Delay score (value_lost + cost_to_restart). Higher scores indicate higher priority."
          },
          {
            "name": "rank",
            "type": "integer",
            "description": "1 = highest priority (highest Cost of Delay) within the evaluated set."
          }
        ],
        "annotations": [
          {
            "name": "tool_name",
            "type": "string",
            "value": "CostOfDelay"
          },
          {
            "name": "value_lost",
            "type": "numeric",
            "description": "Original value_lost input for reference"
          },
          {
            "name": "cost_to_restart",
            "type": "numeric",
            "description": "Original cost_to_restart input for reference"
          }
        ]
      },
      "tool_run_metadata": {
        "records_raw_inputs": true,
        "raw_inputs_schema": {
          "value_lost": "numeric",
          "cost_to_restart": "numeric"
        },
        "additional_fields": {
          "total_items_scored": "integer",
          "items_excluded": "integer",
          "exclusion_reasons": "array of strings"
        }
      }
    },
    "interaction_model": {
      "ui_pattern": "sequential_item_scoring",
      "supports_inline_edit": true,
      "supports_batch_edit": false,
      "step_flow": [
        "input_items",
        "score_value_lost_per_item",
        "score_cost_to_restart_per_item",
        "validate_all_items",
        "calculate_cost_of_delay",
        "rank_items",
        "review_results"
      ],
      "validation_behavior": {
        "on_invalid_item": "mark_item_error_and_exclude_from_scoring",
        "on_any_error": "allow_run_with_warnings",
        "validation_rules": [
          "value_lost must be numeric and >= 0",
          "cost_to_restart must be numeric and >= 0",
          "Both values must be provided (cannot be null or empty)"
        ]
      },
      "ui_notes": "The tool presents items one at a time in a two-phase scoring process. First collects value_lost, then cost_to_restart for each item. Progress is tracked visually. Users can navigate back to edit previous items before finalizing."
    },
    "constraints_and_assumptions": {
      "max_items_tested": 1000,
      "performance_notes": "O(n) over number of items for scoring. Sequential UI flow means user interaction time scales linearly with item count.",
      "assumes_attribute_types": "Numeric fields must already be parsed by platform. Values are assumed to be on a consistent scale (relative or absolute) as specified in tool_config.",
      "assumptions": [
        "Items are independent (no dependencies between items)",
        "Value lost and restart costs are additive (no interaction effects)",
        "Time sensitivity is captured in the value_lost component",
        "Restart costs are independent of delay duration (fixed cost to resume)"
      ]
    },
    "test_vectors": [
      {
        "name": "simple_three_item_example",
        "items": [
          {
            "id": "item-1",
            "label": "Airline ticket purchase",
            "attributes": {
              "value_lost": 8,
              "cost_to_restart": 2
            }
          },
          {
            "id": "item-2",
            "label": "Quarterly report preparation",
            "attributes": {
              "value_lost": 5,
              "cost_to_restart": 4
            }
          },
          {
            "id": "item-3",
            "label": "Long-term research project",
            "attributes": {
              "value_lost": 3,
              "cost_to_restart": 1
            }
          }
        ],
        "tool_config": {
          "value_scale": "relative",
          "allow_zero_values": true
        },
        "expected_scores": [
          {
            "item_id": "item-1",
            "score": 10,
            "rank": 1,
            "value_lost": 8,
            "cost_to_restart": 2
          },
          {
            "item_id": "item-2",
            "score": 9,
            "rank": 2,
            "value_lost": 5,
            "cost_to_restart": 4
          },
          {
            "item_id": "item-3",
            "score": 4,
            "rank": 3,
            "value_lost": 3,
            "cost_to_restart": 1
          }
        ]
      },
      {
        "name": "zero_value_lost_example",
        "items": [
          {
            "id": "item-a",
            "label": "Low priority task",
            "attributes": {
              "value_lost": 0,
              "cost_to_restart": 5
            }
          },
          {
            "id": "item-b",
            "label": "High value task",
            "attributes": {
              "value_lost": 10,
              "cost_to_restart": 0
            }
          }
        ],
        "tool_config": {
          "value_scale": "relative",
          "allow_zero_values": true
        },
        "expected_scores": [
          {
            "item_id": "item-b",
            "score": 10,
            "rank": 1,
            "value_lost": 10,
            "cost_to_restart": 0
          },
          {
            "item_id": "item-a",
            "score": 5,
            "rank": 2,
            "value_lost": 0,
            "cost_to_restart": 5
          }
        ]
      },
      {
        "name": "tie_breaking_example",
        "items": [
          {
            "id": "item-x",
            "label": "Alpha task",
            "attributes": {
              "value_lost": 5,
              "cost_to_restart": 5
            }
          },
          {
            "id": "item-y",
            "label": "Beta task",
            "attributes": {
              "value_lost": 5,
              "cost_to_restart": 5
            }
          }
        ],
        "tool_config": {
          "value_scale": "relative",
          "allow_zero_values": true
        },
        "expected_scores": [
          {
            "item_id": "item-x",
            "score": 10,
            "rank": 1,
            "value_lost": 5,
            "cost_to_restart": 5,
            "tie_break_reason": "alphabetical_by_label"
          },
          {
            "item_id": "item-y",
            "score": 10,
            "rank": 2,
            "value_lost": 5,
            "cost_to_restart": 5,
            "tie_break_reason": "alphabetical_by_label"
          }
        ]
      }
    ]
  },
  {
    "tool_spec_version": "1.0",
    "identity": {
      "tool_name": "DotVoting",
      "short_label": "Dot Voting",
      "description": "A prioritization method where participants allocate a fixed number of dots or nonbinding votes to various options, with the most-voted options being ranked the highest.",
      "category": "voting_model",
      "status": "stable"
    },
    "semantics": {
      "purpose": "Arrange-phase prioritization through collaborative voting. Participants allocate a fixed number of dots to items, and items are ranked by total vote count.",
      "requires_comparable_items": false,
      "notes_on_comparability": "Items do not need to share any attributes or criteria. Dot voting is purely preference-based and does not require item comparability beyond being in the same set."
    },
    "inputs": {
      "required_item_fields": [
        "id",
        "label"
      ],
      "required_item_attributes": [],
      "optional_item_attributes": [
        {
          "key": "description",
          "type": "string",
          "description": "Optional description or context for the item."
        }
      ],
      "criteria_set_contract": {
        "allowed_criteria_ids": [],
        "requires_explicit_criteria_set": false,
        "notes": "Dot voting does not use criteria. It is a pure voting method that does not evaluate items against dimensions."
      },
      "lens_support": {
        "requires_lens": false,
        "notes": "If a lens is provided, the tool only votes on items within that lens. Votes are counted only for items in the lens."
      },
      "tool_config": {
        "fields": [
          {
            "name": "dots_per_participant",
            "type": "integer",
            "allowed_values": null,
            "min": 1,
            "max": 20,
            "default": 5,
            "description": "Number of dots each participant can allocate. Each dot represents one vote for an item."
          },
          {
            "name": "allow_partial_allocation",
            "type": "boolean",
            "allowed_values": null,
            "default": false,
            "description": "If false, participants must allocate all dots before finishing. If true, participants can finish with remaining dots unallocated."
          },
          {
            "name": "allow_remove_votes",
            "type": "boolean",
            "allowed_values": null,
            "default": true,
            "description": "Whether participants can remove votes they have already placed."
          }
        ],
        "supports_defaults": true
      }
    },
    "algorithm": {
      "score_model_type": "vote_count",
      "formula_description": "Items are ranked by total vote count (number of dots received). Higher vote count = higher priority.",
      "formula_variables": {},
      "scaling_and_normalization": {
        "normalizes_votes": "none",
        "normalizes_other_fields": "none",
        "notes": "Vote counts are raw integers. No normalization is applied."
      },
      "handles_missing_values": {
        "strategy": "treat_as_zero",
        "rules": [
          "Items with no votes receive a vote count of 0.",
          "Items with 0 votes are ranked lowest (after all items with votes)."
        ]
      },
      "tie_breaking": {
        "primary": "items with same vote count share the same rank",
        "secondary": "alphabetical by item.label",
        "tertiary": "stable by original import order",
        "notes": "Ties are acceptable in dot voting and indicate equal priority. The tool explicitly shows tied items."
      },
      "determinism": true,
      "notes": "Same vote counts always produce the same ranking. The algorithm is deterministic."
    },
    "outputs": {
      "score_set_schema": {
        "per_item_fields": [
          {
            "name": "item_id",
            "type": "string"
          },
          {
            "name": "votes",
            "type": "integer",
            "description": "Total number of dots/votes received by this item."
          },
          {
            "name": "rank",
            "type": "integer",
            "description": "1 = highest priority (most votes). Items with same vote count share the same rank."
          },
          {
            "name": "vote_percentage",
            "type": "numeric",
            "description": "Percentage of total votes this item received (votes / total_votes * 100)."
          },
          {
            "name": "is_tied",
            "type": "boolean",
            "description": "Whether this item is tied with other items at the same rank."
          }
        ],
        "annotations": [
          {
            "name": "tool_name",
            "type": "string",
            "value": "DotVoting"
          },
          {
            "name": "total_votes",
            "type": "integer",
            "description": "Total number of votes allocated across all items."
          },
          {
            "name": "dots_per_participant",
            "type": "integer",
            "description": "Number of dots each participant was allowed to allocate."
          }
        ]
      },
      "tool_run_metadata": {
        "records_raw_inputs": true,
        "raw_inputs_schema": {
          "votes": "integer",
          "item_labels": "array of strings"
        },
        "notes": "Records the vote count per item and the original item labels for audit purposes."
      }
    },
    "interaction_model": {
      "ui_pattern": "visual_voting_grid",
      "supports_inline_edit": true,
      "supports_batch_edit": false,
      "step_flow": [
        "review_items",
        "configure_dots_per_participant",
        "allocate_dots_to_items",
        "review_allocation",
        "finish_voting",
        "review_results"
      ],
      "validation_behavior": {
        "on_invalid_item": "exclude_from_voting",
        "on_any_error": "allow_continue_with_warnings",
        "notes": "Items without id or label are excluded. Participants must allocate all dots (if allow_partial_allocation is false) before finishing."
      },
      "ui_characteristics": {
        "visual_feedback": "colored dots displayed on items to show vote allocation",
        "progress_tracking": "shows remaining dots and progress bar",
        "interactive_elements": "click items to add dots, click remove to undo votes",
        "grid_layout": "items displayed in responsive grid (1-3 columns based on screen size)"
      }
    },
    "constraints_and_assumptions": {
      "max_items_tested": 100,
      "performance_notes": "O(n) over number of items for vote counting and ranking. Real-time vote updates are O(1) per vote action.",
      "assumes_attribute_types": "none - dot voting does not use item attributes",
      "assumes_participant_model": "single participant per tool run. For multi-participant scenarios, votes should be aggregated across multiple tool runs.",
      "notes": "Designed for collaborative sessions where participants can see and adjust their votes in real-time. Best for 5-20 items to maintain visual clarity."
    },
    "toolRequirements": {
      "prerequisites": [
        "POOL_EXISTS"
      ],
      "requiredCriteria": [],
      "poolRequirements": {
        "minItems": 2,
        "requireLocked": false
      }
    },
    "test_vectors": [
      {
        "name": "simple_three_item_example",
        "items": [
          {
            "id": "A",
            "label": "Item A"
          },
          {
            "id": "B",
            "label": "Item B"
          },
          {
            "id": "C",
            "label": "Item C"
          }
        ],
        "tool_config": {
          "dots_per_participant": 5,
          "allow_partial_allocation": false,
          "allow_remove_votes": true
        },
        "votes": {
          "A": 3,
          "B": 2,
          "C": 0
        },
        "expected_scores": [
          {
            "item_id": "A",
            "votes": 3,
            "rank": 1,
            "vote_percentage": 60,
            "is_tied": false
          },
          {
            "item_id": "B",
            "votes": 2,
            "rank": 2,
            "vote_percentage": 40,
            "is_tied": false
          },
          {
            "item_id": "C",
            "votes": 0,
            "rank": 3,
            "vote_percentage": 0,
            "is_tied": false
          }
        ],
        "expected_metadata": {
          "total_votes": 5,
          "dots_per_participant": 5
        }
      },
      {
        "name": "tie_breaking_example",
        "items": [
          {
            "id": "X",
            "label": "Item X"
          },
          {
            "id": "Y",
            "label": "Item Y"
          },
          {
            "id": "Z",
            "label": "Item Z"
          }
        ],
        "tool_config": {
          "dots_per_participant": 6,
          "allow_partial_allocation": false,
          "allow_remove_votes": true
        },
        "votes": {
          "X": 3,
          "Y": 3,
          "Z": 0
        },
        "expected_scores": [
          {
            "item_id": "X",
            "votes": 3,
            "rank": 1,
            "vote_percentage": 50,
            "is_tied": true
          },
          {
            "item_id": "Y",
            "votes": 3,
            "rank": 1,
            "vote_percentage": 50,
            "is_tied": true
          },
          {
            "item_id": "Z",
            "votes": 0,
            "rank": 2,
            "vote_percentage": 0,
            "is_tied": false
          }
        ],
        "expected_metadata": {
          "total_votes": 6,
          "dots_per_participant": 6
        }
      }
    ]
  },
  {
    "tool_spec_version": "1.0",
    "identity": {
      "tool_name": "DotVoting",
      "short_label": "Dot Voting",
      "description": "A prioritization method where participants allocate a fixed number of dots or nonbinding votes to various options, with the most-voted options being ranked the highest.",
      "category": "voting_model",
      "status": "stable"
    },
    "semantics": {
      "purpose": "Arrange-phase prioritization through collaborative voting. Participants allocate a fixed number of dots to items, and items are ranked by total vote count.",
      "requires_comparable_items": false,
      "notes_on_comparability": "Items do not need to share any attributes or criteria. Dot voting is purely preference-based and does not require item comparability beyond being in the same set."
    },
    "inputs": {
      "required_item_fields": [
        "id",
        "label"
      ],
      "required_item_attributes": [],
      "optional_item_attributes": [
        {
          "key": "description",
          "type": "string",
          "description": "Optional description or context for the item."
        }
      ],
      "criteria_set_contract": {
        "allowed_criteria_ids": [],
        "requires_explicit_criteria_set": false,
        "notes": "Dot voting does not use criteria. It is a pure voting method that does not evaluate items against dimensions."
      },
      "lens_support": {
        "requires_lens": false,
        "notes": "If a lens is provided, the tool only votes on items within that lens. Votes are counted only for items in the lens."
      },
      "tool_config": {
        "fields": [
          {
            "name": "dots_per_participant",
            "type": "integer",
            "allowed_values": null,
            "min": 1,
            "max": 20,
            "default": 5,
            "description": "Number of dots each participant can allocate. Each dot represents one vote for an item."
          },
          {
            "name": "allow_partial_allocation",
            "type": "boolean",
            "allowed_values": null,
            "default": false,
            "description": "If false, participants must allocate all dots before finishing. If true, participants can finish with remaining dots unallocated."
          },
          {
            "name": "allow_remove_votes",
            "type": "boolean",
            "allowed_values": null,
            "default": true,
            "description": "Whether participants can remove votes they have already placed."
          }
        ],
        "supports_defaults": true
      }
    },
    "algorithm": {
      "score_model_type": "vote_count",
      "formula_description": "Items are ranked by total vote count (number of dots received). Higher vote count = higher priority.",
      "formula_variables": {},
      "scaling_and_normalization": {
        "normalizes_votes": "none",
        "normalizes_other_fields": "none",
        "notes": "Vote counts are raw integers. No normalization is applied."
      },
      "handles_missing_values": {
        "strategy": "treat_as_zero",
        "rules": [
          "Items with no votes receive a vote count of 0.",
          "Items with 0 votes are ranked lowest (after all items with votes)."
        ]
      },
      "tie_breaking": {
        "primary": "items with same vote count share the same rank",
        "secondary": "alphabetical by item.label",
        "tertiary": "stable by original import order",
        "notes": "Ties are acceptable in dot voting and indicate equal priority. The tool explicitly shows tied items."
      },
      "determinism": true,
      "notes": "Same vote counts always produce the same ranking. The algorithm is deterministic."
    },
    "outputs": {
      "score_set_schema": {
        "per_item_fields": [
          {
            "name": "item_id",
            "type": "string"
          },
          {
            "name": "votes",
            "type": "integer",
            "description": "Total number of dots/votes received by this item."
          },
          {
            "name": "rank",
            "type": "integer",
            "description": "1 = highest priority (most votes). Items with same vote count share the same rank."
          },
          {
            "name": "vote_percentage",
            "type": "numeric",
            "description": "Percentage of total votes this item received (votes / total_votes * 100)."
          },
          {
            "name": "is_tied",
            "type": "boolean",
            "description": "Whether this item is tied with other items at the same rank."
          }
        ],
        "annotations": [
          {
            "name": "tool_name",
            "type": "string",
            "value": "DotVoting"
          },
          {
            "name": "total_votes",
            "type": "integer",
            "description": "Total number of votes allocated across all items."
          },
          {
            "name": "dots_per_participant",
            "type": "integer",
            "description": "Number of dots each participant was allowed to allocate."
          }
        ]
      },
      "tool_run_metadata": {
        "records_raw_inputs": true,
        "raw_inputs_schema": {
          "votes": "integer",
          "item_labels": "array of strings"
        },
        "notes": "Records the vote count per item and the original item labels for audit purposes."
      }
    },
    "interaction_model": {
      "ui_pattern": "visual_voting_grid",
      "supports_inline_edit": true,
      "supports_batch_edit": false,
      "step_flow": [
        "review_items",
        "configure_dots_per_participant",
        "allocate_dots_to_items",
        "review_allocation",
        "finish_voting",
        "review_results"
      ],
      "validation_behavior": {
        "on_invalid_item": "exclude_from_voting",
        "on_any_error": "allow_continue_with_warnings",
        "notes": "Items without id or label are excluded. Participants must allocate all dots (if allow_partial_allocation is false) before finishing."
      },
      "ui_characteristics": {
        "visual_feedback": "colored dots displayed on items to show vote allocation",
        "progress_tracking": "shows remaining dots and progress bar",
        "interactive_elements": "click items to add dots, click remove to undo votes",
        "grid_layout": "items displayed in responsive grid (1-3 columns based on screen size)"
      }
    },
    "constraints_and_assumptions": {
      "max_items_tested": 100,
      "performance_notes": "O(n) over number of items for vote counting and ranking. Real-time vote updates are O(1) per vote action.",
      "assumes_attribute_types": "none - dot voting does not use item attributes",
      "assumes_participant_model": "single participant per tool run. For multi-participant scenarios, votes should be aggregated across multiple tool runs.",
      "notes": "Designed for collaborative sessions where participants can see and adjust their votes in real-time. Best for 5-20 items to maintain visual clarity."
    },
    "test_vectors": [
      {
        "name": "simple_three_item_example",
        "items": [
          {
            "id": "A",
            "label": "Item A"
          },
          {
            "id": "B",
            "label": "Item B"
          },
          {
            "id": "C",
            "label": "Item C"
          }
        ],
        "tool_config": {
          "dots_per_participant": 5,
          "allow_partial_allocation": false,
          "allow_remove_votes": true
        },
        "votes": {
          "A": 3,
          "B": 2,
          "C": 0
        },
        "expected_scores": [
          {
            "item_id": "A",
            "votes": 3,
            "rank": 1,
            "vote_percentage": 60,
            "is_tied": false
          },
          {
            "item_id": "B",
            "votes": 2,
            "rank": 2,
            "vote_percentage": 40,
            "is_tied": false
          },
          {
            "item_id": "C",
            "votes": 0,
            "rank": 3,
            "vote_percentage": 0,
            "is_tied": false
          }
        ],
        "expected_metadata": {
          "total_votes": 5,
          "dots_per_participant": 5
        }
      },
      {
        "name": "tie_breaking_example",
        "items": [
          {
            "id": "X",
            "label": "Item X"
          },
          {
            "id": "Y",
            "label": "Item Y"
          },
          {
            "id": "Z",
            "label": "Item Z"
          }
        ],
        "tool_config": {
          "dots_per_participant": 6,
          "allow_partial_allocation": false,
          "allow_remove_votes": true
        },
        "votes": {
          "X": 3,
          "Y": 3,
          "Z": 0
        },
        "expected_scores": [
          {
            "item_id": "X",
            "votes": 3,
            "rank": 1,
            "vote_percentage": 50,
            "is_tied": true
          },
          {
            "item_id": "Y",
            "votes": 3,
            "rank": 1,
            "vote_percentage": 50,
            "is_tied": true
          },
          {
            "item_id": "Z",
            "votes": 0,
            "rank": 2,
            "vote_percentage": 0,
            "is_tied": false
          }
        ],
        "expected_metadata": {
          "total_votes": 6,
          "dots_per_participant": 6
        }
      }
    ]
  },
  {
    "tool_spec_version": "1.0",
    "identity": {
      "tool_name": "IMPACT_EFFORT_MATRIX",
      "short_label": "Eisenhower Matrix",
      "description": "2×2 categorization matrix that classifies items by urgency and importance into four quadrants: Do (urgent + important), Schedule (important, not urgent), Delegate (urgent, not important), Eliminate (neither). Based on Dwight Eisenhower's decision-making principle.",
      "category": "decision_framework",
      "status": "stable"
    },
    "semantics": {
      "purpose": "Arrange-phase categorization by sorting items into urgency/importance quadrants. Produces tier assignments with implicit priority order: Do > Schedule > Delegate > Eliminate.",
      "requires_comparable_items": true,
      "notes_on_comparability": "Items must be assessable on two dimensions: urgency (time pressure) and importance (impact on goals). Items can be at any abstraction level but should be comparable within the same evaluation context."
    },
    "inputs": {
      "required_item_fields": [
        "id",
        "label"
      ],
      "required_item_attributes": [],
      "optional_item_attributes": [],
      "criteria_set_contract": {
        "allowed_criteria_ids": [],
        "requires_explicit_criteria_set": false,
        "notes": "The tool uses two fixed axes: urgency and importance. Items are categorized via drag-and-drop during the run. No pre-existing criteria set required."
      },
      "lens_support": {
        "requires_lens": false,
        "notes": "If a lens is provided, only items within the lens are imported for categorization."
      },
      "tool_config": {
        "fields": [],
        "supports_defaults": true
      }
    },
    "algorithm": {
      "score_model_type": "quadrant_classification",
      "formula_description": "Items are assigned to one of four quadrants by user judgment. Quadrants map to priority scores: Urgent+Important (Do) = 4, Important+Not Urgent (Schedule) = 3, Urgent+Not Important (Delegate) = 2, Neither (Eliminate) = 1.",
      "formula_variables": {
        "quadrant": "categorical: ui (Do), in (Schedule), un (Delegate), nn (Eliminate)"
      },
      "scaling_and_normalization": {
        "score_mapping": "ui=4, in=3, un=2, nn=1"
      },
      "handles_missing_values": {
        "strategy": "exclude_uncategorized",
        "rules": [
          "Items not yet assigned to a quadrant are excluded from results.",
          "All items must be categorized before results can be saved."
        ]
      },
      "tie_breaking": {
        "primary": "quadrant priority order (ui > in > un > nn)",
        "secondary": "original import order"
      },
      "determinism": true
    },
    "outputs": {
      "score_set_schema": {
        "per_item_fields": [
          {
            "name": "item_id",
            "type": "string"
          },
          {
            "name": "score",
            "type": "numeric",
            "description": "Quadrant priority score: Do=4, Schedule=3, Delegate=2, Eliminate=1."
          },
          {
            "name": "category",
            "type": "string",
            "description": "Quadrant key: ui, in, un, nn."
          }
        ],
        "annotations": [
          {
            "name": "tool_name",
            "type": "string",
            "value": "EisenhowerMatrix"
          },
          {
            "name": "urgency",
            "type": "string",
            "description": "high or low"
          },
          {
            "name": "importance",
            "type": "string",
            "description": "high or low"
          },
          {
            "name": "action",
            "type": "string",
            "description": "DO, SCHEDULE, DELEGATE, or ELIMINATE"
          }
        ]
      },
      "tool_run_metadata": {
        "records_raw_inputs": true,
        "raw_inputs_schema": {
          "quadrant_assignments": "Map of item_id to quadrant key"
        }
      }
    },
    "interaction_model": {
      "ui_pattern": "drag_and_drop_matrix",
      "supports_inline_edit": false,
      "supports_batch_edit": false,
      "step_flow": [
        "import_or_add_items",
        "drag_items_to_quadrants",
        "review_quadrant_assignments",
        "save_results"
      ],
      "validation_behavior": {
        "on_invalid_item": "keep_in_uncategorized_zone",
        "on_any_error": "prevent_save_until_all_categorized"
      }
    },
    "constraints_and_assumptions": {
      "max_items_tested": 100,
      "performance_notes": "O(n) for rendering. Practical limit is ~50 items for usable drag-and-drop experience.",
      "assumes_attribute_types": "No attribute requirements. Items classified by user judgment.",
      "assumptions": [
        "Items can be meaningfully assessed on urgency and importance dimensions",
        "All items should be categorized before saving results"
      ]
    },
    "toolRequirements": {
      "prerequisites": [
        "POOL_NONEMPTY"
      ],
      "requiredCriteria": [],
      "poolRequirements": {
        "minItems": 1,
        "requireLocked": false
      }
    },
    "test_vectors": [
      {
        "name": "basic_categorization",
        "description": "Four items placed into four different quadrants",
        "items": [
          {
            "id": "item-1",
            "label": "Server outage",
            "quadrant": "ui"
          },
          {
            "id": "item-2",
            "label": "Strategic roadmap planning",
            "quadrant": "in"
          },
          {
            "id": "item-3",
            "label": "Meeting request",
            "quadrant": "un"
          },
          {
            "id": "item-4",
            "label": "Reorganize old files",
            "quadrant": "nn"
          }
        ],
        "expected_scores": [
          {
            "item_id": "item-1",
            "score": 4,
            "category": "ui"
          },
          {
            "item_id": "item-2",
            "score": 3,
            "category": "in"
          },
          {
            "item_id": "item-3",
            "score": 2,
            "category": "un"
          },
          {
            "item_id": "item-4",
            "score": 1,
            "category": "nn"
          }
        ]
      }
    ]
  },
  {
    "tool_spec_version": "1.0",
    "identity": {
      "tool_name": "FAST_VS_RIGHT",
      "short_label": "Fast vs Right Framework",
      "description": "Determines whether a decision requires speed (\"fast\") or rigor (\"right\"). Assesses urgency and correctness to choose the right approach.",
      "category": "decision_framework",
      "status": "stable"
    },
    "semantics": {
      "purpose": "Pre-arrange decision guidance: determines the appropriate decision-making approach based on urgency and correctness requirements. Does not prioritize items, but guides how to approach prioritization decisions.",
      "requires_comparable_items": false,
      "notes_on_comparability": "This tool evaluates a single decision context, not multiple items. It outputs guidance rather than scores or ranks."
    },
    "inputs": {
      "required_item_fields": [],
      "required_item_attributes": [
        {
          "key": "decision_name",
          "type": "string",
          "description": "Name or title of the decision being evaluated."
        },
        {
          "key": "context",
          "type": "string",
          "description": "Description of the situation, background, and why this decision matters."
        },
        {
          "key": "success_criteria",
          "type": "string",
          "description": "What does success look like? How will you know you made the right decision?"
        },
        {
          "key": "urgency_level",
          "type": "enum",
          "allowed_values": [
            "low",
            "medium",
            "high"
          ],
          "description": "How urgent is this decision? High = hours/days, Medium = this week, Low = open timeline."
        },
        {
          "key": "correctness_level",
          "type": "enum",
          "allowed_values": [
            "low",
            "medium",
            "high"
          ],
          "description": "How 'right' does this decision need to be? Consider consequences of being wrong, visibility, risk, political cost."
        }
      ],
      "optional_item_attributes": [
        {
          "key": "constraints",
          "type": "string",
          "description": "Optional constraints (e.g., budget limits, technical requirements)."
        },
        {
          "key": "deadlines",
          "type": "string",
          "description": "Optional deadline information."
        },
        {
          "key": "stakeholders",
          "type": "string",
          "description": "Optional stakeholder information."
        }
      ],
      "criteria_set_contract": {
        "allowed_criteria_ids": [],
        "requires_explicit_criteria_set": false,
        "notes": "This tool does not use explicit criteria sets. It uses fixed dimensions: urgency and correctness."
      },
      "lens_support": {
        "requires_lens": false,
        "notes": "N/A - this tool evaluates a single decision context."
      },
      "tool_config": {
        "fields": [],
        "supports_defaults": true
      }
    },
    "algorithm": {
      "score_model_type": "quadrant_classification",
      "formula_description": "Classifies decision into one of four quadrants based on urgency_level and correctness_level: Fast & Right (high urgency + high correctness), Fast (high urgency + low/medium correctness), Right (low/medium urgency + high correctness), Either (low urgency + low correctness).",
      "formula_variables": {
        "urgency_level": "enum: low, medium, high",
        "correctness_level": "enum: low, medium, high"
      },
      "scaling_and_normalization": {
        "normalizes_confidence": "N/A",
        "normalizes_other_fields": "N/A"
      },
      "handles_missing_values": {
        "strategy": "reject_decision",
        "rules": [
          "If urgency_level or correctness_level is missing, the decision cannot be evaluated and an error is returned."
        ]
      },
      "tie_breaking": {
        "primary": "quadrant_rules",
        "secondary": "N/A",
        "tertiary": "N/A"
      },
      "quadrant_rules": [
        {
          "condition": "urgency_level == 'high' AND correctness_level == 'high'",
          "quadrant": "fast-right",
          "description": "Fast & Right: requires both speed and rigor"
        },
        {
          "condition": "urgency_level == 'high' AND correctness_level != 'high'",
          "quadrant": "fast",
          "description": "Fast (Not Right): speed is more important than perfect correctness"
        },
        {
          "condition": "urgency_level != 'high' AND correctness_level == 'high'",
          "quadrant": "right",
          "description": "Right (Not Fast): correctness is more important than speed"
        },
        {
          "condition": "urgency_level != 'high' AND correctness_level != 'high'",
          "quadrant": "either",
          "description": "Either: neither speed nor perfect correctness is critical"
        }
      ],
      "determinism": true
    },
    "outputs": {
      "score_set_schema": {
        "per_item_fields": [],
        "annotations": [
          {
            "name": "tool_name",
            "type": "string",
            "value": "FAST_VS_RIGHT"
          },
          {
            "name": "quadrant",
            "type": "enum",
            "allowed_values": [
              "fast-right",
              "fast",
              "right",
              "either"
            ],
            "description": "The quadrant classification for this decision."
          },
          {
            "name": "quadrant_title",
            "type": "string",
            "description": "Human-readable title for the quadrant."
          },
          {
            "name": "quadrant_description",
            "type": "string",
            "description": "Description of what this quadrant means."
          },
          {
            "name": "guidance",
            "type": "string",
            "description": "Actionable guidance for this quadrant."
          },
          {
            "name": "manager_conversation_script",
            "type": "string",
            "description": "Suggested script for negotiating expectations with manager."
          }
        ]
      },
      "tool_run_metadata": {
        "records_raw_inputs": true,
        "raw_inputs_schema": {
          "decision_name": "string",
          "context": "string",
          "success_criteria": "string",
          "urgency_level": "enum",
          "correctness_level": "enum",
          "constraints": "string (optional)",
          "deadlines": "string (optional)",
          "stakeholders": "string (optional)"
        }
      }
    },
    "interaction_model": {
      "ui_pattern": "decision_context_wizard",
      "supports_inline_edit": true,
      "supports_batch_edit": false,
      "step_flow": [
        "input_decision_context",
        "assess_urgency_dimension",
        "assess_correctness_dimension",
        "display_quadrant_result",
        "show_guidance_and_next_steps"
      ],
      "validation_behavior": {
        "on_invalid_item": "prevent_progression_until_valid",
        "on_any_error": "show_error_message_and_allow_correction"
      }
    },
    "constraints_and_assumptions": {
      "max_items_tested": 1,
      "performance_notes": "O(1) - evaluates a single decision context. No computational complexity.",
      "assumes_attribute_types": "urgency_level and correctness_level must be valid enum values. String fields are free-form."
    },
    "toolRequirements": {
      "prerequisites": [],
      "requiredCriteria": [],
      "poolRequirements": {
        "minItems": 1,
        "requireLocked": false
      }
    },
    "test_vectors": [
      {
        "name": "high_urgency_high_correctness",
        "items": [],
        "decision_context": {
          "decision_name": "Choose CRM system",
          "context": "Need to select a CRM system for the sales team. Decision affects 50+ users and will be difficult to change once implemented.",
          "success_criteria": "CRM system supports all required workflows, integrates with existing tools, and team adopts it successfully.",
          "urgency_level": "high",
          "correctness_level": "high"
        },
        "tool_config": {},
        "expected_result": {
          "quadrant": "fast-right",
          "quadrant_title": "Fast & Right",
          "quadrant_description": "This decision requires both speed and rigor. You need to move quickly while ensuring correctness."
        }
      },
      {
        "name": "high_urgency_low_correctness",
        "items": [],
        "decision_context": {
          "decision_name": "Choose lunch venue",
          "context": "Team needs to decide where to have lunch today.",
          "success_criteria": "Team gets fed and is reasonably happy.",
          "urgency_level": "high",
          "correctness_level": "low"
        },
        "tool_config": {},
        "expected_result": {
          "quadrant": "fast",
          "quadrant_title": "Fast (Not Right)",
          "quadrant_description": "Speed is more important than perfect correctness. Make a good-enough decision quickly."
        }
      }
    ]
  },
  {
    "tool_spec_version": "1.0",
    "identity": {
      "tool_name": "FAST_VS_RIGHT",
      "short_label": "Fast vs Right Framework",
      "description": "Determines whether a decision requires speed (\"fast\") or rigor (\"right\"). Assesses urgency and correctness to choose the right approach.",
      "category": "decision_framework",
      "status": "stable"
    },
    "semantics": {
      "purpose": "Pre-arrange decision guidance: determines the appropriate decision-making approach based on urgency and correctness requirements. Does not prioritize items, but guides how to approach prioritization decisions.",
      "requires_comparable_items": false,
      "notes_on_comparability": "This tool evaluates a single decision context, not multiple items. It outputs guidance rather than scores or ranks."
    },
    "inputs": {
      "required_item_fields": [],
      "required_item_attributes": [
        {
          "key": "decision_name",
          "type": "string",
          "description": "Name or title of the decision being evaluated."
        },
        {
          "key": "context",
          "type": "string",
          "description": "Description of the situation, background, and why this decision matters."
        },
        {
          "key": "success_criteria",
          "type": "string",
          "description": "What does success look like? How will you know you made the right decision?"
        },
        {
          "key": "urgency_level",
          "type": "enum",
          "allowed_values": [
            "low",
            "medium",
            "high"
          ],
          "description": "How urgent is this decision? High = hours/days, Medium = this week, Low = open timeline."
        },
        {
          "key": "correctness_level",
          "type": "enum",
          "allowed_values": [
            "low",
            "medium",
            "high"
          ],
          "description": "How 'right' does this decision need to be? Consider consequences of being wrong, visibility, risk, political cost."
        }
      ],
      "optional_item_attributes": [
        {
          "key": "constraints",
          "type": "string",
          "description": "Optional constraints (e.g., budget limits, technical requirements)."
        },
        {
          "key": "deadlines",
          "type": "string",
          "description": "Optional deadline information."
        },
        {
          "key": "stakeholders",
          "type": "string",
          "description": "Optional stakeholder information."
        }
      ],
      "criteria_set_contract": {
        "allowed_criteria_ids": [],
        "requires_explicit_criteria_set": false,
        "notes": "This tool does not use explicit criteria sets. It uses fixed dimensions: urgency and correctness."
      },
      "lens_support": {
        "requires_lens": false,
        "notes": "N/A - this tool evaluates a single decision context."
      },
      "tool_config": {
        "fields": [],
        "supports_defaults": true
      }
    },
    "algorithm": {
      "score_model_type": "quadrant_classification",
      "formula_description": "Classifies decision into one of four quadrants based on urgency_level and correctness_level: Fast & Right (high urgency + high correctness), Fast (high urgency + low/medium correctness), Right (low/medium urgency + high correctness), Either (low urgency + low correctness).",
      "formula_variables": {
        "urgency_level": "enum: low, medium, high",
        "correctness_level": "enum: low, medium, high"
      },
      "scaling_and_normalization": {
        "normalizes_confidence": "N/A",
        "normalizes_other_fields": "N/A"
      },
      "handles_missing_values": {
        "strategy": "reject_decision",
        "rules": [
          "If urgency_level or correctness_level is missing, the decision cannot be evaluated and an error is returned."
        ]
      },
      "tie_breaking": {
        "primary": "quadrant_rules",
        "secondary": "N/A",
        "tertiary": "N/A"
      },
      "quadrant_rules": [
        {
          "condition": "urgency_level == 'high' AND correctness_level == 'high'",
          "quadrant": "fast-right",
          "description": "Fast & Right: requires both speed and rigor"
        },
        {
          "condition": "urgency_level == 'high' AND correctness_level != 'high'",
          "quadrant": "fast",
          "description": "Fast (Not Right): speed is more important than perfect correctness"
        },
        {
          "condition": "urgency_level != 'high' AND correctness_level == 'high'",
          "quadrant": "right",
          "description": "Right (Not Fast): correctness is more important than speed"
        },
        {
          "condition": "urgency_level != 'high' AND correctness_level != 'high'",
          "quadrant": "either",
          "description": "Either: neither speed nor perfect correctness is critical"
        }
      ],
      "determinism": true
    },
    "outputs": {
      "score_set_schema": {
        "per_item_fields": [],
        "annotations": [
          {
            "name": "tool_name",
            "type": "string",
            "value": "FAST_VS_RIGHT"
          },
          {
            "name": "quadrant",
            "type": "enum",
            "allowed_values": [
              "fast-right",
              "fast",
              "right",
              "either"
            ],
            "description": "The quadrant classification for this decision."
          },
          {
            "name": "quadrant_title",
            "type": "string",
            "description": "Human-readable title for the quadrant."
          },
          {
            "name": "quadrant_description",
            "type": "string",
            "description": "Description of what this quadrant means."
          },
          {
            "name": "guidance",
            "type": "string",
            "description": "Actionable guidance for this quadrant."
          },
          {
            "name": "manager_conversation_script",
            "type": "string",
            "description": "Suggested script for negotiating expectations with manager."
          }
        ]
      },
      "tool_run_metadata": {
        "records_raw_inputs": true,
        "raw_inputs_schema": {
          "decision_name": "string",
          "context": "string",
          "success_criteria": "string",
          "urgency_level": "enum",
          "correctness_level": "enum",
          "constraints": "string (optional)",
          "deadlines": "string (optional)",
          "stakeholders": "string (optional)"
        }
      }
    },
    "interaction_model": {
      "ui_pattern": "decision_context_wizard",
      "supports_inline_edit": true,
      "supports_batch_edit": false,
      "step_flow": [
        "input_decision_context",
        "assess_urgency_dimension",
        "assess_correctness_dimension",
        "display_quadrant_result",
        "show_guidance_and_next_steps"
      ],
      "validation_behavior": {
        "on_invalid_item": "prevent_progression_until_valid",
        "on_any_error": "show_error_message_and_allow_correction"
      }
    },
    "constraints_and_assumptions": {
      "max_items_tested": 1,
      "performance_notes": "O(1) - evaluates a single decision context. No computational complexity.",
      "assumes_attribute_types": "urgency_level and correctness_level must be valid enum values. String fields are free-form."
    },
    "test_vectors": [
      {
        "name": "high_urgency_high_correctness",
        "items": [],
        "decision_context": {
          "decision_name": "Choose CRM system",
          "context": "Need to select a CRM system for the sales team. Decision affects 50+ users and will be difficult to change once implemented.",
          "success_criteria": "CRM system supports all required workflows, integrates with existing tools, and team adopts it successfully.",
          "urgency_level": "high",
          "correctness_level": "high"
        },
        "tool_config": {},
        "expected_result": {
          "quadrant": "fast-right",
          "quadrant_title": "Fast & Right",
          "quadrant_description": "This decision requires both speed and rigor. You need to move quickly while ensuring correctness."
        }
      },
      {
        "name": "high_urgency_low_correctness",
        "items": [],
        "decision_context": {
          "decision_name": "Choose lunch venue",
          "context": "Team needs to decide where to have lunch today.",
          "success_criteria": "Team gets fed and is reasonably happy.",
          "urgency_level": "high",
          "correctness_level": "low"
        },
        "tool_config": {},
        "expected_result": {
          "quadrant": "fast",
          "quadrant_title": "Fast (Not Right)",
          "quadrant_description": "Speed is more important than perfect correctness. Make a good-enough decision quickly."
        }
      }
    ]
  },
  {
    "tool_spec_version": "1.0",
    "identity": {
      "tool_name": "IMPACT_EFFORT_MATRIX",
      "short_label": "Impact / Effort Matrix",
      "description": "Prioritizes items using a 2×2 matrix: Quick Wins (high impact, low effort), Big Bets (high impact, high effort), Fill-ins, and Avoid.",
      "category": "categorization_model",
      "status": "stable"
    },
    "semantics": {
      "purpose": "Arrange-phase prioritization by categorizing items into four quadrants based on Impact and Effort scores.",
      "requires_comparable_items": true,
      "notes_on_comparability": "Items must be scored on consistent Impact and Effort scales (typically 1-5) to enable meaningful categorization."
    },
    "inputs": {
      "required_item_fields": [
        "id",
        "label"
      ],
      "required_item_attributes": [
        {
          "key": "impact",
          "type": "numeric",
          "description": "How valuable or beneficial is this item? Typically 1-5 scale where 5 = very high impact, 1 = low impact."
        },
        {
          "key": "effort",
          "type": "numeric",
          "description": "How difficult or expensive is this item? Typically 1-5 scale where 5 = very high effort, 1 = low effort."
        }
      ],
      "optional_item_attributes": [],
      "criteria_set_contract": {
        "allowed_criteria_ids": [
          "impact",
          "effort"
        ],
        "requires_explicit_criteria_set": false,
        "notes": "If a CriteriaSet is provided, its criteria ids must match 'impact' and 'effort'. The tool uses a fixed set of two criteria."
      },
      "lens_support": {
        "requires_lens": false,
        "notes": "If a lens is provided, the tool only categorizes items within that lens."
      },
      "tool_config": {
        "fields": [
          {
            "name": "scale_max",
            "type": "numeric",
            "allowed_values": null,
            "default": 5,
            "description": "Maximum value for impact and effort scales (default 5). Used to determine midpoint threshold."
          },
          {
            "name": "midpoint_threshold",
            "type": "numeric",
            "allowed_values": null,
            "default": null,
            "description": "Explicit midpoint threshold. If null, calculated as scale_max / 2. Items >= midpoint are 'high', < midpoint are 'low'."
          }
        ],
        "supports_defaults": true
      }
    },
    "algorithm": {
      "score_model_type": "quadrant_categorization",
      "formula_description": "Categorizes each item into one of four quadrants: Quick Wins (impact >= midpoint AND effort < midpoint), Big Bets (impact >= midpoint AND effort >= midpoint), Fill-ins (impact < midpoint AND effort < midpoint), Avoid (impact < midpoint AND effort >= midpoint).",
      "formula_variables": {
        "impact": "numeric, typically 1-5",
        "effort": "numeric, typically 1-5"
      },
      "scaling_and_normalization": {
        "normalizes_confidence": "N/A",
        "normalizes_other_fields": "No normalization. Uses raw scores with midpoint threshold to determine high/low."
      },
      "handles_missing_values": {
        "strategy": "reject_item",
        "rules": [
          "If impact or effort is missing, null, or outside valid scale range, the item is excluded from categorization and returned with an error reason."
        ]
      },
      "tie_breaking": {
        "primary": "quadrant_priority",
        "secondary": "alphabetical by item.label within quadrant",
        "tertiary": "stable by original import order"
      },
      "quadrant_priority": [
        {
          "quadrant": "quick-wins",
          "rank": 1,
          "description": "High Impact / Low Effort - Start here"
        },
        {
          "quadrant": "big-bets",
          "rank": 2,
          "description": "High Impact / High Effort - Strategic initiatives"
        },
        {
          "quadrant": "fill-ins",
          "rank": 3,
          "description": "Low Impact / Low Effort - Do when you have spare capacity"
        },
        {
          "quadrant": "avoid",
          "rank": 4,
          "description": "Low Impact / High Effort - Avoid unless compelling reasons"
        }
      ],
      "determinism": true
    },
    "outputs": {
      "score_set_schema": {
        "per_item_fields": [
          {
            "name": "item_id",
            "type": "string"
          },
          {
            "name": "quadrant",
            "type": "enum",
            "allowed_values": [
              "quick-wins",
              "big-bets",
              "fill-ins",
              "avoid"
            ],
            "description": "The quadrant category assigned to this item."
          },
          {
            "name": "quadrant_rank",
            "type": "integer",
            "description": "Numeric rank of quadrant (1=quick-wins, 2=big-bets, 3=fill-ins, 4=avoid) for sorting purposes."
          },
          {
            "name": "impact",
            "type": "numeric",
            "description": "Original impact score."
          },
          {
            "name": "effort",
            "type": "numeric",
            "description": "Original effort score."
          }
        ],
        "annotations": [
          {
            "name": "tool_name",
            "type": "string",
            "value": "IMPACT_EFFORT_MATRIX"
          },
          {
            "name": "midpoint_threshold_used",
            "type": "numeric",
            "description": "The midpoint threshold used to determine high/low (e.g., 3 for 1-5 scale)"
          }
        ]
      },
      "tool_run_metadata": {
        "records_raw_inputs": true,
        "raw_inputs_schema": {
          "impact": "numeric",
          "effort": "numeric"
        }
      }
    },
    "interaction_model": {
      "ui_pattern": "sequential_scoring_then_categorization",
      "supports_inline_edit": true,
      "supports_batch_edit": false,
      "step_flow": [
        "input_items",
        "for_each_item_collect_impact",
        "for_each_item_collect_effort",
        "calculate_quadrants",
        "display_results_grouped_by_quadrant",
        "allow_quadrant_adjustments"
      ],
      "validation_behavior": {
        "on_invalid_item": "prevent_progression_until_valid",
        "on_any_error": "show_error_message_and_allow_correction"
      }
    },
    "constraints_and_assumptions": {
      "max_items_tested": 1000,
      "performance_notes": "O(n) over number of items for categorization. Efficient for large item sets.",
      "assumes_attribute_types": "Impact and effort must be numeric. Typically 1-5 scale, but any consistent scale works if midpoint is configured appropriately."
    },
    "toolRequirements": {
      "prerequisites": [
        "CRITERIA_LOCKED",
        "SCALES_DEFINED",
        "POOL_NONEMPTY"
      ],
      "requiredCriteria": [
        {
          "tag": "axis:x",
          "criterionTypes": [
            "numeric",
            "ordinal"
          ],
          "minCount": 1
        },
        {
          "tag": "axis:y",
          "criterionTypes": [
            "numeric",
            "ordinal"
          ],
          "minCount": 1
        }
      ],
      "poolRequirements": {
        "minItems": 1,
        "requireLocked": false
      }
    },
    "test_vectors": [
      {
        "name": "simple_four_item_example",
        "items": [
          {
            "id": "A",
            "label": "Item A",
            "attributes": {
              "impact": 5,
              "effort": 1
            }
          },
          {
            "id": "B",
            "label": "Item B",
            "attributes": {
              "impact": 5,
              "effort": 5
            }
          },
          {
            "id": "C",
            "label": "Item C",
            "attributes": {
              "impact": 2,
              "effort": 2
            }
          },
          {
            "id": "D",
            "label": "Item D",
            "attributes": {
              "impact": 1,
              "effort": 5
            }
          }
        ],
        "tool_config": {
          "scale_max": 5,
          "midpoint_threshold": 3
        },
        "expected_categorizations": [
          {
            "item_id": "A",
            "quadrant": "quick-wins",
            "quadrant_rank": 1,
            "impact": 5,
            "effort": 1
          },
          {
            "item_id": "B",
            "quadrant": "big-bets",
            "quadrant_rank": 2,
            "impact": 5,
            "effort": 5
          },
          {
            "item_id": "C",
            "quadrant": "fill-ins",
            "quadrant_rank": 3,
            "impact": 2,
            "effort": 2
          },
          {
            "item_id": "D",
            "quadrant": "avoid",
            "quadrant_rank": 4,
            "impact": 1,
            "effort": 5
          }
        ]
      }
    ]
  },
  {
    "tool_spec_version": "1.0",
    "identity": {
      "tool_name": "IMPACT_EFFORT_MATRIX",
      "short_label": "Impact / Effort Matrix",
      "description": "Prioritizes items using a 2×2 matrix: Quick Wins (high impact, low effort), Big Bets (high impact, high effort), Fill-ins, and Avoid.",
      "category": "categorization_model",
      "status": "stable"
    },
    "semantics": {
      "purpose": "Arrange-phase prioritization by categorizing items into four quadrants based on Impact and Effort scores.",
      "requires_comparable_items": true,
      "notes_on_comparability": "Items must be scored on consistent Impact and Effort scales (typically 1-5) to enable meaningful categorization."
    },
    "inputs": {
      "required_item_fields": [
        "id",
        "label"
      ],
      "required_item_attributes": [
        {
          "key": "impact",
          "type": "numeric",
          "description": "How valuable or beneficial is this item? Typically 1-5 scale where 5 = very high impact, 1 = low impact."
        },
        {
          "key": "effort",
          "type": "numeric",
          "description": "How difficult or expensive is this item? Typically 1-5 scale where 5 = very high effort, 1 = low effort."
        }
      ],
      "optional_item_attributes": [],
      "criteria_set_contract": {
        "allowed_criteria_ids": [
          "impact",
          "effort"
        ],
        "requires_explicit_criteria_set": false,
        "notes": "If a CriteriaSet is provided, its criteria ids must match 'impact' and 'effort'. The tool uses a fixed set of two criteria."
      },
      "lens_support": {
        "requires_lens": false,
        "notes": "If a lens is provided, the tool only categorizes items within that lens."
      },
      "tool_config": {
        "fields": [
          {
            "name": "scale_max",
            "type": "numeric",
            "allowed_values": null,
            "default": 5,
            "description": "Maximum value for impact and effort scales (default 5). Used to determine midpoint threshold."
          },
          {
            "name": "midpoint_threshold",
            "type": "numeric",
            "allowed_values": null,
            "default": null,
            "description": "Explicit midpoint threshold. If null, calculated as scale_max / 2. Items >= midpoint are 'high', < midpoint are 'low'."
          }
        ],
        "supports_defaults": true
      }
    },
    "algorithm": {
      "score_model_type": "quadrant_categorization",
      "formula_description": "Categorizes each item into one of four quadrants: Quick Wins (impact >= midpoint AND effort < midpoint), Big Bets (impact >= midpoint AND effort >= midpoint), Fill-ins (impact < midpoint AND effort < midpoint), Avoid (impact < midpoint AND effort >= midpoint).",
      "formula_variables": {
        "impact": "numeric, typically 1-5",
        "effort": "numeric, typically 1-5"
      },
      "scaling_and_normalization": {
        "normalizes_confidence": "N/A",
        "normalizes_other_fields": "No normalization. Uses raw scores with midpoint threshold to determine high/low."
      },
      "handles_missing_values": {
        "strategy": "reject_item",
        "rules": [
          "If impact or effort is missing, null, or outside valid scale range, the item is excluded from categorization and returned with an error reason."
        ]
      },
      "tie_breaking": {
        "primary": "quadrant_priority",
        "secondary": "alphabetical by item.label within quadrant",
        "tertiary": "stable by original import order"
      },
      "quadrant_priority": [
        {
          "quadrant": "quick-wins",
          "rank": 1,
          "description": "High Impact / Low Effort - Start here"
        },
        {
          "quadrant": "big-bets",
          "rank": 2,
          "description": "High Impact / High Effort - Strategic initiatives"
        },
        {
          "quadrant": "fill-ins",
          "rank": 3,
          "description": "Low Impact / Low Effort - Do when you have spare capacity"
        },
        {
          "quadrant": "avoid",
          "rank": 4,
          "description": "Low Impact / High Effort - Avoid unless compelling reasons"
        }
      ],
      "determinism": true
    },
    "outputs": {
      "score_set_schema": {
        "per_item_fields": [
          {
            "name": "item_id",
            "type": "string"
          },
          {
            "name": "quadrant",
            "type": "enum",
            "allowed_values": [
              "quick-wins",
              "big-bets",
              "fill-ins",
              "avoid"
            ],
            "description": "The quadrant category assigned to this item."
          },
          {
            "name": "quadrant_rank",
            "type": "integer",
            "description": "Numeric rank of quadrant (1=quick-wins, 2=big-bets, 3=fill-ins, 4=avoid) for sorting purposes."
          },
          {
            "name": "impact",
            "type": "numeric",
            "description": "Original impact score."
          },
          {
            "name": "effort",
            "type": "numeric",
            "description": "Original effort score."
          }
        ],
        "annotations": [
          {
            "name": "tool_name",
            "type": "string",
            "value": "IMPACT_EFFORT_MATRIX"
          },
          {
            "name": "midpoint_threshold_used",
            "type": "numeric",
            "description": "The midpoint threshold used to determine high/low (e.g., 3 for 1-5 scale)"
          }
        ]
      },
      "tool_run_metadata": {
        "records_raw_inputs": true,
        "raw_inputs_schema": {
          "impact": "numeric",
          "effort": "numeric"
        }
      }
    },
    "interaction_model": {
      "ui_pattern": "sequential_scoring_then_categorization",
      "supports_inline_edit": true,
      "supports_batch_edit": false,
      "step_flow": [
        "input_items",
        "for_each_item_collect_impact",
        "for_each_item_collect_effort",
        "calculate_quadrants",
        "display_results_grouped_by_quadrant",
        "allow_quadrant_adjustments"
      ],
      "validation_behavior": {
        "on_invalid_item": "prevent_progression_until_valid",
        "on_any_error": "show_error_message_and_allow_correction"
      }
    },
    "constraints_and_assumptions": {
      "max_items_tested": 1000,
      "performance_notes": "O(n) over number of items for categorization. Efficient for large item sets.",
      "assumes_attribute_types": "Impact and effort must be numeric. Typically 1-5 scale, but any consistent scale works if midpoint is configured appropriately."
    },
    "test_vectors": [
      {
        "name": "simple_four_item_example",
        "items": [
          {
            "id": "A",
            "label": "Item A",
            "attributes": {
              "impact": 5,
              "effort": 1
            }
          },
          {
            "id": "B",
            "label": "Item B",
            "attributes": {
              "impact": 5,
              "effort": 5
            }
          },
          {
            "id": "C",
            "label": "Item C",
            "attributes": {
              "impact": 2,
              "effort": 2
            }
          },
          {
            "id": "D",
            "label": "Item D",
            "attributes": {
              "impact": 1,
              "effort": 5
            }
          }
        ],
        "tool_config": {
          "scale_max": 5,
          "midpoint_threshold": 3
        },
        "expected_categorizations": [
          {
            "item_id": "A",
            "quadrant": "quick-wins",
            "quadrant_rank": 1,
            "impact": 5,
            "effort": 1
          },
          {
            "item_id": "B",
            "quadrant": "big-bets",
            "quadrant_rank": 2,
            "impact": 5,
            "effort": 5
          },
          {
            "item_id": "C",
            "quadrant": "fill-ins",
            "quadrant_rank": 3,
            "impact": 2,
            "effort": 2
          },
          {
            "item_id": "D",
            "quadrant": "avoid",
            "quadrant_rank": 4,
            "impact": 1,
            "effort": 5
          }
        ]
      }
    ]
  },
  {
    "tool_spec_version": "1.0",
    "identity": {
      "tool_name": "MagicPrioritization",
      "short_label": "Magic Prioritization Trick",
      "description": "A staged decoupling approach that separates urgency, value, and duration judgments to surface qualitative Cost of Delay (CD3). Based on John Cutler's work and Joshua Arnold's qualitative cost of delay framework. The 'magic trick' prevents teams from collapsing multiple dimensions into a single intuitive judgment by constraining the order in which questions are answered.",
      "category": "scoring_model",
      "status": "stable",
      "attribution": {
        "primary_source": "John Cutler (TBM 245: The Magic Prioritization Trick)",
        "secondary_source": "Joshua Arnold (Black Swan Farming - Qualitative Cost of Delay)",
        "source_urls": [
          "https://cutlefish.substack.com/p/tbm-245-the-magic-prioritization",
          "https://blackswanfarming.com/qualitative-cost-delay/"
        ]
      }
    },
    "semantics": {
      "purpose": "Arrange-phase prioritization by surfacing weighted Cost of Delay (CD3) through staged decoupling of urgency, value, and duration judgments. The behavioral hack prevents premature synthesis and exposes cognitive biases that typically collapse these dimensions.",
      "requires_comparable_items": true,
      "notes_on_comparability": "Items should be at similar levels of abstraction (altitude). High-altitude themes should be broken down into options before plotting. All items are evaluated on three dimensions: urgency (time sensitivity), value (impact magnitude), and duration (time to first meaningful payoff)."
    },
    "inputs": {
      "required_item_fields": [
        "id",
        "label"
      ],
      "required_item_attributes": [
        {
          "key": "urgency",
          "type": "enum",
          "allowed_values": [
            "asap",
            "soon",
            "whenever"
          ],
          "description": "Time sensitivity of the item. ASAP = delay causes escalating harm or lost opportunity window. Soon = important timing but delay doesn't compound significantly. Whenever = no significant time pressure."
        },
        {
          "key": "value",
          "type": "enum",
          "allowed_values": [
            "game-changer",
            "optimizer",
            "tweak"
          ],
          "description": "Impact magnitude if completed. Game Changer = transforms outcomes significantly. Optimizer = meaningful improvement. Tweak = incremental benefit."
        },
        {
          "key": "duration",
          "type": "enum",
          "allowed_values": [
            "1-3-months",
            "1-3-quarters",
            "1-3-years"
          ],
          "description": "Time to first meaningful payoff or decision-grade evidence. Not just time-to-build, but time-to-realize-value."
        }
      ],
      "optional_item_attributes": [],
      "criteria_set_contract": {
        "allowed_criteria_ids": [
          "urgency",
          "value",
          "duration"
        ],
        "requires_explicit_criteria_set": false,
        "notes": "The tool uses three fixed criteria with enumerated values. Each dimension is evaluated independently in sequence to prevent premature synthesis."
      },
      "lens_support": {
        "requires_lens": false,
        "notes": "If a lens is provided, the tool only scores items within that lens."
      },
      "tool_config": {
        "fields": [
          {
            "name": "show_matrix",
            "type": "boolean",
            "default": true,
            "description": "Whether to display the 27-cell matrix visualization in results."
          },
          {
            "name": "category_thresholds",
            "type": "object",
            "default": {
              "high": 4,
              "medium": 1.5
            },
            "description": "Score thresholds for CD3 categories. High >= 4, Medium >= 1.5, Low < 1.5."
          }
        ],
        "supports_defaults": true
      }
    },
    "algorithm": {
      "score_model_type": "formula",
      "formula_description": "Weighted CD3 = (Urgency × Value) / Duration. This approximates Cost of Delay divided by Duration for sequencing.",
      "formula_variables": {
        "urgency": "enum mapped to numeric: asap=3, soon=2, whenever=1",
        "value": "enum mapped to numeric: game-changer=3, optimizer=2, tweak=1",
        "duration": "enum mapped to numeric divisor: 1-3-months=1, 1-3-quarters=2, 1-3-years=3"
      },
      "scaling_and_normalization": {
        "urgency_mapping": "asap=3, soon=2, whenever=1",
        "value_mapping": "game-changer=3, optimizer=2, tweak=1",
        "duration_mapping": "1-3-months=1, 1-3-quarters=2, 1-3-years=3 (as divisor)",
        "score_range": "Min: 0.33 (whenever × tweak / 1-3-years), Max: 9.0 (asap × game-changer / 1-3-months)"
      },
      "handles_missing_values": {
        "strategy": "reject_item",
        "rules": [
          "All three dimensions (urgency, value, duration) must be set for scoring.",
          "Items with any missing dimension are excluded and returned with error reason."
        ]
      },
      "tie_breaking": {
        "primary": "higher score wins (higher CD3 = higher priority)",
        "secondary": "urgency (ASAP > Soon > Whenever)",
        "tertiary": "value (Game Changer > Optimizer > Tweak)",
        "quaternary": "alphabetical by item.label"
      },
      "determinism": true,
      "notes": "The staged decoupling is a behavioral technique. By constraining the order of evaluation (urgency → value → duration with no backtracking on previous dimensions), the tool surfaces real cost of delay without complex calculations. The 27-cell matrix emerges from 3×3×3 combinations."
    },
    "outputs": {
      "score_set_schema": {
        "per_item_fields": [
          {
            "name": "item_id",
            "type": "string"
          },
          {
            "name": "score",
            "type": "numeric",
            "description": "Weighted CD3 score. Higher scores indicate higher priority."
          },
          {
            "name": "rank",
            "type": "integer",
            "description": "1 = highest priority within the evaluated set."
          },
          {
            "name": "cd3_category",
            "type": "enum",
            "allowed_values": [
              "high",
              "medium",
              "low"
            ],
            "description": "Priority category based on score thresholds."
          }
        ],
        "annotations": [
          {
            "name": "tool_name",
            "type": "string",
            "value": "MagicPrioritization"
          },
          {
            "name": "urgency",
            "type": "string",
            "description": "Original urgency selection"
          },
          {
            "name": "value",
            "type": "string",
            "description": "Original value selection"
          },
          {
            "name": "duration",
            "type": "string",
            "description": "Original duration selection"
          }
        ]
      },
      "tool_run_metadata": {
        "records_raw_inputs": true,
        "raw_inputs_schema": {
          "urgency": "enum",
          "value": "enum",
          "duration": "enum"
        },
        "additional_fields": {
          "total_items_scored": "integer",
          "items_by_category": {
            "high": "integer",
            "medium": "integer",
            "low": "integer"
          },
          "items_excluded": "integer",
          "exclusion_reasons": "array of strings"
        }
      }
    },
    "interaction_model": {
      "ui_pattern": "staged_sequential_scoring",
      "supports_inline_edit": false,
      "supports_batch_edit": false,
      "step_flow": [
        "input_items",
        "for_each_item: score_urgency",
        "for_each_item: score_value (urgency locked)",
        "for_each_item: score_duration (urgency and value locked)",
        "calculate_weighted_cd3",
        "categorize_items",
        "rank_items",
        "display_27_cell_matrix",
        "review_results"
      ],
      "validation_behavior": {
        "on_invalid_item": "mark_item_error_and_exclude_from_scoring",
        "on_any_error": "allow_run_with_warnings",
        "validation_rules": [
          "urgency must be one of: asap, soon, whenever",
          "value must be one of: game-changer, optimizer, tweak",
          "duration must be one of: 1-3-months, 1-3-quarters, 1-3-years"
        ]
      },
      "ui_notes": "The key behavioral technique is staged constraint: users evaluate urgency first, then value (without changing urgency), then duration (without changing either). Previous selections are shown as 'locked' context. The 27-cell matrix reveals the full picture only after all items are scored."
    },
    "constraints_and_assumptions": {
      "max_items_tested": 1000,
      "performance_notes": "O(n) over number of items for scoring. Sequential UI flow with 3 phases per item means user interaction time scales linearly.",
      "assumes_attribute_types": "All attributes are enumerated types with fixed allowed values.",
      "assumptions": [
        "Items are independent (no dependencies between items)",
        "Items are at comparable levels of abstraction/altitude",
        "Urgency captures time sensitivity, not importance",
        "Value captures outcome magnitude independent of urgency and duration",
        "Duration is time-to-first-meaningful-payoff, not time-to-complete-everything",
        "The staged decoupling prevents cognitive collapse of dimensions"
      ]
    },
    "toolRequirements": {
      "prerequisites": [
        "POOL_EXISTS"
      ],
      "requiredCriteria": [
        {
          "tag": "term:urgency",
          "criterionTypes": [
            "ordinal"
          ],
          "minCount": 1
        },
        {
          "tag": "term:value",
          "criterionTypes": [
            "ordinal"
          ],
          "minCount": 1
        },
        {
          "tag": "term:duration",
          "criterionTypes": [
            "ordinal"
          ],
          "minCount": 1
        }
      ],
      "poolRequirements": {
        "minItems": 2,
        "requireLocked": false
      }
    },
    "test_vectors": [
      {
        "name": "classic_cutler_example",
        "description": "Demonstrates that Soon + Game Changer + 1-3y equals ASAP + Optimizer + 1-3y",
        "items": [
          {
            "id": "item-1",
            "label": "Strategic Platform Modernization",
            "attributes": {
              "urgency": "soon",
              "value": "game-changer",
              "duration": "1-3-years"
            }
          },
          {
            "id": "item-2",
            "label": "Urgent Process Optimization",
            "attributes": {
              "urgency": "asap",
              "value": "optimizer",
              "duration": "1-3-years"
            }
          }
        ],
        "expected_scores": [
          {
            "item_id": "item-1",
            "score": 2,
            "cd3_category": "medium",
            "calculation": "soon(2) × game-changer(3) / 1-3-years(3) = 6/3 = 2.0"
          },
          {
            "item_id": "item-2",
            "score": 2,
            "cd3_category": "medium",
            "calculation": "asap(3) × optimizer(2) / 1-3-years(3) = 6/3 = 2.0"
          }
        ],
        "notes": "Both items have equivalent CD3 scores, illustrating Cutler's key insight about dimension trade-offs."
      },
      {
        "name": "quick_win_vs_long_game_changer",
        "description": "A quick optimizer beats a slow game changer",
        "items": [
          {
            "id": "item-a",
            "label": "Quick Win Optimization",
            "attributes": {
              "urgency": "asap",
              "value": "optimizer",
              "duration": "1-3-months"
            }
          },
          {
            "id": "item-b",
            "label": "Transformative Initiative",
            "attributes": {
              "urgency": "asap",
              "value": "game-changer",
              "duration": "1-3-years"
            }
          }
        ],
        "expected_scores": [
          {
            "item_id": "item-a",
            "score": 6,
            "rank": 1,
            "cd3_category": "high",
            "calculation": "asap(3) × optimizer(2) / 1-3-months(1) = 6/1 = 6.0"
          },
          {
            "item_id": "item-b",
            "score": 3,
            "rank": 2,
            "cd3_category": "medium",
            "calculation": "asap(3) × game-changer(3) / 1-3-years(3) = 9/3 = 3.0"
          }
        ],
        "notes": "The quick win scores higher despite lower value because the short duration increases throughput."
      },
      {
        "name": "full_range_example",
        "items": [
          {
            "id": "max",
            "label": "Maximum Priority Item",
            "attributes": {
              "urgency": "asap",
              "value": "game-changer",
              "duration": "1-3-months"
            }
          },
          {
            "id": "min",
            "label": "Minimum Priority Item",
            "attributes": {
              "urgency": "whenever",
              "value": "tweak",
              "duration": "1-3-years"
            }
          }
        ],
        "expected_scores": [
          {
            "item_id": "max",
            "score": 9,
            "rank": 1,
            "cd3_category": "high",
            "calculation": "asap(3) × game-changer(3) / 1-3-months(1) = 9/1 = 9.0"
          },
          {
            "item_id": "min",
            "score": 0.33,
            "rank": 2,
            "cd3_category": "low",
            "calculation": "whenever(1) × tweak(1) / 1-3-years(3) = 1/3 = 0.33"
          }
        ]
      }
    ]
  },
  {
    "tool_spec_version": "1.0",
    "identity": {
      "tool_name": "Max Priorities Pyramid",
      "short_label": "Pyramid",
      "description": "Visualizes relationships among priorities across time and categories using a pyramid structure. Connects high-level strategic goals to tactical actions through three time horizons and strategic directions.",
      "category": "framework",
      "status": "stable"
    },
    "semantics": {
      "purpose": "Arrange-phase prioritization by organizing items into a multi-dimensional pyramid structure that highlights relationships between strategic and tactical priorities across time horizons and categories.",
      "requires_comparable_items": false,
      "notes_on_comparability": "Items are organized into segments rather than directly compared. Stack ranking occurs within each segment (category × time horizon × strategic direction combination)."
    },
    "inputs": {
      "required_item_fields": [
        "id",
        "label"
      ],
      "required_item_attributes": [
        {
          "key": "category",
          "type": "enum",
          "description": "The category bucket for the item. Must be one of: 'Process Automation', 'Tech Debt', 'Defects', 'Other Priorities'.",
          "allowed_values": [
            "Process Automation",
            "Tech Debt",
            "Defects",
            "Other Priorities"
          ]
        },
        {
          "key": "timeHorizon",
          "type": "enum",
          "description": "The time horizon for the priority. 'Transform' = long-term future, 'Grow' = mid-term strategic, 'Run' = immediate/tactical.",
          "allowed_values": [
            "Transform",
            "Grow",
            "Run"
          ]
        },
        {
          "key": "strategicDirection",
          "type": "enum",
          "description": "The strategic direction for the priority. 'Get House in Order' = foundational/tactical, 'Reinforce Foundation' = strengthening existing, 'Create the Future' = innovative/strategic.",
          "allowed_values": [
            "Get House in Order",
            "Reinforce Foundation",
            "Create the Future"
          ]
        }
      ],
      "optional_item_attributes": [
        {
          "key": "description",
          "type": "string",
          "description": "Optional detailed description of the priority item."
        },
        {
          "key": "rank",
          "type": "integer",
          "description": "Stack rank within the segment (category + timeHorizon + strategicDirection). Lower numbers = higher priority within segment. Defaults to 0 if not provided."
        }
      ],
      "criteria_set_contract": {
        "allowed_criteria_ids": [],
        "requires_explicit_criteria_set": false,
        "notes": "The Max Priorities Pyramid does not use a CriteriaSet. It uses fixed dimensions (category, timeHorizon, strategicDirection) that are part of the item attributes rather than external criteria."
      },
      "lens_support": {
        "requires_lens": false,
        "notes": "If a lens is provided, the tool only organizes items within that lens. The pyramid structure applies to the filtered set."
      },
      "tool_config": {
        "fields": [
          {
            "name": "northStar",
            "type": "string",
            "required": true,
            "description": "The North Star priority - the one priority, goal, metric, or mission that every other priority should point toward. Displayed at the top of the pyramid."
          },
          {
            "name": "categories",
            "type": "array",
            "default": [
              "Process Automation",
              "Tech Debt",
              "Defects",
              "Other Priorities"
            ],
            "description": "Customizable category buckets. Defaults to standard four categories but can be overridden."
          }
        ],
        "supports_defaults": true
      }
    },
    "algorithm": {
      "score_model_type": "organizational_framework",
      "formula_description": "Items are organized into segments defined by the Cartesian product of (category × timeHorizon × strategicDirection). Within each segment, items are stack-ranked by the 'rank' attribute (lower rank = higher priority within segment).",
      "formula_variables": {
        "category": "enum: one of the four category buckets",
        "timeHorizon": "enum: Transform | Grow | Run",
        "strategicDirection": "enum: Get House in Order | Reinforce Foundation | Create the Future",
        "rank": "integer: stack rank within segment (0 = highest priority in segment)"
      },
      "scaling_and_normalization": {
        "normalizes_confidence": "N/A - no confidence scoring",
        "normalizes_other_fields": "Ranks are relative within each segment only. No cross-segment normalization."
      },
      "handles_missing_values": {
        "strategy": "assign_defaults",
        "rules": [
          "If category is missing, defaults to 'Other Priorities'",
          "If timeHorizon is missing, defaults to 'Run'",
          "If strategicDirection is missing, defaults to 'Get House in Order'",
          "If rank is missing, defaults to 0 (or next available rank in segment if items already exist)"
        ]
      },
      "tie_breaking": {
        "primary": "lower rank value wins within segment",
        "secondary": "alphabetical by item.label within same rank",
        "tertiary": "stable by original import order"
      },
      "determinism": true,
      "notes": "The pyramid structure is deterministic: items are always placed in the same segment given the same attribute values. Ranking within segments is deterministic based on rank attribute."
    },
    "outputs": {
      "score_set_schema": {
        "per_item_fields": [
          {
            "name": "item_id",
            "type": "string"
          },
          {
            "name": "segment_key",
            "type": "string",
            "description": "Composite key: '{category}-{timeHorizon}-{strategicDirection}'"
          },
          {
            "name": "category",
            "type": "string",
            "description": "The category bucket the item is assigned to"
          },
          {
            "name": "timeHorizon",
            "type": "string",
            "description": "The time horizon: Transform, Grow, or Run"
          },
          {
            "name": "strategicDirection",
            "type": "string",
            "description": "The strategic direction: Get House in Order, Reinforce Foundation, or Create the Future"
          },
          {
            "name": "rank",
            "type": "integer",
            "description": "Stack rank within the segment (1 = highest priority in segment, 2 = second, etc.)"
          },
          {
            "name": "rank_within_segment",
            "type": "integer",
            "description": "1-indexed position within the segment after sorting by rank attribute"
          }
        ],
        "annotations": [
          {
            "name": "tool_name",
            "type": "string",
            "value": "Max Priorities Pyramid"
          },
          {
            "name": "north_star",
            "type": "string",
            "description": "The North Star priority that guides all other priorities"
          },
          {
            "name": "segment_summary",
            "type": "object",
            "description": "Count of items per segment"
          }
        ]
      },
      "tool_run_metadata": {
        "records_raw_inputs": true,
        "raw_inputs_schema": {
          "category": "string (enum)",
          "timeHorizon": "string (enum)",
          "strategicDirection": "string (enum)",
          "rank": "integer",
          "description": "string (optional)"
        }
      }
    },
    "interaction_model": {
      "ui_pattern": "pyramid_visualization_with_editing",
      "supports_inline_edit": true,
      "supports_batch_edit": false,
      "step_flow": [
        "setup_north_star",
        "add_items",
        "categorize_items",
        "assign_time_horizon",
        "assign_strategic_direction",
        "stack_rank_within_segments",
        "review_pyramid",
        "view_or_edit_mode"
      ],
      "validation_behavior": {
        "on_invalid_item": "assign_defaults_and_allow_edit",
        "on_any_error": "allow_run_with_warnings_and_default_assignments"
      },
      "notes": "The UI displays a pyramid structure with three horizontal layers (time horizons) and four vertical columns (categories). Each cell contains items organized by strategic direction, with stack ranking visible within each segment. Users can switch between View Mode (read-only) and Edit Mode (full editing capabilities)."
    },
    "constraints_and_assumptions": {
      "max_items_tested": 1000,
      "performance_notes": "O(n) for organizing items into segments, O(n log n) for sorting within segments. Visual rendering scales with number of segments (12 segments = 4 categories × 3 time horizons, with 3 strategic directions per segment = 36 possible segment cells).",
      "assumes_attribute_types": "Category, timeHorizon, and strategicDirection must be valid enum values. Rank must be parseable as integer.",
      "visual_constraints": "The pyramid visualization works best with 10-100 items. Very large item sets may require pagination or filtering by lens.",
      "assumptions": [
        "Items can belong to exactly one segment (one category, one time horizon, one strategic direction combination)",
        "Stack ranking is meaningful only within a segment, not across segments",
        "The North Star priority is a single string that guides all other priorities but is not itself an item in the pyramid"
      ]
    },
    "toolRequirements": {
      "prerequisites": [
        "POOL_NONEMPTY"
      ],
      "requiredCriteria": [
        {
          "tag": "category:*",
          "criterionTypes": [
            "categorical"
          ],
          "minCount": 1
        }
      ],
      "poolRequirements": {
        "minItems": 3,
        "requireLocked": false
      }
    },
    "test_vectors": [
      {
        "name": "simple_three_item_example",
        "items": [
          {
            "id": "item-1",
            "label": "Automate deployment pipeline",
            "description": "Reduce manual deployment steps",
            "attributes": {
              "category": "Process Automation",
              "timeHorizon": "Run",
              "strategicDirection": "Get House in Order",
              "rank": 1
            }
          },
          {
            "id": "item-2",
            "label": "Refactor authentication system",
            "description": "Replace legacy auth with modern OAuth2",
            "attributes": {
              "category": "Tech Debt",
              "timeHorizon": "Grow",
              "strategicDirection": "Reinforce Foundation",
              "rank": 1
            }
          },
          {
            "id": "item-3",
            "label": "Build AI-powered recommendation engine",
            "description": "New ML capability for product recommendations",
            "attributes": {
              "category": "Other Priorities",
              "timeHorizon": "Transform",
              "strategicDirection": "Create the Future",
              "rank": 1
            }
          }
        ],
        "tool_config": {
          "northStar": "Deliver exceptional customer experience through reliable, scalable technology",
          "categories": [
            "Process Automation",
            "Tech Debt",
            "Defects",
            "Other Priorities"
          ]
        },
        "expected_scores": [
          {
            "item_id": "item-1",
            "segment_key": "Process Automation-Run-Get House in Order",
            "category": "Process Automation",
            "timeHorizon": "Run",
            "strategicDirection": "Get House in Order",
            "rank": 1,
            "rank_within_segment": 1
          },
          {
            "item_id": "item-2",
            "segment_key": "Tech Debt-Grow-Reinforce Foundation",
            "category": "Tech Debt",
            "timeHorizon": "Grow",
            "strategicDirection": "Reinforce Foundation",
            "rank": 1,
            "rank_within_segment": 1
          },
          {
            "item_id": "item-3",
            "segment_key": "Other Priorities-Transform-Create the Future",
            "category": "Other Priorities",
            "timeHorizon": "Transform",
            "strategicDirection": "Create the Future",
            "rank": 1,
            "rank_within_segment": 1
          }
        ]
      },
      {
        "name": "stack_ranking_within_segment",
        "items": [
          {
            "id": "bug-1",
            "label": "Critical security vulnerability",
            "attributes": {
              "category": "Defects",
              "timeHorizon": "Run",
              "strategicDirection": "Get House in Order",
              "rank": 1
            }
          },
          {
            "id": "bug-2",
            "label": "Minor UI glitch",
            "attributes": {
              "category": "Defects",
              "timeHorizon": "Run",
              "strategicDirection": "Get House in Order",
              "rank": 2
            }
          },
          {
            "id": "bug-3",
            "label": "Performance issue in search",
            "attributes": {
              "category": "Defects",
              "timeHorizon": "Run",
              "strategicDirection": "Get House in Order",
              "rank": 3
            }
          }
        ],
        "tool_config": {
          "northStar": "Maintain system reliability and security",
          "categories": [
            "Process Automation",
            "Tech Debt",
            "Defects",
            "Other Priorities"
          ]
        },
        "expected_scores": [
          {
            "item_id": "bug-1",
            "segment_key": "Defects-Run-Get House in Order",
            "category": "Defects",
            "timeHorizon": "Run",
            "strategicDirection": "Get House in Order",
            "rank": 1,
            "rank_within_segment": 1
          },
          {
            "item_id": "bug-2",
            "segment_key": "Defects-Run-Get House in Order",
            "category": "Defects",
            "timeHorizon": "Run",
            "strategicDirection": "Get House in Order",
            "rank": 2,
            "rank_within_segment": 2
          },
          {
            "item_id": "bug-3",
            "segment_key": "Defects-Run-Get House in Order",
            "category": "Defects",
            "timeHorizon": "Run",
            "strategicDirection": "Get House in Order",
            "rank": 3,
            "rank_within_segment": 3
          }
        ]
      },
      {
        "name": "missing_attributes_defaults",
        "items": [
          {
            "id": "item-incomplete",
            "label": "Item with missing attributes",
            "attributes": {}
          }
        ],
        "tool_config": {
          "northStar": "Test default assignments",
          "categories": [
            "Process Automation",
            "Tech Debt",
            "Defects",
            "Other Priorities"
          ]
        },
        "expected_scores": [
          {
            "item_id": "item-incomplete",
            "segment_key": "Other Priorities-Run-Get House in Order",
            "category": "Other Priorities",
            "timeHorizon": "Run",
            "strategicDirection": "Get House in Order",
            "rank": 0,
            "rank_within_segment": 1
          }
        ]
      }
    ]
  },
  {
    "tool_spec_version": "1.0",
    "identity": {
      "tool_name": "Max Priorities Pyramid",
      "short_label": "Pyramid",
      "description": "Visualizes relationships among priorities across time and categories using a pyramid structure. Connects high-level strategic goals to tactical actions through three time horizons and strategic directions.",
      "category": "framework",
      "status": "stable"
    },
    "semantics": {
      "purpose": "Arrange-phase prioritization by organizing items into a multi-dimensional pyramid structure that highlights relationships between strategic and tactical priorities across time horizons and categories.",
      "requires_comparable_items": false,
      "notes_on_comparability": "Items are organized into segments rather than directly compared. Stack ranking occurs within each segment (category × time horizon × strategic direction combination)."
    },
    "inputs": {
      "required_item_fields": [
        "id",
        "label"
      ],
      "required_item_attributes": [
        {
          "key": "category",
          "type": "enum",
          "description": "The category bucket for the item. Must be one of: 'Process Automation', 'Tech Debt', 'Defects', 'Other Priorities'.",
          "allowed_values": [
            "Process Automation",
            "Tech Debt",
            "Defects",
            "Other Priorities"
          ]
        },
        {
          "key": "timeHorizon",
          "type": "enum",
          "description": "The time horizon for the priority. 'Transform' = long-term future, 'Grow' = mid-term strategic, 'Run' = immediate/tactical.",
          "allowed_values": [
            "Transform",
            "Grow",
            "Run"
          ]
        },
        {
          "key": "strategicDirection",
          "type": "enum",
          "description": "The strategic direction for the priority. 'Get House in Order' = foundational/tactical, 'Reinforce Foundation' = strengthening existing, 'Create the Future' = innovative/strategic.",
          "allowed_values": [
            "Get House in Order",
            "Reinforce Foundation",
            "Create the Future"
          ]
        }
      ],
      "optional_item_attributes": [
        {
          "key": "description",
          "type": "string",
          "description": "Optional detailed description of the priority item."
        },
        {
          "key": "rank",
          "type": "integer",
          "description": "Stack rank within the segment (category + timeHorizon + strategicDirection). Lower numbers = higher priority within segment. Defaults to 0 if not provided."
        }
      ],
      "criteria_set_contract": {
        "allowed_criteria_ids": [],
        "requires_explicit_criteria_set": false,
        "notes": "The Max Priorities Pyramid does not use a CriteriaSet. It uses fixed dimensions (category, timeHorizon, strategicDirection) that are part of the item attributes rather than external criteria."
      },
      "lens_support": {
        "requires_lens": false,
        "notes": "If a lens is provided, the tool only organizes items within that lens. The pyramid structure applies to the filtered set."
      },
      "tool_config": {
        "fields": [
          {
            "name": "northStar",
            "type": "string",
            "required": true,
            "description": "The North Star priority - the one priority, goal, metric, or mission that every other priority should point toward. Displayed at the top of the pyramid."
          },
          {
            "name": "categories",
            "type": "array",
            "default": [
              "Process Automation",
              "Tech Debt",
              "Defects",
              "Other Priorities"
            ],
            "description": "Customizable category buckets. Defaults to standard four categories but can be overridden."
          }
        ],
        "supports_defaults": true
      }
    },
    "algorithm": {
      "score_model_type": "organizational_framework",
      "formula_description": "Items are organized into segments defined by the Cartesian product of (category × timeHorizon × strategicDirection). Within each segment, items are stack-ranked by the 'rank' attribute (lower rank = higher priority within segment).",
      "formula_variables": {
        "category": "enum: one of the four category buckets",
        "timeHorizon": "enum: Transform | Grow | Run",
        "strategicDirection": "enum: Get House in Order | Reinforce Foundation | Create the Future",
        "rank": "integer: stack rank within segment (0 = highest priority in segment)"
      },
      "scaling_and_normalization": {
        "normalizes_confidence": "N/A - no confidence scoring",
        "normalizes_other_fields": "Ranks are relative within each segment only. No cross-segment normalization."
      },
      "handles_missing_values": {
        "strategy": "assign_defaults",
        "rules": [
          "If category is missing, defaults to 'Other Priorities'",
          "If timeHorizon is missing, defaults to 'Run'",
          "If strategicDirection is missing, defaults to 'Get House in Order'",
          "If rank is missing, defaults to 0 (or next available rank in segment if items already exist)"
        ]
      },
      "tie_breaking": {
        "primary": "lower rank value wins within segment",
        "secondary": "alphabetical by item.label within same rank",
        "tertiary": "stable by original import order"
      },
      "determinism": true,
      "notes": "The pyramid structure is deterministic: items are always placed in the same segment given the same attribute values. Ranking within segments is deterministic based on rank attribute."
    },
    "outputs": {
      "score_set_schema": {
        "per_item_fields": [
          {
            "name": "item_id",
            "type": "string"
          },
          {
            "name": "segment_key",
            "type": "string",
            "description": "Composite key: '{category}-{timeHorizon}-{strategicDirection}'"
          },
          {
            "name": "category",
            "type": "string",
            "description": "The category bucket the item is assigned to"
          },
          {
            "name": "timeHorizon",
            "type": "string",
            "description": "The time horizon: Transform, Grow, or Run"
          },
          {
            "name": "strategicDirection",
            "type": "string",
            "description": "The strategic direction: Get House in Order, Reinforce Foundation, or Create the Future"
          },
          {
            "name": "rank",
            "type": "integer",
            "description": "Stack rank within the segment (1 = highest priority in segment, 2 = second, etc.)"
          },
          {
            "name": "rank_within_segment",
            "type": "integer",
            "description": "1-indexed position within the segment after sorting by rank attribute"
          }
        ],
        "annotations": [
          {
            "name": "tool_name",
            "type": "string",
            "value": "Max Priorities Pyramid"
          },
          {
            "name": "north_star",
            "type": "string",
            "description": "The North Star priority that guides all other priorities"
          },
          {
            "name": "segment_summary",
            "type": "object",
            "description": "Count of items per segment"
          }
        ]
      },
      "tool_run_metadata": {
        "records_raw_inputs": true,
        "raw_inputs_schema": {
          "category": "string (enum)",
          "timeHorizon": "string (enum)",
          "strategicDirection": "string (enum)",
          "rank": "integer",
          "description": "string (optional)"
        }
      }
    },
    "interaction_model": {
      "ui_pattern": "pyramid_visualization_with_editing",
      "supports_inline_edit": true,
      "supports_batch_edit": false,
      "step_flow": [
        "setup_north_star",
        "add_items",
        "categorize_items",
        "assign_time_horizon",
        "assign_strategic_direction",
        "stack_rank_within_segments",
        "review_pyramid",
        "view_or_edit_mode"
      ],
      "validation_behavior": {
        "on_invalid_item": "assign_defaults_and_allow_edit",
        "on_any_error": "allow_run_with_warnings_and_default_assignments"
      },
      "notes": "The UI displays a pyramid structure with three horizontal layers (time horizons) and four vertical columns (categories). Each cell contains items organized by strategic direction, with stack ranking visible within each segment. Users can switch between View Mode (read-only) and Edit Mode (full editing capabilities)."
    },
    "constraints_and_assumptions": {
      "max_items_tested": 1000,
      "performance_notes": "O(n) for organizing items into segments, O(n log n) for sorting within segments. Visual rendering scales with number of segments (12 segments = 4 categories × 3 time horizons, with 3 strategic directions per segment = 36 possible segment cells).",
      "assumes_attribute_types": "Category, timeHorizon, and strategicDirection must be valid enum values. Rank must be parseable as integer.",
      "visual_constraints": "The pyramid visualization works best with 10-100 items. Very large item sets may require pagination or filtering by lens.",
      "assumptions": [
        "Items can belong to exactly one segment (one category, one time horizon, one strategic direction combination)",
        "Stack ranking is meaningful only within a segment, not across segments",
        "The North Star priority is a single string that guides all other priorities but is not itself an item in the pyramid"
      ]
    },
    "test_vectors": [
      {
        "name": "simple_three_item_example",
        "items": [
          {
            "id": "item-1",
            "label": "Automate deployment pipeline",
            "description": "Reduce manual deployment steps",
            "attributes": {
              "category": "Process Automation",
              "timeHorizon": "Run",
              "strategicDirection": "Get House in Order",
              "rank": 1
            }
          },
          {
            "id": "item-2",
            "label": "Refactor authentication system",
            "description": "Replace legacy auth with modern OAuth2",
            "attributes": {
              "category": "Tech Debt",
              "timeHorizon": "Grow",
              "strategicDirection": "Reinforce Foundation",
              "rank": 1
            }
          },
          {
            "id": "item-3",
            "label": "Build AI-powered recommendation engine",
            "description": "New ML capability for product recommendations",
            "attributes": {
              "category": "Other Priorities",
              "timeHorizon": "Transform",
              "strategicDirection": "Create the Future",
              "rank": 1
            }
          }
        ],
        "tool_config": {
          "northStar": "Deliver exceptional customer experience through reliable, scalable technology",
          "categories": [
            "Process Automation",
            "Tech Debt",
            "Defects",
            "Other Priorities"
          ]
        },
        "expected_scores": [
          {
            "item_id": "item-1",
            "segment_key": "Process Automation-Run-Get House in Order",
            "category": "Process Automation",
            "timeHorizon": "Run",
            "strategicDirection": "Get House in Order",
            "rank": 1,
            "rank_within_segment": 1
          },
          {
            "item_id": "item-2",
            "segment_key": "Tech Debt-Grow-Reinforce Foundation",
            "category": "Tech Debt",
            "timeHorizon": "Grow",
            "strategicDirection": "Reinforce Foundation",
            "rank": 1,
            "rank_within_segment": 1
          },
          {
            "item_id": "item-3",
            "segment_key": "Other Priorities-Transform-Create the Future",
            "category": "Other Priorities",
            "timeHorizon": "Transform",
            "strategicDirection": "Create the Future",
            "rank": 1,
            "rank_within_segment": 1
          }
        ]
      },
      {
        "name": "stack_ranking_within_segment",
        "items": [
          {
            "id": "bug-1",
            "label": "Critical security vulnerability",
            "attributes": {
              "category": "Defects",
              "timeHorizon": "Run",
              "strategicDirection": "Get House in Order",
              "rank": 1
            }
          },
          {
            "id": "bug-2",
            "label": "Minor UI glitch",
            "attributes": {
              "category": "Defects",
              "timeHorizon": "Run",
              "strategicDirection": "Get House in Order",
              "rank": 2
            }
          },
          {
            "id": "bug-3",
            "label": "Performance issue in search",
            "attributes": {
              "category": "Defects",
              "timeHorizon": "Run",
              "strategicDirection": "Get House in Order",
              "rank": 3
            }
          }
        ],
        "tool_config": {
          "northStar": "Maintain system reliability and security",
          "categories": [
            "Process Automation",
            "Tech Debt",
            "Defects",
            "Other Priorities"
          ]
        },
        "expected_scores": [
          {
            "item_id": "bug-1",
            "segment_key": "Defects-Run-Get House in Order",
            "category": "Defects",
            "timeHorizon": "Run",
            "strategicDirection": "Get House in Order",
            "rank": 1,
            "rank_within_segment": 1
          },
          {
            "item_id": "bug-2",
            "segment_key": "Defects-Run-Get House in Order",
            "category": "Defects",
            "timeHorizon": "Run",
            "strategicDirection": "Get House in Order",
            "rank": 2,
            "rank_within_segment": 2
          },
          {
            "item_id": "bug-3",
            "segment_key": "Defects-Run-Get House in Order",
            "category": "Defects",
            "timeHorizon": "Run",
            "strategicDirection": "Get House in Order",
            "rank": 3,
            "rank_within_segment": 3
          }
        ]
      },
      {
        "name": "missing_attributes_defaults",
        "items": [
          {
            "id": "item-incomplete",
            "label": "Item with missing attributes",
            "attributes": {}
          }
        ],
        "tool_config": {
          "northStar": "Test default assignments",
          "categories": [
            "Process Automation",
            "Tech Debt",
            "Defects",
            "Other Priorities"
          ]
        },
        "expected_scores": [
          {
            "item_id": "item-incomplete",
            "segment_key": "Other Priorities-Run-Get House in Order",
            "category": "Other Priorities",
            "timeHorizon": "Run",
            "strategicDirection": "Get House in Order",
            "rank": 0,
            "rank_within_segment": 1
          }
        ]
      }
    ]
  },
  {
    "tool_spec_version": "1.0",
    "identity": {
      "tool_name": "MOSCOW",
      "short_label": "MoSCoW Method",
      "description": "Categorizes items into Must Have, Should Have, Could Have, or Won't Have. Simple and effective for scope management and requirement prioritization.",
      "category": "categorization_model",
      "status": "stable"
    },
    "semantics": {
      "purpose": "Arrange-phase prioritization by categorizing items into four priority buckets rather than ranking.",
      "requires_comparable_items": false,
      "notes_on_comparability": "Items are evaluated independently and assigned to categories based on essentiality and importance."
    },
    "inputs": {
      "required_item_fields": [
        "id",
        "label"
      ],
      "required_item_attributes": [],
      "optional_item_attributes": [
        {
          "key": "description",
          "type": "string",
          "description": "Optional description to help with categorization decisions."
        }
      ],
      "criteria_set_contract": {
        "allowed_criteria_ids": [],
        "requires_explicit_criteria_set": false,
        "notes": "MoSCoW does not use explicit criteria. Categorization is based on user judgment of essentiality."
      },
      "lens_support": {
        "requires_lens": false,
        "notes": "If a lens is provided, the tool only categorizes items within that lens."
      },
      "tool_config": {
        "fields": [],
        "supports_defaults": true
      }
    },
    "algorithm": {
      "score_model_type": "categorization",
      "formula_description": "Each item is assigned to exactly one category: Must, Should, Could, or Wont",
      "formula_variables": {},
      "scaling_and_normalization": {
        "normalizes_confidence": "N/A",
        "normalizes_other_fields": "N/A"
      },
      "handles_missing_values": {
        "strategy": "require_category_assignment",
        "rules": [
          "Every item must be assigned to a category. Items without a category assignment cannot be processed."
        ]
      },
      "tie_breaking": {
        "primary": "category_hierarchy",
        "secondary": "alphabetical by item.label within category",
        "tertiary": "stable by original import order"
      },
      "category_hierarchy": [
        {
          "category": "Must",
          "rank": 1,
          "description": "Essential, non-negotiable. The project cannot succeed without this."
        },
        {
          "category": "Should",
          "rank": 2,
          "description": "Important but not essential. High value but can be deferred if needed."
        },
        {
          "category": "Could",
          "rank": 3,
          "description": "Nice-to-have. Low impact if absent. Can be included if time/resources allow."
        },
        {
          "category": "Wont",
          "rank": 4,
          "description": "Explicitly out of scope for now. May be considered in future iterations."
        }
      ],
      "determinism": true
    },
    "outputs": {
      "score_set_schema": {
        "per_item_fields": [
          {
            "name": "item_id",
            "type": "string"
          },
          {
            "name": "category",
            "type": "enum",
            "allowed_values": [
              "Must",
              "Should",
              "Could",
              "Wont"
            ],
            "description": "The MoSCoW category assigned to this item."
          },
          {
            "name": "category_rank",
            "type": "integer",
            "description": "Numeric rank of category (1=Must, 2=Should, 3=Could, 4=Wont) for sorting purposes."
          }
        ],
        "annotations": [
          {
            "name": "tool_name",
            "type": "string",
            "value": "MOSCOW"
          }
        ]
      },
      "tool_run_metadata": {
        "records_raw_inputs": false,
        "raw_inputs_schema": {}
      }
    },
    "interaction_model": {
      "ui_pattern": "sequential_categorization",
      "supports_inline_edit": true,
      "supports_batch_edit": true,
      "step_flow": [
        "input_items",
        "for_each_item_show_category_options",
        "user_selects_category",
        "review_all_categorizations",
        "allow_category_adjustments",
        "finalize_categorizations"
      ],
      "validation_behavior": {
        "on_invalid_item": "require_category_before_proceeding",
        "on_any_error": "allow_review_and_adjustment"
      }
    },
    "constraints_and_assumptions": {
      "max_items_tested": 1000,
      "performance_notes": "O(n) over number of items. No computational complexity, only user interaction time.",
      "assumes_attribute_types": "No numeric calculations required. Pure categorization based on user judgment."
    },
    "toolRequirements": {
      "prerequisites": [
        "POOL_EXISTS"
      ],
      "requiredCriteria": [],
      "poolRequirements": {
        "minItems": 1,
        "requireLocked": false
      }
    },
    "test_vectors": [
      {
        "name": "simple_four_item_example",
        "items": [
          {
            "id": "A",
            "label": "User authentication"
          },
          {
            "id": "B",
            "label": "Email notifications"
          },
          {
            "id": "C",
            "label": "Dark mode theme"
          },
          {
            "id": "D",
            "label": "Advanced analytics dashboard"
          }
        ],
        "tool_config": {},
        "expected_categorizations": [
          {
            "item_id": "A",
            "category": "Must",
            "category_rank": 1
          },
          {
            "item_id": "B",
            "category": "Should",
            "category_rank": 2
          },
          {
            "item_id": "C",
            "category": "Could",
            "category_rank": 3
          },
          {
            "item_id": "D",
            "category": "Wont",
            "category_rank": 4
          }
        ]
      }
    ]
  },
  {
    "tool_spec_version": "1.0",
    "identity": {
      "tool_name": "MOSCOW",
      "short_label": "MoSCoW Method",
      "description": "Categorizes items into Must Have, Should Have, Could Have, or Won't Have. Simple and effective for scope management and requirement prioritization.",
      "category": "categorization_model",
      "status": "stable"
    },
    "semantics": {
      "purpose": "Arrange-phase prioritization by categorizing items into four priority buckets rather than ranking.",
      "requires_comparable_items": false,
      "notes_on_comparability": "Items are evaluated independently and assigned to categories based on essentiality and importance."
    },
    "inputs": {
      "required_item_fields": [
        "id",
        "label"
      ],
      "required_item_attributes": [],
      "optional_item_attributes": [
        {
          "key": "description",
          "type": "string",
          "description": "Optional description to help with categorization decisions."
        }
      ],
      "criteria_set_contract": {
        "allowed_criteria_ids": [],
        "requires_explicit_criteria_set": false,
        "notes": "MoSCoW does not use explicit criteria. Categorization is based on user judgment of essentiality."
      },
      "lens_support": {
        "requires_lens": false,
        "notes": "If a lens is provided, the tool only categorizes items within that lens."
      },
      "tool_config": {
        "fields": [],
        "supports_defaults": true
      }
    },
    "algorithm": {
      "score_model_type": "categorization",
      "formula_description": "Each item is assigned to exactly one category: Must, Should, Could, or Wont",
      "formula_variables": {},
      "scaling_and_normalization": {
        "normalizes_confidence": "N/A",
        "normalizes_other_fields": "N/A"
      },
      "handles_missing_values": {
        "strategy": "require_category_assignment",
        "rules": [
          "Every item must be assigned to a category. Items without a category assignment cannot be processed."
        ]
      },
      "tie_breaking": {
        "primary": "category_hierarchy",
        "secondary": "alphabetical by item.label within category",
        "tertiary": "stable by original import order"
      },
      "category_hierarchy": [
        {
          "category": "Must",
          "rank": 1,
          "description": "Essential, non-negotiable. The project cannot succeed without this."
        },
        {
          "category": "Should",
          "rank": 2,
          "description": "Important but not essential. High value but can be deferred if needed."
        },
        {
          "category": "Could",
          "rank": 3,
          "description": "Nice-to-have. Low impact if absent. Can be included if time/resources allow."
        },
        {
          "category": "Wont",
          "rank": 4,
          "description": "Explicitly out of scope for now. May be considered in future iterations."
        }
      ],
      "determinism": true
    },
    "outputs": {
      "score_set_schema": {
        "per_item_fields": [
          {
            "name": "item_id",
            "type": "string"
          },
          {
            "name": "category",
            "type": "enum",
            "allowed_values": [
              "Must",
              "Should",
              "Could",
              "Wont"
            ],
            "description": "The MoSCoW category assigned to this item."
          },
          {
            "name": "category_rank",
            "type": "integer",
            "description": "Numeric rank of category (1=Must, 2=Should, 3=Could, 4=Wont) for sorting purposes."
          }
        ],
        "annotations": [
          {
            "name": "tool_name",
            "type": "string",
            "value": "MOSCOW"
          }
        ]
      },
      "tool_run_metadata": {
        "records_raw_inputs": false,
        "raw_inputs_schema": {}
      }
    },
    "interaction_model": {
      "ui_pattern": "sequential_categorization",
      "supports_inline_edit": true,
      "supports_batch_edit": true,
      "step_flow": [
        "input_items",
        "for_each_item_show_category_options",
        "user_selects_category",
        "review_all_categorizations",
        "allow_category_adjustments",
        "finalize_categorizations"
      ],
      "validation_behavior": {
        "on_invalid_item": "require_category_before_proceeding",
        "on_any_error": "allow_review_and_adjustment"
      }
    },
    "constraints_and_assumptions": {
      "max_items_tested": 1000,
      "performance_notes": "O(n) over number of items. No computational complexity, only user interaction time.",
      "assumes_attribute_types": "No numeric calculations required. Pure categorization based on user judgment."
    },
    "test_vectors": [
      {
        "name": "simple_four_item_example",
        "items": [
          {
            "id": "A",
            "label": "User authentication"
          },
          {
            "id": "B",
            "label": "Email notifications"
          },
          {
            "id": "C",
            "label": "Dark mode theme"
          },
          {
            "id": "D",
            "label": "Advanced analytics dashboard"
          }
        ],
        "tool_config": {},
        "expected_categorizations": [
          {
            "item_id": "A",
            "category": "Must",
            "category_rank": 1
          },
          {
            "item_id": "B",
            "category": "Should",
            "category_rank": 2
          },
          {
            "item_id": "C",
            "category": "Could",
            "category_rank": 3
          },
          {
            "item_id": "D",
            "category": "Wont",
            "category_rank": 4
          }
        ]
      }
    ]
  },
  {
    "tool_spec_version": "1.0",
    "identity": {
      "tool_name": "OPPORTUNITY_SOLUTION_TREE",
      "short_label": "Opportunity Solution Tree",
      "description": "Visualizes the path from desired outcomes to validated solutions through customer-centric discovery. Based on Teresa Torres' Continuous Discovery Habits framework.",
      "category": "strategy_model",
      "status": "stable"
    },
    "semantics": {
      "purpose": "Strategy-phase tool for visualizing and managing product discovery. Helps teams connect business outcomes to customer opportunities, potential solutions, and assumption tests. Enables continuous discovery by mapping the relationship between what the business needs (outcomes) and what customers need (opportunities).",
      "requires_comparable_items": false,
      "notes_on_comparability": "This is a visual mapping tool, not a ranking or scoring tool. Items (nodes) are organized hierarchically rather than compared directly."
    },
    "inputs": {
      "required_item_fields": [
        "id",
        "type",
        "title",
        "parentId"
      ],
      "required_item_attributes": [
        {
          "key": "type",
          "type": "categorical",
          "description": "Node type: outcome, opportunity, solution, or experiment",
          "constraints": {
            "allowed_values": [
              "outcome",
              "opportunity",
              "solution",
              "experiment"
            ]
          }
        },
        {
          "key": "title",
          "type": "string",
          "description": "The name/title of the node"
        },
        {
          "key": "parentId",
          "type": "string",
          "description": "ID of the parent node. Null for root nodes (outcomes)."
        }
      ],
      "optional_item_attributes": [
        {
          "key": "description",
          "type": "string",
          "description": "Detailed description of the node"
        },
        {
          "key": "source",
          "type": "categorical",
          "description": "Source of opportunity (for opportunity nodes)",
          "constraints": {
            "allowed_values": [
              "customer_interview",
              "support_ticket",
              "sales_conversation",
              "analytics",
              "survey",
              "user_research",
              "assumption"
            ]
          }
        },
        {
          "key": "customerQuote",
          "type": "string",
          "description": "Direct quote from customer (for opportunity nodes)"
        },
        {
          "key": "validationStatus",
          "type": "categorical",
          "description": "Validation status (for solution nodes)",
          "constraints": {
            "allowed_values": [
              "not_started",
              "in_progress",
              "validated",
              "invalidated",
              "needs_more_data"
            ]
          }
        },
        {
          "key": "experimentType",
          "type": "categorical",
          "description": "Type of experiment (for experiment nodes)",
          "constraints": {
            "allowed_values": [
              "ab_test",
              "prototype_test",
              "fake_door",
              "concierge",
              "wizard_of_oz",
              "landing_page",
              "user_interview",
              "survey",
              "mvp"
            ]
          }
        },
        {
          "key": "isTargeted",
          "type": "boolean",
          "description": "Whether this opportunity is the current focus (for opportunity nodes)"
        }
      ],
      "criteria_set_contract": {
        "allowed_criteria_ids": null,
        "requires_explicit_criteria_set": false,
        "notes": "This tool does not use scoring criteria. It is a visual mapping tool."
      },
      "lens_support": {
        "requires_lens": false,
        "notes": "Tree structure is inherently filtered by the parent-child relationships."
      },
      "tool_config": {
        "fields": [
          {
            "name": "show_collapsed",
            "type": "boolean",
            "default": false,
            "description": "Whether to start with nodes collapsed"
          },
          {
            "name": "highlight_target",
            "type": "boolean",
            "default": true,
            "description": "Whether to visually highlight the target opportunity"
          }
        ],
        "supports_defaults": true
      }
    },
    "algorithm": {
      "score_model_type": "tree_structure",
      "formula_description": "N/A - This is a hierarchical mapping tool, not a scoring model.",
      "formula_variables": {},
      "scaling_and_normalization": {
        "normalizes_inputs": "none",
        "normalizes_outputs": "none"
      },
      "handles_missing_values": {
        "strategy": "allow_partial",
        "rules": [
          "Nodes can be created with minimal information (just title) and enriched later",
          "Optional fields do not block node creation or tree rendering"
        ]
      },
      "tie_breaking": {
        "primary": "N/A - not a ranking tool",
        "secondary": "N/A",
        "tertiary": "N/A"
      },
      "determinism": true,
      "notes": "Tree structure is deterministic based on parent-child relationships. No randomization or scoring involved."
    },
    "outputs": {
      "score_set_schema": {
        "per_item_fields": [
          {
            "name": "node_id",
            "type": "string"
          },
          {
            "name": "node_type",
            "type": "string",
            "description": "outcome, opportunity, solution, or experiment"
          },
          {
            "name": "depth",
            "type": "integer",
            "description": "Depth level in the tree (0 = root)"
          },
          {
            "name": "children_count",
            "type": "integer",
            "description": "Number of direct children"
          }
        ],
        "annotations": [
          {
            "name": "tool_name",
            "type": "string",
            "value": "OPPORTUNITY_SOLUTION_TREE"
          },
          {
            "name": "tree_stats",
            "type": "object",
            "description": "Aggregate statistics about the tree"
          }
        ]
      },
      "tool_run_metadata": {
        "records_raw_inputs": true,
        "raw_inputs_schema": {
          "nodes": "array of OSTNode objects",
          "metadata": "tree-level metadata"
        },
        "additional_metadata": [
          "total_outcomes",
          "total_opportunities",
          "total_solutions",
          "total_experiments",
          "validated_solutions_count",
          "target_opportunity_id"
        ]
      }
    },
    "interaction_model": {
      "ui_pattern": "tree_visualization",
      "supports_inline_edit": true,
      "supports_batch_edit": false,
      "step_flow": [
        "create_outcome",
        "add_opportunities_from_interviews",
        "select_target_opportunity",
        "brainstorm_solutions",
        "define_experiments",
        "run_experiments_and_record_learnings",
        "iterate"
      ],
      "validation_behavior": {
        "on_invalid_item": "show_warning_and_allow_edit",
        "on_any_error": "show_errors"
      }
    },
    "constraints_and_assumptions": {
      "max_items_tested": 500,
      "performance_notes": "Tree rendering scales linearly with node count. Deep nesting (>10 levels) may affect visual clarity.",
      "assumes_attribute_types": "Node types must be one of the four defined types. Parent-child relationships must be valid (no cycles, orphaned non-root nodes).",
      "platform_dependencies": [
        "React state management",
        "Browser localStorage for persistence"
      ]
    },
    "toolRequirements": {
      "prerequisites": [],
      "requiredCriteria": [],
      "poolRequirements": {
        "minItems": 1,
        "requireLocked": false
      }
    },
    "test_vectors": [
      {
        "name": "simple_tree_structure",
        "items": [
          {
            "id": "outcome-1",
            "type": "outcome",
            "title": "Increase user engagement by 20%",
            "parentId": null
          },
          {
            "id": "opp-1",
            "type": "opportunity",
            "title": "Users struggle to find relevant content",
            "parentId": "outcome-1",
            "attributes": {
              "source": "customer_interview",
              "isTargeted": true
            }
          },
          {
            "id": "sol-1",
            "type": "solution",
            "title": "AI-powered recommendations",
            "parentId": "opp-1",
            "attributes": {
              "validationStatus": "in_progress"
            }
          },
          {
            "id": "exp-1",
            "type": "experiment",
            "title": "Fake door test",
            "parentId": "sol-1",
            "attributes": {
              "experimentType": "fake_door",
              "status": "completed"
            }
          }
        ],
        "tool_config": {
          "show_collapsed": false,
          "highlight_target": true
        },
        "expected_structure": {
          "total_nodes": 4,
          "max_depth": 3,
          "outcomes": 1,
          "opportunities": 1,
          "solutions": 1,
          "experiments": 1
        },
        "notes": "Basic tree structure with one node of each type in a linear hierarchy."
      }
    ],
    "references": {
      "methodology": "Continuous Discovery Habits by Teresa Torres",
      "url": "https://www.producttalk.org/opportunity-solution-tree/",
      "key_concepts": [
        "Product outcomes vs business outcomes",
        "Opportunities from customer interviews",
        "Target opportunity focus",
        "Compare and contrast solutions",
        "Assumption testing"
      ]
    }
  },
  {
    "tool_spec_version": "1.0",
    "identity": {
      "tool_name": "Pairwise Ranking",
      "short_label": "Paired Comparison",
      "description": "Prioritizes items by comparing every possible pair and forcing binary choices. Uses simple win/loss counting where each win earns +1 point. Based on the Paired Comparison method popularized by Richard Bolles and refined in Managing Priorities by Harry Max.",
      "category": "comparison_model",
      "status": "stable"
    },
    "semantics": {
      "purpose": "Arrange-phase prioritization for subjective, qualitative, or emotionally weighted items where criteria are unclear or inconsistent. Transforms complex prioritization into a sequence of simple binary choices.",
      "requires_comparable_items": true,
      "notes_on_comparability": "Items must be comparable in the context of the decision being made. The tool forces a choice between each pair - no ties allowed within individual comparisons. Items are compared on overall preference/importance rather than specific criteria."
    },
    "inputs": {
      "required_item_fields": [
        "id",
        "label"
      ],
      "required_item_attributes": [],
      "optional_item_attributes": [
        {
          "key": "description",
          "type": "string",
          "description": "Optional description or context for the item (not used in scoring, but may be displayed during comparisons)"
        }
      ],
      "criteria_set_contract": {
        "allowed_criteria_ids": [],
        "requires_explicit_criteria_set": false,
        "notes": "This tool does not use explicit criteria. Comparisons are made on overall preference/importance. The user's judgment during each pairwise comparison implicitly defines the evaluation criteria."
      },
      "lens_support": {
        "requires_lens": false,
        "notes": "If a lens is provided, the tool only compares items within that lens. All pairwise comparisons are generated from the filtered item set."
      },
      "tool_config": {
        "fields": [
          {
            "name": "enable_tie_breaking",
            "type": "boolean",
            "default": true,
            "description": "Whether to offer tie-breaking rounds when items have equal win counts. If false, ties are preserved in final ranking."
          },
          {
            "name": "max_items",
            "type": "integer",
            "default": 15,
            "description": "Maximum recommended number of items. Tool will warn at 12 items and block at 15+ items due to combinatorial explosion of comparisons (n*(n-1)/2 comparisons required)."
          },
          {
            "name": "min_items",
            "type": "integer",
            "default": 2,
            "description": "Minimum number of items required. Must be at least 2 to generate any comparisons."
          }
        ],
        "supports_defaults": true
      }
    },
    "algorithm": {
      "score_model_type": "comparison_based",
      "formula_description": "Each item starts with 0 wins. For each pairwise comparison, the selected winner receives +1 win, the loser receives +0. Final score = total wins. Rank = sorted by wins (descending), then alphabetically by label for stability.",
      "formula_variables": {
        "wins": "integer, >= 0, incremented by 1 for each comparison won",
        "losses": "integer, >= 0, incremented by 1 for each comparison lost",
        "comparisons": "integer, >= 0, total number of comparisons the item participated in"
      },
      "scaling_and_normalization": {
        "normalizes_confidence": null,
        "normalizes_other_fields": "none - raw win counts are used directly as scores"
      },
      "handles_missing_values": {
        "strategy": "not_applicable",
        "rules": [
          "This tool does not use attribute-based scoring, so missing values are not applicable. All items must have id and label. If an item is missing required fields, it is excluded from the comparison set before generation begins."
        ]
      },
      "tie_breaking": {
        "primary": "higher win count wins",
        "secondary": "alphabetical by item.label (case-sensitive string comparison)",
        "tertiary": "stable by original import order (item.id generation order)",
        "optional_tie_breaker_round": {
          "enabled": true,
          "description": "When items have equal win counts, user can optionally run a tie-breaking round. Only the tied items are compared pairwise. Tie-breaker wins are added to original wins to break the tie.",
          "algorithm": "Same pairwise comparison process, but only among tied items. Tie-breaker wins are added to original win count to determine final ranking."
        }
      },
      "determinism": true,
      "comparison_generation": {
        "method": "all_unique_pairs",
        "formula": "For n items, generates n*(n-1)/2 unique unordered pairs using nested loops: for i in [0..n-1], for j in [i+1..n-1], create pair (items[i].id, items[j].id)",
        "order": "lexicographic by item index (stable, deterministic)"
      },
      "scoring_process": {
        "initialization": "All items start with wins=0, losses=0, comparisons=0",
        "comparison_loop": "For each pair (A, B): user selects winner → winner.wins += 1, loser.losses += 1, both comparisons += 1",
        "finalization": "Sort items by wins (descending), then by label (ascending) for stability"
      }
    },
    "outputs": {
      "score_set_schema": {
        "per_item_fields": [
          {
            "name": "item_id",
            "type": "string",
            "description": "The unique identifier of the item"
          },
          {
            "name": "score",
            "type": "integer",
            "description": "Total number of wins (comparisons won). This is the primary ranking metric."
          },
          {
            "name": "rank",
            "type": "integer",
            "description": "1 = highest priority (most wins). Ties are possible if tie-breaking is disabled or skipped."
          },
          {
            "name": "wins",
            "type": "integer",
            "description": "Number of pairwise comparisons won (same as score)"
          },
          {
            "name": "losses",
            "type": "integer",
            "description": "Number of pairwise comparisons lost"
          },
          {
            "name": "comparisons",
            "type": "integer",
            "description": "Total number of comparisons the item participated in (wins + losses)"
          },
          {
            "name": "is_tied",
            "type": "boolean",
            "description": "Whether this item is part of a tie group (has same score as other items)"
          }
        ],
        "annotations": [
          {
            "name": "tool_name",
            "type": "string",
            "value": "Pairwise Ranking"
          },
          {
            "name": "total_comparisons",
            "type": "integer",
            "description": "Total number of pairwise comparisons completed (n*(n-1)/2)"
          },
          {
            "name": "tie_breaking_used",
            "type": "boolean",
            "description": "Whether tie-breaking rounds were executed"
          }
        ]
      },
      "tool_run_metadata": {
        "records_raw_inputs": true,
        "raw_inputs_schema": {
          "matchup_history": {
            "type": "array",
            "items": {
              "pair": [
                "string",
                "string"
              ],
              "winner": "string",
              "timestamp": "integer"
            },
            "description": "Complete history of all pairwise comparisons made, in order"
          },
          "tie_breaker_history": {
            "type": "array",
            "items": {
              "tied_score": "integer",
              "tied_item_ids": [
                "string"
              ],
              "matchup_history": "array"
            },
            "description": "History of tie-breaking rounds, if any were executed"
          }
        }
      }
    },
    "interaction_model": {
      "ui_pattern": "sequential_pairwise_comparison",
      "supports_inline_edit": false,
      "supports_batch_edit": false,
      "step_flow": [
        "input_items",
        "review_item_list",
        "sequential_pairwise_comparisons",
        "optional_tie_breaking",
        "review_results"
      ],
      "validation_behavior": {
        "on_invalid_item": "exclude_from_comparison_set_before_start",
        "on_any_error": "prevent_start_until_valid"
      },
      "comparison_interface": {
        "presentation": "two_equal_weight_buttons",
        "prompt": "Which is more important right now?",
        "user_actions": [
          "select_item_1",
          "select_item_2",
          "undo_last_comparison",
          "pause_and_review_partial_results"
        ],
        "keyboard_shortcuts": {
          "select_first": [
            "1",
            "ArrowLeft"
          ],
          "select_second": [
            "2",
            "ArrowRight"
          ],
          "undo": [
            "Backspace"
          ]
        },
        "progress_indication": "Comparison X of Y (Z% complete)",
        "no_ties_allowed": true,
        "notes": "Each comparison must result in exactly one winner. The tool does not allow 'tie' or 'equal' selections within individual comparisons."
      },
      "input_methods": {
        "supported_formats": [
          "free_text_newline_separated",
          "json_array",
          "comma_separated",
          "single_item_entry"
        ],
        "auto_detection": true,
        "duplicate_handling": "case_insensitive_deduplication_preserving_order"
      }
    },
    "constraints_and_assumptions": {
      "max_items_tested": 15,
      "max_items_recommended": 12,
      "warning_threshold": 12,
      "performance_notes": "O(n²) comparisons required. For n items, generates n*(n-1)/2 comparisons. Example: 10 items = 45 comparisons, 15 items = 105 comparisons. Performance is linear in number of comparisons, but user time grows quadratically.",
      "assumes_attribute_types": "Items only need id and label. No attribute parsing required.",
      "assumes_user_consistency": "Tool assumes user makes consistent choices, but does not enforce transitivity. If A > B and B > C, user could still choose C > A in a later comparison.",
      "computational_complexity": {
        "comparison_generation": "O(n²) time, O(n²) space",
        "scoring": "O(n²) time (one operation per comparison)",
        "ranking": "O(n log n) time (sorting)"
      }
    },
    "toolRequirements": {
      "prerequisites": [
        "POOL_NONEMPTY"
      ],
      "requiredCriteria": [],
      "poolRequirements": {
        "minItems": 2,
        "requireLocked": true
      }
    },
    "test_vectors": [
      {
        "name": "simple_three_item_example",
        "items": [
          {
            "id": "item-0",
            "label": "Pancakes"
          },
          {
            "id": "item-1",
            "label": "Spaghetti"
          },
          {
            "id": "item-2",
            "label": "Sushi"
          }
        ],
        "tool_config": {
          "enable_tie_breaking": true,
          "max_items": 15,
          "min_items": 2
        },
        "matchup_history": [
          {
            "pair": [
              "item-0",
              "item-1"
            ],
            "winner": "item-0",
            "timestamp": 1000
          },
          {
            "pair": [
              "item-0",
              "item-2"
            ],
            "winner": "item-0",
            "timestamp": 2000
          },
          {
            "pair": [
              "item-1",
              "item-2"
            ],
            "winner": "item-1",
            "timestamp": 3000
          }
        ],
        "expected_scores": [
          {
            "item_id": "item-0",
            "score": 2,
            "rank": 1,
            "wins": 2,
            "losses": 0,
            "comparisons": 2,
            "is_tied": false
          },
          {
            "item_id": "item-1",
            "score": 1,
            "rank": 2,
            "wins": 1,
            "losses": 1,
            "comparisons": 2,
            "is_tied": false
          },
          {
            "item_id": "item-2",
            "score": 0,
            "rank": 3,
            "wins": 0,
            "losses": 2,
            "comparisons": 2,
            "is_tied": false
          }
        ]
      },
      {
        "name": "tie_breaking_example",
        "items": [
          {
            "id": "item-0",
            "label": "Ravioli"
          },
          {
            "id": "item-1",
            "label": "Ice Cream"
          },
          {
            "id": "item-2",
            "label": "Choc Cake"
          }
        ],
        "tool_config": {
          "enable_tie_breaking": true,
          "max_items": 15,
          "min_items": 2
        },
        "matchup_history": [
          {
            "pair": [
              "item-0",
              "item-1"
            ],
            "winner": "item-0",
            "timestamp": 1000
          },
          {
            "pair": [
              "item-0",
              "item-2"
            ],
            "winner": "item-2",
            "timestamp": 2000
          },
          {
            "pair": [
              "item-1",
              "item-2"
            ],
            "winner": "item-1",
            "timestamp": 3000
          }
        ],
        "tie_breaker_history": [
          {
            "tied_score": 1,
            "tied_item_ids": [
              "item-0",
              "item-1",
              "item-2"
            ],
            "matchup_history": [
              {
                "pair": [
                  "item-0",
                  "item-1"
                ],
                "winner": "item-0",
                "timestamp": 4000
              },
              {
                "pair": [
                  "item-0",
                  "item-2"
                ],
                "winner": "item-0",
                "timestamp": 5000
              },
              {
                "pair": [
                  "item-1",
                  "item-2"
                ],
                "winner": "item-1",
                "timestamp": 6000
              }
            ]
          }
        ],
        "expected_scores": [
          {
            "item_id": "item-0",
            "score": 3,
            "rank": 1,
            "wins": 3,
            "losses": 0,
            "comparisons": 3,
            "is_tied": false
          },
          {
            "item_id": "item-1",
            "score": 2,
            "rank": 2,
            "wins": 2,
            "losses": 1,
            "comparisons": 3,
            "is_tied": false
          },
          {
            "item_id": "item-2",
            "score": 1,
            "rank": 3,
            "wins": 1,
            "losses": 2,
            "comparisons": 3,
            "is_tied": false
          }
        ],
        "notes": "All three items tied with 1 win each. Tie-breaker round: item-0 wins 2, item-1 wins 1, item-2 wins 0. Final scores = original (1) + tie-breaker wins, breaking the tie."
      },
      {
        "name": "four_item_workflow_example",
        "items": [
          {
            "id": "item-0",
            "label": "Work from home"
          },
          {
            "id": "item-1",
            "label": "Remote office"
          },
          {
            "id": "item-2",
            "label": "Hybrid"
          },
          {
            "id": "item-3",
            "label": "Corporate HQ"
          }
        ],
        "tool_config": {
          "enable_tie_breaking": false,
          "max_items": 15,
          "min_items": 2
        },
        "matchup_history": [
          {
            "pair": [
              "item-0",
              "item-1"
            ],
            "winner": "item-0",
            "timestamp": 1000
          },
          {
            "pair": [
              "item-0",
              "item-2"
            ],
            "winner": "item-2",
            "timestamp": 2000
          },
          {
            "pair": [
              "item-0",
              "item-3"
            ],
            "winner": "item-0",
            "timestamp": 3000
          },
          {
            "pair": [
              "item-1",
              "item-2"
            ],
            "winner": "item-2",
            "timestamp": 4000
          },
          {
            "pair": [
              "item-1",
              "item-3"
            ],
            "winner": "item-3",
            "timestamp": 5000
          },
          {
            "pair": [
              "item-2",
              "item-3"
            ],
            "winner": "item-2",
            "timestamp": 6000
          }
        ],
        "expected_scores": [
          {
            "item_id": "item-2",
            "score": 3,
            "rank": 1,
            "wins": 3,
            "losses": 0,
            "comparisons": 3,
            "is_tied": false
          },
          {
            "item_id": "item-0",
            "score": 2,
            "rank": 2,
            "wins": 2,
            "losses": 1,
            "comparisons": 3,
            "is_tied": false
          },
          {
            "item_id": "item-3",
            "score": 1,
            "rank": 3,
            "wins": 1,
            "losses": 2,
            "comparisons": 3,
            "is_tied": false
          },
          {
            "item_id": "item-1",
            "score": 0,
            "rank": 4,
            "wins": 0,
            "losses": 3,
            "comparisons": 3,
            "is_tied": false
          }
        ],
        "notes": "Example from Managing Priorities: 4 items = 6 comparisons. Final ranking: Hybrid (3), WFH (2), HQ (1), Remote (0)."
      }
    ]
  },
  {
    "tool_spec_version": "1.0",
    "identity": {
      "tool_name": "Pairwise Ranking",
      "short_label": "Paired Comparison",
      "description": "Prioritizes items by comparing every possible pair and forcing binary choices. Uses simple win/loss counting where each win earns +1 point. Based on the Paired Comparison method popularized by Richard Bolles and refined in Managing Priorities by Harry Max.",
      "category": "comparison_model",
      "status": "stable"
    },
    "semantics": {
      "purpose": "Arrange-phase prioritization for subjective, qualitative, or emotionally weighted items where criteria are unclear or inconsistent. Transforms complex prioritization into a sequence of simple binary choices.",
      "requires_comparable_items": true,
      "notes_on_comparability": "Items must be comparable in the context of the decision being made. The tool forces a choice between each pair - no ties allowed within individual comparisons. Items are compared on overall preference/importance rather than specific criteria."
    },
    "inputs": {
      "required_item_fields": [
        "id",
        "label"
      ],
      "required_item_attributes": [],
      "optional_item_attributes": [
        {
          "key": "description",
          "type": "string",
          "description": "Optional description or context for the item (not used in scoring, but may be displayed during comparisons)"
        }
      ],
      "criteria_set_contract": {
        "allowed_criteria_ids": [],
        "requires_explicit_criteria_set": false,
        "notes": "This tool does not use explicit criteria. Comparisons are made on overall preference/importance. The user's judgment during each pairwise comparison implicitly defines the evaluation criteria."
      },
      "lens_support": {
        "requires_lens": false,
        "notes": "If a lens is provided, the tool only compares items within that lens. All pairwise comparisons are generated from the filtered item set."
      },
      "tool_config": {
        "fields": [
          {
            "name": "enable_tie_breaking",
            "type": "boolean",
            "default": true,
            "description": "Whether to offer tie-breaking rounds when items have equal win counts. If false, ties are preserved in final ranking."
          },
          {
            "name": "max_items",
            "type": "integer",
            "default": 15,
            "description": "Maximum recommended number of items. Tool will warn at 12 items and block at 15+ items due to combinatorial explosion of comparisons (n*(n-1)/2 comparisons required)."
          },
          {
            "name": "min_items",
            "type": "integer",
            "default": 2,
            "description": "Minimum number of items required. Must be at least 2 to generate any comparisons."
          }
        ],
        "supports_defaults": true
      }
    },
    "algorithm": {
      "score_model_type": "comparison_based",
      "formula_description": "Each item starts with 0 wins. For each pairwise comparison, the selected winner receives +1 win, the loser receives +0. Final score = total wins. Rank = sorted by wins (descending), then alphabetically by label for stability.",
      "formula_variables": {
        "wins": "integer, >= 0, incremented by 1 for each comparison won",
        "losses": "integer, >= 0, incremented by 1 for each comparison lost",
        "comparisons": "integer, >= 0, total number of comparisons the item participated in"
      },
      "scaling_and_normalization": {
        "normalizes_confidence": null,
        "normalizes_other_fields": "none - raw win counts are used directly as scores"
      },
      "handles_missing_values": {
        "strategy": "not_applicable",
        "rules": [
          "This tool does not use attribute-based scoring, so missing values are not applicable. All items must have id and label. If an item is missing required fields, it is excluded from the comparison set before generation begins."
        ]
      },
      "tie_breaking": {
        "primary": "higher win count wins",
        "secondary": "alphabetical by item.label (case-sensitive string comparison)",
        "tertiary": "stable by original import order (item.id generation order)",
        "optional_tie_breaker_round": {
          "enabled": true,
          "description": "When items have equal win counts, user can optionally run a tie-breaking round. Only the tied items are compared pairwise. Tie-breaker wins are added to original wins to break the tie.",
          "algorithm": "Same pairwise comparison process, but only among tied items. Tie-breaker wins are added to original win count to determine final ranking."
        }
      },
      "determinism": true,
      "comparison_generation": {
        "method": "all_unique_pairs",
        "formula": "For n items, generates n*(n-1)/2 unique unordered pairs using nested loops: for i in [0..n-1], for j in [i+1..n-1], create pair (items[i].id, items[j].id)",
        "order": "lexicographic by item index (stable, deterministic)"
      },
      "scoring_process": {
        "initialization": "All items start with wins=0, losses=0, comparisons=0",
        "comparison_loop": "For each pair (A, B): user selects winner → winner.wins += 1, loser.losses += 1, both comparisons += 1",
        "finalization": "Sort items by wins (descending), then by label (ascending) for stability"
      }
    },
    "outputs": {
      "score_set_schema": {
        "per_item_fields": [
          {
            "name": "item_id",
            "type": "string",
            "description": "The unique identifier of the item"
          },
          {
            "name": "score",
            "type": "integer",
            "description": "Total number of wins (comparisons won). This is the primary ranking metric."
          },
          {
            "name": "rank",
            "type": "integer",
            "description": "1 = highest priority (most wins). Ties are possible if tie-breaking is disabled or skipped."
          },
          {
            "name": "wins",
            "type": "integer",
            "description": "Number of pairwise comparisons won (same as score)"
          },
          {
            "name": "losses",
            "type": "integer",
            "description": "Number of pairwise comparisons lost"
          },
          {
            "name": "comparisons",
            "type": "integer",
            "description": "Total number of comparisons the item participated in (wins + losses)"
          },
          {
            "name": "is_tied",
            "type": "boolean",
            "description": "Whether this item is part of a tie group (has same score as other items)"
          }
        ],
        "annotations": [
          {
            "name": "tool_name",
            "type": "string",
            "value": "Pairwise Ranking"
          },
          {
            "name": "total_comparisons",
            "type": "integer",
            "description": "Total number of pairwise comparisons completed (n*(n-1)/2)"
          },
          {
            "name": "tie_breaking_used",
            "type": "boolean",
            "description": "Whether tie-breaking rounds were executed"
          }
        ]
      },
      "tool_run_metadata": {
        "records_raw_inputs": true,
        "raw_inputs_schema": {
          "matchup_history": {
            "type": "array",
            "items": {
              "pair": [
                "string",
                "string"
              ],
              "winner": "string",
              "timestamp": "integer"
            },
            "description": "Complete history of all pairwise comparisons made, in order"
          },
          "tie_breaker_history": {
            "type": "array",
            "items": {
              "tied_score": "integer",
              "tied_item_ids": [
                "string"
              ],
              "matchup_history": "array"
            },
            "description": "History of tie-breaking rounds, if any were executed"
          }
        }
      }
    },
    "interaction_model": {
      "ui_pattern": "sequential_pairwise_comparison",
      "supports_inline_edit": false,
      "supports_batch_edit": false,
      "step_flow": [
        "input_items",
        "review_item_list",
        "sequential_pairwise_comparisons",
        "optional_tie_breaking",
        "review_results"
      ],
      "validation_behavior": {
        "on_invalid_item": "exclude_from_comparison_set_before_start",
        "on_any_error": "prevent_start_until_valid"
      },
      "comparison_interface": {
        "presentation": "two_equal_weight_buttons",
        "prompt": "Which is more important right now?",
        "user_actions": [
          "select_item_1",
          "select_item_2",
          "undo_last_comparison",
          "pause_and_review_partial_results"
        ],
        "keyboard_shortcuts": {
          "select_first": [
            "1",
            "ArrowLeft"
          ],
          "select_second": [
            "2",
            "ArrowRight"
          ],
          "undo": [
            "Backspace"
          ]
        },
        "progress_indication": "Comparison X of Y (Z% complete)",
        "no_ties_allowed": true,
        "notes": "Each comparison must result in exactly one winner. The tool does not allow 'tie' or 'equal' selections within individual comparisons."
      },
      "input_methods": {
        "supported_formats": [
          "free_text_newline_separated",
          "json_array",
          "comma_separated",
          "single_item_entry"
        ],
        "auto_detection": true,
        "duplicate_handling": "case_insensitive_deduplication_preserving_order"
      }
    },
    "constraints_and_assumptions": {
      "max_items_tested": 15,
      "max_items_recommended": 12,
      "warning_threshold": 12,
      "performance_notes": "O(n²) comparisons required. For n items, generates n*(n-1)/2 comparisons. Example: 10 items = 45 comparisons, 15 items = 105 comparisons. Performance is linear in number of comparisons, but user time grows quadratically.",
      "assumes_attribute_types": "Items only need id and label. No attribute parsing required.",
      "assumes_user_consistency": "Tool assumes user makes consistent choices, but does not enforce transitivity. If A > B and B > C, user could still choose C > A in a later comparison.",
      "computational_complexity": {
        "comparison_generation": "O(n²) time, O(n²) space",
        "scoring": "O(n²) time (one operation per comparison)",
        "ranking": "O(n log n) time (sorting)"
      }
    },
    "test_vectors": [
      {
        "name": "simple_three_item_example",
        "items": [
          {
            "id": "item-0",
            "label": "Pancakes"
          },
          {
            "id": "item-1",
            "label": "Spaghetti"
          },
          {
            "id": "item-2",
            "label": "Sushi"
          }
        ],
        "tool_config": {
          "enable_tie_breaking": true,
          "max_items": 15,
          "min_items": 2
        },
        "matchup_history": [
          {
            "pair": [
              "item-0",
              "item-1"
            ],
            "winner": "item-0",
            "timestamp": 1000
          },
          {
            "pair": [
              "item-0",
              "item-2"
            ],
            "winner": "item-0",
            "timestamp": 2000
          },
          {
            "pair": [
              "item-1",
              "item-2"
            ],
            "winner": "item-1",
            "timestamp": 3000
          }
        ],
        "expected_scores": [
          {
            "item_id": "item-0",
            "score": 2,
            "rank": 1,
            "wins": 2,
            "losses": 0,
            "comparisons": 2,
            "is_tied": false
          },
          {
            "item_id": "item-1",
            "score": 1,
            "rank": 2,
            "wins": 1,
            "losses": 1,
            "comparisons": 2,
            "is_tied": false
          },
          {
            "item_id": "item-2",
            "score": 0,
            "rank": 3,
            "wins": 0,
            "losses": 2,
            "comparisons": 2,
            "is_tied": false
          }
        ]
      },
      {
        "name": "tie_breaking_example",
        "items": [
          {
            "id": "item-0",
            "label": "Ravioli"
          },
          {
            "id": "item-1",
            "label": "Ice Cream"
          },
          {
            "id": "item-2",
            "label": "Choc Cake"
          }
        ],
        "tool_config": {
          "enable_tie_breaking": true,
          "max_items": 15,
          "min_items": 2
        },
        "matchup_history": [
          {
            "pair": [
              "item-0",
              "item-1"
            ],
            "winner": "item-0",
            "timestamp": 1000
          },
          {
            "pair": [
              "item-0",
              "item-2"
            ],
            "winner": "item-2",
            "timestamp": 2000
          },
          {
            "pair": [
              "item-1",
              "item-2"
            ],
            "winner": "item-1",
            "timestamp": 3000
          }
        ],
        "tie_breaker_history": [
          {
            "tied_score": 1,
            "tied_item_ids": [
              "item-0",
              "item-1",
              "item-2"
            ],
            "matchup_history": [
              {
                "pair": [
                  "item-0",
                  "item-1"
                ],
                "winner": "item-0",
                "timestamp": 4000
              },
              {
                "pair": [
                  "item-0",
                  "item-2"
                ],
                "winner": "item-0",
                "timestamp": 5000
              },
              {
                "pair": [
                  "item-1",
                  "item-2"
                ],
                "winner": "item-1",
                "timestamp": 6000
              }
            ]
          }
        ],
        "expected_scores": [
          {
            "item_id": "item-0",
            "score": 3,
            "rank": 1,
            "wins": 3,
            "losses": 0,
            "comparisons": 3,
            "is_tied": false
          },
          {
            "item_id": "item-1",
            "score": 2,
            "rank": 2,
            "wins": 2,
            "losses": 1,
            "comparisons": 3,
            "is_tied": false
          },
          {
            "item_id": "item-2",
            "score": 1,
            "rank": 3,
            "wins": 1,
            "losses": 2,
            "comparisons": 3,
            "is_tied": false
          }
        ],
        "notes": "All three items tied with 1 win each. Tie-breaker round: item-0 wins 2, item-1 wins 1, item-2 wins 0. Final scores = original (1) + tie-breaker wins, breaking the tie."
      },
      {
        "name": "four_item_workflow_example",
        "items": [
          {
            "id": "item-0",
            "label": "Work from home"
          },
          {
            "id": "item-1",
            "label": "Remote office"
          },
          {
            "id": "item-2",
            "label": "Hybrid"
          },
          {
            "id": "item-3",
            "label": "Corporate HQ"
          }
        ],
        "tool_config": {
          "enable_tie_breaking": false,
          "max_items": 15,
          "min_items": 2
        },
        "matchup_history": [
          {
            "pair": [
              "item-0",
              "item-1"
            ],
            "winner": "item-0",
            "timestamp": 1000
          },
          {
            "pair": [
              "item-0",
              "item-2"
            ],
            "winner": "item-2",
            "timestamp": 2000
          },
          {
            "pair": [
              "item-0",
              "item-3"
            ],
            "winner": "item-0",
            "timestamp": 3000
          },
          {
            "pair": [
              "item-1",
              "item-2"
            ],
            "winner": "item-2",
            "timestamp": 4000
          },
          {
            "pair": [
              "item-1",
              "item-3"
            ],
            "winner": "item-3",
            "timestamp": 5000
          },
          {
            "pair": [
              "item-2",
              "item-3"
            ],
            "winner": "item-2",
            "timestamp": 6000
          }
        ],
        "expected_scores": [
          {
            "item_id": "item-2",
            "score": 3,
            "rank": 1,
            "wins": 3,
            "losses": 0,
            "comparisons": 3,
            "is_tied": false
          },
          {
            "item_id": "item-0",
            "score": 2,
            "rank": 2,
            "wins": 2,
            "losses": 1,
            "comparisons": 3,
            "is_tied": false
          },
          {
            "item_id": "item-3",
            "score": 1,
            "rank": 3,
            "wins": 1,
            "losses": 2,
            "comparisons": 3,
            "is_tied": false
          },
          {
            "item_id": "item-1",
            "score": 0,
            "rank": 4,
            "wins": 0,
            "losses": 3,
            "comparisons": 3,
            "is_tied": false
          }
        ],
        "notes": "Example from Managing Priorities: 4 items = 6 comparisons. Final ranking: Hybrid (3), WFH (2), HQ (1), Remote (0)."
      }
    ]
  },
  {
    "tool_spec_version": "1.0",
    "identity": {
      "tool_name": "PARETO",
      "short_label": "Pareto Analysis (80/20 Rule)",
      "description": "Identifies the top ~20% of items that generate ~80% of the value. Apply recursively to find the most impactful priorities.",
      "category": "selection_model",
      "status": "stable"
    },
    "semantics": {
      "purpose": "Arrange-phase prioritization by identifying the critical few items that drive most value, based on contribution scores.",
      "requires_comparable_items": true,
      "notes_on_comparability": "Items must have contribution scores on a consistent scale to enable comparison and ranking."
    },
    "inputs": {
      "required_item_fields": [
        "id",
        "label"
      ],
      "required_item_attributes": [
        {
          "key": "contribution",
          "type": "numeric",
          "description": "Relative contribution to overall outcome. Use any consistent numeric scale (typically 1-100). Higher = more contribution."
        }
      ],
      "optional_item_attributes": [],
      "criteria_set_contract": {
        "allowed_criteria_ids": [
          "contribution"
        ],
        "requires_explicit_criteria_set": false,
        "notes": "The tool uses a single 'contribution' criterion. If a CriteriaSet is provided, it should contain one criterion named 'contribution'."
      },
      "lens_support": {
        "requires_lens": false,
        "notes": "If a lens is provided, the tool only analyzes items within that lens."
      },
      "tool_config": {
        "fields": [
          {
            "name": "pareto_threshold",
            "type": "numeric",
            "allowed_values": null,
            "default": 0.2,
            "description": "The share of value left to the useful many (default 0.2): the vital few are the fewest items that together carry the rest, 80% by default."
          },
          {
            "name": "allow_recursive",
            "type": "boolean",
            "allowed_values": null,
            "default": true,
            "description": "Whether to allow recursive Pareto analysis on the top 20%."
          }
        ],
        "supports_defaults": true
      }
    },
    "algorithm": {
      "score_model_type": "selection",
      "formula_description": "Sort items by contribution descending. The vital few are the fewest leading items whose cumulative contribution reaches (1 - pareto_threshold) of the total, 80% by default. Optionally apply recursively to the vital few.",
      "formula_variables": {
        "contribution": "numeric, any scale (typically 1-100)"
      },
      "scaling_and_normalization": {
        "normalizes_confidence": "N/A",
        "normalizes_other_fields": "Items are sorted by raw contribution score. No normalization required."
      },
      "handles_missing_values": {
        "strategy": "reject_item",
        "rules": [
          "If contribution is missing or null, the item is excluded from analysis and returned with an error reason."
        ]
      },
      "tie_breaking": {
        "primary": "higher contribution wins",
        "secondary": "alphabetical by item.label",
        "tertiary": "stable by original import order"
      },
      "determinism": true
    },
    "outputs": {
      "score_set_schema": {
        "per_item_fields": [
          {
            "name": "item_id",
            "type": "string"
          },
          {
            "name": "contribution",
            "type": "numeric",
            "description": "Original contribution score."
          },
          {
            "name": "is_top_20_percent",
            "type": "boolean",
            "description": "Whether this item is in the vital few: the fewest leading items that carry (1 - pareto_threshold) of the total value."
          },
          {
            "name": "rank",
            "type": "integer",
            "description": "Rank by contribution (1 = highest contribution)."
          }
        ],
        "annotations": [
          {
            "name": "tool_name",
            "type": "string",
            "value": "PARETO"
          },
          {
            "name": "pareto_threshold_used",
            "type": "numeric",
            "description": "The threshold fraction used (e.g., 0.2 for 20%)"
          },
          {
            "name": "top_n_count",
            "type": "integer",
            "description": "Number of items in the top group"
          }
        ]
      },
      "tool_run_metadata": {
        "records_raw_inputs": true,
        "raw_inputs_schema": {
          "contribution": "numeric"
        }
      }
    },
    "interaction_model": {
      "ui_pattern": "sequential_scoring_then_selection",
      "supports_inline_edit": true,
      "supports_batch_edit": false,
      "step_flow": [
        "input_items",
        "for_each_item_collect_contribution",
        "calculate_rankings",
        "identify_top_20_percent",
        "display_results_with_grouping",
        "optionally_apply_recursive_pareto"
      ],
      "validation_behavior": {
        "on_invalid_item": "prevent_progression_until_valid",
        "on_any_error": "show_error_message_and_allow_correction"
      }
    },
    "constraints_and_assumptions": {
      "max_items_tested": 1000,
      "performance_notes": "O(n log n) for sorting, O(n) for selection. Efficient for large item sets.",
      "assumes_attribute_types": "Contribution must be numeric. Scale consistency is assumed but not enforced."
    },
    "toolRequirements": {
      "prerequisites": [
        "CRITERIA_LOCKED",
        "SCALES_DEFINED",
        "POOL_NONEMPTY",
        "VALUES_SUFFICIENT"
      ],
      "requiredCriteria": [
        {
          "tag": "term:contribution",
          "criterionTypes": [
            "numeric"
          ],
          "minCount": 1
        }
      ],
      "poolRequirements": {
        "minItems": 5,
        "requireLocked": false
      }
    },
    "test_vectors": [
      {
        "name": "simple_five_item_example",
        "items": [
          {
            "id": "A",
            "label": "Item A",
            "attributes": {
              "contribution": 90
            }
          },
          {
            "id": "B",
            "label": "Item B",
            "attributes": {
              "contribution": 80
            }
          },
          {
            "id": "C",
            "label": "Item C",
            "attributes": {
              "contribution": 70
            }
          },
          {
            "id": "D",
            "label": "Item D",
            "attributes": {
              "contribution": 20
            }
          },
          {
            "id": "E",
            "label": "Item E",
            "attributes": {
              "contribution": 10
            }
          }
        ],
        "tool_config": {
          "pareto_threshold": 0.2
        },
        "expected_results": [
          {
            "item_id": "A",
            "contribution": 90,
            "is_top_20_percent": true,
            "rank": 1
          },
          {
            "item_id": "B",
            "contribution": 80,
            "is_top_20_percent": true,
            "rank": 2
          },
          {
            "item_id": "C",
            "contribution": 70,
            "is_top_20_percent": false,
            "rank": 3
          },
          {
            "item_id": "D",
            "contribution": 20,
            "is_top_20_percent": false,
            "rank": 4
          },
          {
            "item_id": "E",
            "contribution": 10,
            "is_top_20_percent": false,
            "rank": 5
          }
        ]
      }
    ]
  },
  {
    "tool_spec_version": "1.0",
    "identity": {
      "tool_name": "PARETO",
      "short_label": "Pareto Analysis (80/20 Rule)",
      "description": "Identifies the top ~20% of items that generate ~80% of the value. Apply recursively to find the most impactful priorities.",
      "category": "selection_model",
      "status": "stable"
    },
    "semantics": {
      "purpose": "Arrange-phase prioritization by identifying the critical few items that drive most value, based on contribution scores.",
      "requires_comparable_items": true,
      "notes_on_comparability": "Items must have contribution scores on a consistent scale to enable comparison and ranking."
    },
    "inputs": {
      "required_item_fields": [
        "id",
        "label"
      ],
      "required_item_attributes": [
        {
          "key": "contribution",
          "type": "numeric",
          "description": "Relative contribution to overall outcome. Use any consistent numeric scale (typically 1-100). Higher = more contribution."
        }
      ],
      "optional_item_attributes": [],
      "criteria_set_contract": {
        "allowed_criteria_ids": [
          "contribution"
        ],
        "requires_explicit_criteria_set": false,
        "notes": "The tool uses a single 'contribution' criterion. If a CriteriaSet is provided, it should contain one criterion named 'contribution'."
      },
      "lens_support": {
        "requires_lens": false,
        "notes": "If a lens is provided, the tool only analyzes items within that lens."
      },
      "tool_config": {
        "fields": [
          {
            "name": "pareto_threshold",
            "type": "numeric",
            "allowed_values": null,
            "default": 0.2,
            "description": "Fraction of items to identify as top contributors (default 0.2 = 20%)."
          },
          {
            "name": "allow_recursive",
            "type": "boolean",
            "allowed_values": null,
            "default": true,
            "description": "Whether to allow recursive Pareto analysis on the top 20%."
          }
        ],
        "supports_defaults": true
      }
    },
    "algorithm": {
      "score_model_type": "selection",
      "formula_description": "Sort items by contribution descending. Select top N = ceil(total_items * pareto_threshold). Optionally apply recursively to top N.",
      "formula_variables": {
        "contribution": "numeric, any scale (typically 1-100)"
      },
      "scaling_and_normalization": {
        "normalizes_confidence": "N/A",
        "normalizes_other_fields": "Items are sorted by raw contribution score. No normalization required."
      },
      "handles_missing_values": {
        "strategy": "reject_item",
        "rules": [
          "If contribution is missing or null, the item is excluded from analysis and returned with an error reason."
        ]
      },
      "tie_breaking": {
        "primary": "higher contribution wins",
        "secondary": "alphabetical by item.label",
        "tertiary": "stable by original import order"
      },
      "determinism": true
    },
    "outputs": {
      "score_set_schema": {
        "per_item_fields": [
          {
            "name": "item_id",
            "type": "string"
          },
          {
            "name": "contribution",
            "type": "numeric",
            "description": "Original contribution score."
          },
          {
            "name": "is_top_20_percent",
            "type": "boolean",
            "description": "Whether this item is in the top 20% (or configured threshold) of contributors."
          },
          {
            "name": "rank",
            "type": "integer",
            "description": "Rank by contribution (1 = highest contribution)."
          }
        ],
        "annotations": [
          {
            "name": "tool_name",
            "type": "string",
            "value": "PARETO"
          },
          {
            "name": "pareto_threshold_used",
            "type": "numeric",
            "description": "The threshold fraction used (e.g., 0.2 for 20%)"
          },
          {
            "name": "top_n_count",
            "type": "integer",
            "description": "Number of items in the top group"
          }
        ]
      },
      "tool_run_metadata": {
        "records_raw_inputs": true,
        "raw_inputs_schema": {
          "contribution": "numeric"
        }
      }
    },
    "interaction_model": {
      "ui_pattern": "sequential_scoring_then_selection",
      "supports_inline_edit": true,
      "supports_batch_edit": false,
      "step_flow": [
        "input_items",
        "for_each_item_collect_contribution",
        "calculate_rankings",
        "identify_top_20_percent",
        "display_results_with_grouping",
        "optionally_apply_recursive_pareto"
      ],
      "validation_behavior": {
        "on_invalid_item": "prevent_progression_until_valid",
        "on_any_error": "show_error_message_and_allow_correction"
      }
    },
    "constraints_and_assumptions": {
      "max_items_tested": 1000,
      "performance_notes": "O(n log n) for sorting, O(n) for selection. Efficient for large item sets.",
      "assumes_attribute_types": "Contribution must be numeric. Scale consistency is assumed but not enforced."
    },
    "test_vectors": [
      {
        "name": "simple_five_item_example",
        "items": [
          {
            "id": "A",
            "label": "Item A",
            "attributes": {
              "contribution": 90
            }
          },
          {
            "id": "B",
            "label": "Item B",
            "attributes": {
              "contribution": 80
            }
          },
          {
            "id": "C",
            "label": "Item C",
            "attributes": {
              "contribution": 70
            }
          },
          {
            "id": "D",
            "label": "Item D",
            "attributes": {
              "contribution": 20
            }
          },
          {
            "id": "E",
            "label": "Item E",
            "attributes": {
              "contribution": 10
            }
          }
        ],
        "tool_config": {
          "pareto_threshold": 0.2
        },
        "expected_results": [
          {
            "item_id": "A",
            "contribution": 90,
            "is_top_20_percent": true,
            "rank": 1
          },
          {
            "item_id": "B",
            "contribution": 80,
            "is_top_20_percent": true,
            "rank": 2
          },
          {
            "item_id": "C",
            "contribution": 70,
            "is_top_20_percent": false,
            "rank": 3
          },
          {
            "item_id": "D",
            "contribution": 20,
            "is_top_20_percent": false,
            "rank": 4
          },
          {
            "item_id": "E",
            "contribution": 10,
            "is_top_20_percent": false,
            "rank": 5
          }
        ]
      }
    ]
  },
  {
    "tool_spec_version": "1.0",
    "identity": {
      "tool_name": "PRUNE_PRODUCT_TREE",
      "short_label": "Prune the Product Tree",
      "description": "Feature pruning exercise where items (branches) are rated on value, impact, and feasibility (1-5), then marked as active or pruned. Active branches are ranked by composite score. Based on Luke Hohmann's Innovation Games.",
      "category": "decision_framework",
      "status": "stable"
    },
    "semantics": {
      "purpose": "Arrange-phase portfolio thinning. Teams assess features or initiatives on three dimensions and decide which branches to keep vs. cut. Produces a scored, ranked list of surviving branches.",
      "requires_comparable_items": true,
      "notes_on_comparability": "Items represent features, initiatives, or capabilities at a comparable level of specificity. All items are scored on the same three 1-5 scales."
    },
    "inputs": {
      "required_item_fields": [
        "id",
        "label"
      ],
      "required_item_attributes": [],
      "optional_item_attributes": [],
      "criteria_set_contract": {
        "allowed_criteria_ids": [
          "value",
          "impact",
          "feasibility"
        ],
        "requires_explicit_criteria_set": false,
        "notes": "Three fixed dimensions are used: value (1-5), impact (1-5), feasibility (1-5). Scores are collected during the run via sliders. No pre-existing criteria set required."
      },
      "lens_support": {
        "requires_lens": false,
        "notes": "If a lens is provided, only items in the lens are imported as branches."
      },
      "tool_config": {
        "fields": [
          {
            "name": "default_score",
            "type": "numeric",
            "default": 3,
            "description": "Starting value for all three sliders (value, impact, feasibility) when an item is first added."
          }
        ],
        "supports_defaults": true
      }
    },
    "algorithm": {
      "score_model_type": "formula",
      "formula_description": "score = (value + impact + feasibility) / 3. Score range: 1.0 (minimum) to 5.0 (maximum). Only active (non-pruned) branches appear in results.",
      "formula_variables": {
        "value": "integer 1-5: strategic or customer value if completed",
        "impact": "integer 1-5: magnitude of positive outcome",
        "feasibility": "integer 1-5: likelihood of successful delivery"
      },
      "scaling_and_normalization": {
        "score_range": "1.0 (min) to 5.0 (max)",
        "normalizes_scores": false
      },
      "handles_missing_values": {
        "strategy": "use_defaults",
        "rules": [
          "Items without explicit ratings default to score 3 for each dimension (configurable via tool_config.default_score)."
        ]
      },
      "tie_breaking": {
        "primary": "higher score wins",
        "secondary": "original import/add order"
      },
      "determinism": true
    },
    "outputs": {
      "score_set_schema": {
        "per_item_fields": [
          {
            "name": "item_id",
            "type": "string"
          },
          {
            "name": "score",
            "type": "numeric",
            "description": "Composite score: (value + impact + feasibility) / 3."
          },
          {
            "name": "rank",
            "type": "integer",
            "description": "Rank among active branches only. 1 = highest score."
          },
          {
            "name": "category",
            "type": "string",
            "description": "active or pruned."
          }
        ],
        "annotations": [
          {
            "name": "tool_name",
            "type": "string",
            "value": "PruneProductTree"
          },
          {
            "name": "value",
            "type": "integer",
            "description": "Raw value rating 1-5."
          },
          {
            "name": "impact",
            "type": "integer",
            "description": "Raw impact rating 1-5."
          },
          {
            "name": "feasibility",
            "type": "integer",
            "description": "Raw feasibility rating 1-5."
          },
          {
            "name": "status",
            "type": "string",
            "description": "active or pruned."
          }
        ]
      },
      "tool_run_metadata": {
        "records_raw_inputs": true,
        "raw_inputs_schema": {
          "branches": "Array of {id, name, status, value, impact, feasibility} for all branches including pruned"
        },
        "additional_fields": {
          "total_branches": "integer",
          "active_count": "integer",
          "pruned_count": "integer"
        }
      }
    },
    "interaction_model": {
      "ui_pattern": "input_then_evaluate",
      "supports_inline_edit": true,
      "supports_batch_edit": false,
      "step_flow": [
        "add_branches",
        "start_pruning",
        "rate_each_branch_on_three_dimensions",
        "prune_low_value_branches",
        "review_active_vs_pruned",
        "save_results"
      ],
      "validation_behavior": {
        "on_invalid_item": "prevent_start_if_no_branches",
        "on_any_error": "show_error_and_allow_correction"
      }
    },
    "constraints_and_assumptions": {
      "max_items_tested": 100,
      "performance_notes": "O(n) rendering. Practical limit is ~30 branches for a focused pruning session.",
      "assumes_attribute_types": "value, impact, and feasibility are integer 1-5 collected via sliders.",
      "assumptions": [
        "Items represent discrete features or initiatives that can be independently assessed",
        "Pruning decisions are team judgments — the scores inform but do not automate the prune/keep decision",
        "Pruned branches are retained in tool_run_metadata for audit"
      ]
    },
    "toolRequirements": {
      "prerequisites": [
        "POOL_NONEMPTY"
      ],
      "requiredCriteria": [],
      "poolRequirements": {
        "minItems": 1,
        "requireLocked": false
      }
    },
    "test_vectors": [
      {
        "name": "basic_pruning",
        "description": "Three branches: two active, one pruned",
        "items": [
          {
            "id": "b-1",
            "label": "Mobile notifications",
            "value": 5,
            "impact": 4,
            "feasibility": 3,
            "status": "active"
          },
          {
            "id": "b-2",
            "label": "Dark mode",
            "value": 3,
            "impact": 2,
            "feasibility": 5,
            "status": "active"
          },
          {
            "id": "b-3",
            "label": "Export to CSV",
            "value": 1,
            "impact": 1,
            "feasibility": 2,
            "status": "pruned"
          }
        ],
        "expected_scores": [
          {
            "item_id": "b-1",
            "score": 4,
            "rank": 1,
            "category": "active",
            "calculation": "(5+4+3)/3 = 4.0"
          },
          {
            "item_id": "b-2",
            "score": 3.33,
            "rank": 2,
            "category": "active",
            "calculation": "(3+2+5)/3 = 3.33"
          }
        ],
        "notes": "Pruned branches excluded from scored output. Retained in tool_run_metadata."
      }
    ]
  },
  {
    "tool_spec_version": "1.0",
    "identity": {
      "tool_name": "RICE",
      "short_label": "RICE Scoring",
      "description": "Ranks items by Reach * Impact * Confidence / Effort. Ideal for product management and feature prioritization.",
      "category": "scoring_model",
      "status": "stable"
    },
    "semantics": {
      "purpose": "Arrange-phase prioritization by converting item-level inputs into a sortable score and rank.",
      "requires_comparable_items": true,
      "notes_on_comparability": "Items must share the RICE criteria: reach, impact, confidence, effort."
    },
    "inputs": {
      "required_item_fields": [
        "id",
        "label"
      ],
      "required_item_attributes": [
        {
          "key": "reach",
          "type": "numeric",
          "description": "Estimated number of users/events per time period. Must be > 0."
        },
        {
          "key": "impact",
          "type": "numeric",
          "description": "Impact per user on a fixed scale (typically 0.25, 0.5, 1, 2, 3 where 3 = massive, 2 = high, 1 = medium, 0.5 = low, 0.25 = minimal). Must be > 0."
        },
        {
          "key": "confidence",
          "type": "numeric",
          "description": "Confidence as a percentage (0-100). Represents confidence in Reach and Impact estimates."
        },
        {
          "key": "effort",
          "type": "numeric",
          "description": "Total effort required, usually in person-months (or weeks/points if consistent). Must be > 0."
        }
      ],
      "optional_item_attributes": [],
      "criteria_set_contract": {
        "allowed_criteria_ids": [
          "reach",
          "impact",
          "confidence",
          "effort"
        ],
        "requires_explicit_criteria_set": false,
        "notes": "If a CriteriaSet is provided, its criteria ids must match these keys. The tool uses a fixed set of criteria."
      },
      "lens_support": {
        "requires_lens": false,
        "notes": "If a lens is provided, the tool only scores items within that lens."
      },
      "tool_config": {
        "fields": [
          {
            "name": "confidence_input_scale",
            "type": "enum",
            "allowed_values": [
              "0_to_100",
              "0_to_1"
            ],
            "default": "0_to_100",
            "description": "Interprets the numeric confidence values. Default assumes percentage (0-100)."
          }
        ],
        "supports_defaults": true
      }
    },
    "algorithm": {
      "score_model_type": "formula",
      "formula_description": "RICE = (reach * impact * (confidence / 100)) / effort",
      "formula_variables": {
        "reach": "numeric, > 0",
        "impact": "numeric, > 0",
        "confidence": "numeric, 0-100 (converted to 0-1 by dividing by 100)",
        "effort": "numeric, > 0"
      },
      "scaling_and_normalization": {
        "normalizes_confidence": "Divides confidence by 100 to convert percentage to decimal (0-1).",
        "normalizes_other_fields": "none"
      },
      "handles_missing_values": {
        "strategy": "reject_item",
        "rules": [
          "If any of reach, impact, confidence, or effort is missing, null, or <= 0, the item is excluded from scoring and returned with an error reason."
        ]
      },
      "tie_breaking": {
        "primary": "higher score wins",
        "secondary": "alphabetical by item.label",
        "tertiary": "stable by original import order"
      },
      "determinism": true
    },
    "outputs": {
      "score_set_schema": {
        "per_item_fields": [
          {
            "name": "item_id",
            "type": "string"
          },
          {
            "name": "score",
            "type": "numeric",
            "description": "RICE score. Higher = higher priority."
          },
          {
            "name": "rank",
            "type": "integer",
            "description": "1 = highest priority within the evaluated set."
          }
        ],
        "annotations": [
          {
            "name": "tool_name",
            "type": "string",
            "value": "RICE"
          },
          {
            "name": "raw_reach",
            "type": "numeric",
            "description": "Original reach value"
          },
          {
            "name": "raw_impact",
            "type": "numeric",
            "description": "Original impact value"
          },
          {
            "name": "raw_confidence",
            "type": "numeric",
            "description": "Original confidence value (0-100)"
          },
          {
            "name": "raw_effort",
            "type": "numeric",
            "description": "Original effort value"
          }
        ]
      },
      "tool_run_metadata": {
        "records_raw_inputs": true,
        "raw_inputs_schema": {
          "reach": "numeric",
          "impact": "numeric",
          "confidence": "numeric (0-100)",
          "effort": "numeric"
        }
      }
    },
    "interaction_model": {
      "ui_pattern": "sequential_wizard",
      "supports_inline_edit": true,
      "supports_batch_edit": false,
      "step_flow": [
        "input_items",
        "for_each_item_collect_reach",
        "for_each_item_collect_impact",
        "for_each_item_collect_confidence",
        "for_each_item_collect_effort",
        "calculate_scores",
        "review_results"
      ],
      "validation_behavior": {
        "on_invalid_item": "prevent_progression_until_valid",
        "on_any_error": "show_error_message_and_allow_correction"
      }
    },
    "constraints_and_assumptions": {
      "max_items_tested": 1000,
      "performance_notes": "O(n) over number of items for scoring. Sequential input collection may be slow for large item sets.",
      "assumes_attribute_types": "numeric fields must already be parsed by platform. Confidence is assumed to be 0-100 percentage unless config specifies otherwise."
    },
    "toolRequirements": {
      "prerequisites": [
        "CRITERIA_LOCKED",
        "SCALES_DEFINED",
        "POOL_NONEMPTY",
        "VALUES_SUFFICIENT"
      ],
      "requiredCriteria": [
        {
          "tag": "term:reach",
          "criterionTypes": [
            "numeric"
          ],
          "minCount": 1
        },
        {
          "tag": "term:impact",
          "criterionTypes": [
            "numeric"
          ],
          "minCount": 1
        },
        {
          "tag": "term:confidence",
          "criterionTypes": [
            "numeric"
          ],
          "minCount": 1
        },
        {
          "tag": "term:effort",
          "criterionTypes": [
            "numeric"
          ],
          "minCount": 1
        }
      ],
      "poolRequirements": {
        "minItems": 2,
        "requireLocked": false
      }
    },
    "test_vectors": [
      {
        "name": "simple_three_item_example",
        "items": [
          {
            "id": "A",
            "label": "Item A",
            "attributes": {
              "reach": 1200,
              "impact": 3,
              "confidence": 80,
              "effort": 2
            }
          },
          {
            "id": "B",
            "label": "Item B",
            "attributes": {
              "reach": 500,
              "impact": 2,
              "confidence": 90,
              "effort": 1
            }
          },
          {
            "id": "C",
            "label": "Item C",
            "attributes": {
              "reach": 800,
              "impact": 1,
              "confidence": 70,
              "effort": 0.5
            }
          }
        ],
        "tool_config": {
          "confidence_input_scale": "0_to_100"
        },
        "expected_scores": [
          {
            "item_id": "A",
            "score": 1440,
            "rank": 1
          },
          {
            "item_id": "B",
            "score": 900,
            "rank": 2
          },
          {
            "item_id": "C",
            "score": 1120,
            "rank": 3
          }
        ]
      }
    ]
  },
  {
    "tool_spec_version": "1.0",
    "identity": {
      "tool_name": "RICE",
      "short_label": "RICE Scoring",
      "description": "Ranks items by Reach * Impact * Confidence / Effort. Ideal for product management and feature prioritization.",
      "category": "scoring_model",
      "status": "stable"
    },
    "semantics": {
      "purpose": "Arrange-phase prioritization by converting item-level inputs into a sortable score and rank.",
      "requires_comparable_items": true,
      "notes_on_comparability": "Items must share the RICE criteria: reach, impact, confidence, effort."
    },
    "inputs": {
      "required_item_fields": [
        "id",
        "label"
      ],
      "required_item_attributes": [
        {
          "key": "reach",
          "type": "numeric",
          "description": "Estimated number of users/events per time period. Must be > 0."
        },
        {
          "key": "impact",
          "type": "numeric",
          "description": "Impact per user on a fixed scale (typically 0.25, 0.5, 1, 2, 3 where 3 = massive, 2 = high, 1 = medium, 0.5 = low, 0.25 = minimal). Must be > 0."
        },
        {
          "key": "confidence",
          "type": "numeric",
          "description": "Confidence as a percentage (0-100). Represents confidence in Reach and Impact estimates."
        },
        {
          "key": "effort",
          "type": "numeric",
          "description": "Total effort required, usually in person-months (or weeks/points if consistent). Must be > 0."
        }
      ],
      "optional_item_attributes": [],
      "criteria_set_contract": {
        "allowed_criteria_ids": [
          "reach",
          "impact",
          "confidence",
          "effort"
        ],
        "requires_explicit_criteria_set": false,
        "notes": "If a CriteriaSet is provided, its criteria ids must match these keys. The tool uses a fixed set of criteria."
      },
      "lens_support": {
        "requires_lens": false,
        "notes": "If a lens is provided, the tool only scores items within that lens."
      },
      "tool_config": {
        "fields": [
          {
            "name": "confidence_input_scale",
            "type": "enum",
            "allowed_values": [
              "0_to_100",
              "0_to_1"
            ],
            "default": "0_to_100",
            "description": "Interprets the numeric confidence values. Default assumes percentage (0-100)."
          }
        ],
        "supports_defaults": true
      }
    },
    "algorithm": {
      "score_model_type": "formula",
      "formula_description": "RICE = (reach * impact * (confidence / 100)) / effort",
      "formula_variables": {
        "reach": "numeric, > 0",
        "impact": "numeric, > 0",
        "confidence": "numeric, 0-100 (converted to 0-1 by dividing by 100)",
        "effort": "numeric, > 0"
      },
      "scaling_and_normalization": {
        "normalizes_confidence": "Divides confidence by 100 to convert percentage to decimal (0-1).",
        "normalizes_other_fields": "none"
      },
      "handles_missing_values": {
        "strategy": "reject_item",
        "rules": [
          "If any of reach, impact, confidence, or effort is missing, null, or <= 0, the item is excluded from scoring and returned with an error reason."
        ]
      },
      "tie_breaking": {
        "primary": "higher score wins",
        "secondary": "alphabetical by item.label",
        "tertiary": "stable by original import order"
      },
      "determinism": true
    },
    "outputs": {
      "score_set_schema": {
        "per_item_fields": [
          {
            "name": "item_id",
            "type": "string"
          },
          {
            "name": "score",
            "type": "numeric",
            "description": "RICE score. Higher = higher priority."
          },
          {
            "name": "rank",
            "type": "integer",
            "description": "1 = highest priority within the evaluated set."
          }
        ],
        "annotations": [
          {
            "name": "tool_name",
            "type": "string",
            "value": "RICE"
          },
          {
            "name": "raw_reach",
            "type": "numeric",
            "description": "Original reach value"
          },
          {
            "name": "raw_impact",
            "type": "numeric",
            "description": "Original impact value"
          },
          {
            "name": "raw_confidence",
            "type": "numeric",
            "description": "Original confidence value (0-100)"
          },
          {
            "name": "raw_effort",
            "type": "numeric",
            "description": "Original effort value"
          }
        ]
      },
      "tool_run_metadata": {
        "records_raw_inputs": true,
        "raw_inputs_schema": {
          "reach": "numeric",
          "impact": "numeric",
          "confidence": "numeric (0-100)",
          "effort": "numeric"
        }
      }
    },
    "interaction_model": {
      "ui_pattern": "sequential_wizard",
      "supports_inline_edit": true,
      "supports_batch_edit": false,
      "step_flow": [
        "input_items",
        "for_each_item_collect_reach",
        "for_each_item_collect_impact",
        "for_each_item_collect_confidence",
        "for_each_item_collect_effort",
        "calculate_scores",
        "review_results"
      ],
      "validation_behavior": {
        "on_invalid_item": "prevent_progression_until_valid",
        "on_any_error": "show_error_message_and_allow_correction"
      }
    },
    "constraints_and_assumptions": {
      "max_items_tested": 1000,
      "performance_notes": "O(n) over number of items for scoring. Sequential input collection may be slow for large item sets.",
      "assumes_attribute_types": "numeric fields must already be parsed by platform. Confidence is assumed to be 0-100 percentage unless config specifies otherwise."
    },
    "test_vectors": [
      {
        "name": "simple_three_item_example",
        "items": [
          {
            "id": "A",
            "label": "Item A",
            "attributes": {
              "reach": 1200,
              "impact": 3,
              "confidence": 80,
              "effort": 2
            }
          },
          {
            "id": "B",
            "label": "Item B",
            "attributes": {
              "reach": 500,
              "impact": 2,
              "confidence": 90,
              "effort": 1
            }
          },
          {
            "id": "C",
            "label": "Item C",
            "attributes": {
              "reach": 800,
              "impact": 1,
              "confidence": 70,
              "effort": 0.5
            }
          }
        ],
        "tool_config": {
          "confidence_input_scale": "0_to_100"
        },
        "expected_scores": [
          {
            "item_id": "A",
            "score": 1440,
            "rank": 1
          },
          {
            "item_id": "B",
            "score": 900,
            "rank": 2
          },
          {
            "item_id": "C",
            "score": 1120,
            "rank": 3
          }
        ]
      }
    ]
  },
  {
    "tool_spec_version": "1.0",
    "identity": {
      "tool_name": "Simple Weighted Scoring",
      "short_label": "Weighted Scoring",
      "description": "Prioritizes items by scoring each on multiple weighted criteria and calculating weighted totals. Transparent, repeatable method for team decision-making with explicit trade-offs.",
      "category": "scoring_model",
      "status": "stable"
    },
    "semantics": {
      "purpose": "Arrange-phase prioritization by converting item-level scores across multiple weighted criteria into a single sortable score and rank.",
      "requires_comparable_items": true,
      "notes_on_comparability": "Items must be scored on the same set of criteria. All items are evaluated against all criteria in the active CriteriaSet."
    },
    "inputs": {
      "required_item_fields": [
        "id",
        "label"
      ],
      "required_item_attributes": [],
      "optional_item_attributes": [
        {
          "key": "description",
          "type": "string",
          "description": "Optional description or notes for the item."
        }
      ],
      "criteria_set_contract": {
        "allowed_criteria_ids": "any",
        "requires_explicit_criteria_set": true,
        "notes": "CriteriaSet must contain at least one criterion. Each criterion requires: id, name, description, and weight (percentage 0-100). All criterion weights must sum to exactly 100%."
      },
      "lens_support": {
        "requires_lens": false,
        "notes": "If a lens is provided, the tool only scores items within that lens. All items in the lens are scored against all criteria in the CriteriaSet."
      },
      "tool_config": {
        "fields": [
          {
            "name": "scoring_scale_min",
            "type": "integer",
            "allowed_values": null,
            "default": 1,
            "description": "Minimum value for scoring scale (typically 1)."
          },
          {
            "name": "scoring_scale_max",
            "type": "integer",
            "allowed_values": null,
            "default": 5,
            "description": "Maximum value for scoring scale (typically 3, 5, or 7)."
          },
          {
            "name": "higher_is_better",
            "type": "boolean",
            "allowed_values": null,
            "default": true,
            "description": "Whether higher scores indicate better performance. Always true for this tool; cost/effort criteria should be scored inversely (low effort = high score)."
          },
          {
            "name": "require_all_scores",
            "type": "boolean",
            "allowed_values": null,
            "default": true,
            "description": "If true, all items must have scores for all criteria before calculation can proceed."
          }
        ],
        "supports_defaults": true
      }
    },
    "algorithm": {
      "score_model_type": "weighted_sum",
      "formula_description": "weighted_total = Σ(criterion_score × criterion_weight / 100) for all criteria",
      "formula_variables": {
        "criterion_score": "numeric, between scoring_scale_min and scoring_scale_max",
        "criterion_weight": "numeric, percentage (0-100), all weights must sum to 100"
      },
      "scaling_and_normalization": {
        "normalizes_weights": "Weights are normalized to percentages (divided by 100) before multiplication. Platform must ensure weights sum to 100% before tool execution.",
        "normalizes_scores": "Scores must be within the configured scale range (min-max). No normalization applied; raw scores are used directly."
      },
      "handles_missing_values": {
        "strategy": "reject_item_or_prevent_run",
        "rules": [
          "If require_all_scores is true (default), the tool will not calculate results until all items have scores for all criteria.",
          "If an item is missing a score for any criterion, that item is excluded from the ScoreSet.",
          "Platform should validate completeness before tool execution."
        ]
      },
      "tie_breaking": {
        "primary": "higher weighted_total wins",
        "secondary": "alphabetical by item.label",
        "tertiary": "stable by original import order",
        "notes": "Ties in weighted totals are allowed in final output. Platform may offer optional tie-breaking rounds."
      },
      "determinism": true
    },
    "outputs": {
      "score_set_schema": {
        "per_item_fields": [
          {
            "name": "item_id",
            "type": "string"
          },
          {
            "name": "weighted_total",
            "type": "numeric",
            "description": "Sum of (criterion_score × criterion_weight / 100) for all criteria. Typically displayed with 2-3 decimal places."
          },
          {
            "name": "rank",
            "type": "integer",
            "description": "1 = highest priority (highest weighted_total). Ties may share the same rank."
          },
          {
            "name": "criterion_contributions",
            "type": "object",
            "description": "Per-criterion breakdown showing score × weight for each criterion. Keys are criterion_ids, values are { score: number, weighted_contribution: number }."
          }
        ],
        "annotations": [
          {
            "name": "tool_name",
            "type": "string",
            "value": "Simple Weighted Scoring"
          },
          {
            "name": "criteria_set_id",
            "type": "string",
            "description": "ID of the CriteriaSet used for this run."
          },
          {
            "name": "scoring_scale",
            "type": "object",
            "description": "{ min: number, max: number } indicating the scale used."
          }
        ]
      },
      "tool_run_metadata": {
        "records_raw_inputs": true,
        "raw_inputs_schema": {
          "item_scores": "object mapping item_id → { criterion_id: score }",
          "criteria_weights": "object mapping criterion_id → weight (percentage)",
          "scoring_scale": "{ min: number, max: number }"
        },
        "additional_fields": [
          {
            "name": "decision_context",
            "type": "object",
            "description": "Optional context: { name: string, success_definition: string, constraints?: string }"
          },
          {
            "name": "total_items_scored",
            "type": "integer",
            "description": "Number of items included in the final ScoreSet."
          },
          {
            "name": "total_criteria",
            "type": "integer",
            "description": "Number of criteria in the CriteriaSet."
          }
        ]
      }
    },
    "interaction_model": {
      "ui_pattern": "guided_workflow",
      "supports_inline_edit": true,
      "supports_batch_edit": false,
      "step_flow": [
        "define_context",
        "add_items",
        "define_criteria",
        "assign_weights",
        "choose_scale",
        "score_items",
        "review_results"
      ],
      "validation_behavior": {
        "on_invalid_item": "prevent_progression_with_error_message",
        "on_weight_sum_error": "highlight_error_and_offer_normalization",
        "on_missing_scores": "prevent_calculation_and_highlight_empty_cells",
        "on_any_error": "block_progression_until_resolved"
      },
      "workflow_notes": [
        "Context step is optional but recommended for team alignment.",
        "Weights must sum to exactly 100% before proceeding to scoring.",
        "All scores must be filled before calculation can proceed.",
        "Results view shows expandable details per item showing criterion contributions."
      ]
    },
    "constraints_and_assumptions": {
      "max_items_tested": 50,
      "recommended_max_items": 30,
      "max_criteria": 15,
      "performance_notes": "O(n × m) where n = items, m = criteria. Linear time complexity for calculation. UI may become unwieldy with >30 items.",
      "assumes_attribute_types": "Scores must be numeric and within the configured scale range. Weights must be numeric percentages.",
      "assumes_platform_validation": "Platform should validate weight sum = 100% and score completeness before tool execution.",
      "pseudo_quantitative_note": "This is a pseudo-quantitative method. The numbers reflect subjective judgments and weights chosen by users. Results should be used as structured input to decision-making, not as a replacement for judgment."
    },
    "toolRequirements": {
      "prerequisites": [
        "CRITERIA_LOCKED",
        "SCALES_DEFINED",
        "POOL_NONEMPTY",
        "VALUES_SUFFICIENT"
      ],
      "requiredCriteria": [
        {
          "tag": "term:*",
          "criterionTypes": [
            "numeric",
            "ordinal"
          ],
          "minCount": 1
        }
      ],
      "poolRequirements": {
        "minItems": 2,
        "requireLocked": false
      }
    },
    "test_vectors": [
      {
        "name": "simple_three_item_two_criteria",
        "items": [
          {
            "id": "item_a",
            "label": "Item A",
            "attributes": {}
          },
          {
            "id": "item_b",
            "label": "Item B",
            "attributes": {}
          },
          {
            "id": "item_c",
            "label": "Item C",
            "attributes": {}
          }
        ],
        "criteria_set": {
          "id": "test_criteria_1",
          "criteria": [
            {
              "id": "impact",
              "name": "Impact",
              "description": "How much will this impact each person?",
              "weight": 60
            },
            {
              "id": "effort",
              "name": "Effort",
              "description": "How much work is required?",
              "weight": 40
            }
          ]
        },
        "item_scores": {
          "item_a": {
            "impact": 5,
            "effort": 1
          },
          "item_b": {
            "impact": 3,
            "effort": 3
          },
          "item_c": {
            "impact": 2,
            "effort": 5
          }
        },
        "tool_config": {
          "scoring_scale_min": 1,
          "scoring_scale_max": 5,
          "higher_is_better": true,
          "require_all_scores": true
        },
        "expected_scores": [
          {
            "item_id": "item_a",
            "weighted_total": 3.4,
            "rank": 1,
            "criterion_contributions": {
              "impact": {
                "score": 5,
                "weighted_contribution": 3
              },
              "effort": {
                "score": 1,
                "weighted_contribution": 0.4
              }
            }
          },
          {
            "item_id": "item_b",
            "weighted_total": 3,
            "rank": 2,
            "criterion_contributions": {
              "impact": {
                "score": 3,
                "weighted_contribution": 1.8
              },
              "effort": {
                "score": 3,
                "weighted_contribution": 1.2
              }
            }
          },
          {
            "item_id": "item_c",
            "weighted_total": 3.2,
            "rank": 3,
            "criterion_contributions": {
              "impact": {
                "score": 2,
                "weighted_contribution": 1.2
              },
              "effort": {
                "score": 5,
                "weighted_contribution": 2
              }
            }
          }
        ],
        "notes": "Item A wins due to high impact (60% weight) despite low effort score. Item C ranks last despite high effort score because impact is weighted more heavily."
      },
      {
        "name": "rice_criteria_example",
        "items": [
          {
            "id": "feature_x",
            "label": "Feature X",
            "attributes": {}
          },
          {
            "id": "feature_y",
            "label": "Feature Y",
            "attributes": {}
          }
        ],
        "criteria_set": {
          "id": "rice_criteria",
          "criteria": [
            {
              "id": "reach",
              "name": "Reach",
              "description": "How many people will this affect?",
              "weight": 25
            },
            {
              "id": "impact",
              "name": "Impact",
              "description": "How much will this impact each person?",
              "weight": 25
            },
            {
              "id": "confidence",
              "name": "Confidence",
              "description": "How confident are we in our estimates?",
              "weight": 25
            },
            {
              "id": "effort",
              "name": "Effort",
              "description": "How much work is required? (inverted: low effort = high score)",
              "weight": 25
            }
          ]
        },
        "item_scores": {
          "feature_x": {
            "reach": 5,
            "impact": 4,
            "confidence": 3,
            "effort": 2
          },
          "feature_y": {
            "reach": 3,
            "impact": 5,
            "confidence": 4,
            "effort": 4
          }
        },
        "tool_config": {
          "scoring_scale_min": 1,
          "scoring_scale_max": 5,
          "higher_is_better": true,
          "require_all_scores": true
        },
        "expected_scores": [
          {
            "item_id": "feature_x",
            "weighted_total": 3.5,
            "rank": 1,
            "criterion_contributions": {
              "reach": {
                "score": 5,
                "weighted_contribution": 1.25
              },
              "impact": {
                "score": 4,
                "weighted_contribution": 1
              },
              "confidence": {
                "score": 3,
                "weighted_contribution": 0.75
              },
              "effort": {
                "score": 2,
                "weighted_contribution": 0.5
              }
            }
          },
          {
            "item_id": "feature_y",
            "weighted_total": 4,
            "rank": 2,
            "criterion_contributions": {
              "reach": {
                "score": 3,
                "weighted_contribution": 0.75
              },
              "impact": {
                "score": 5,
                "weighted_contribution": 1.25
              },
              "confidence": {
                "score": 4,
                "weighted_contribution": 1
              },
              "effort": {
                "score": 4,
                "weighted_contribution": 1
              }
            }
          }
        ],
        "notes": "Demonstrates using RICE-style criteria with equal weights. Feature Y wins due to balanced high scores across all criteria."
      }
    ]
  },
  {
    "tool_spec_version": "1.0",
    "identity": {
      "tool_name": "Simple Weighted Scoring",
      "short_label": "Weighted Scoring",
      "description": "Prioritizes items by scoring each on multiple weighted criteria and calculating weighted totals. Transparent, repeatable method for team decision-making with explicit trade-offs.",
      "category": "scoring_model",
      "status": "stable"
    },
    "semantics": {
      "purpose": "Arrange-phase prioritization by converting item-level scores across multiple weighted criteria into a single sortable score and rank.",
      "requires_comparable_items": true,
      "notes_on_comparability": "Items must be scored on the same set of criteria. All items are evaluated against all criteria in the active CriteriaSet."
    },
    "inputs": {
      "required_item_fields": [
        "id",
        "label"
      ],
      "required_item_attributes": [],
      "optional_item_attributes": [
        {
          "key": "description",
          "type": "string",
          "description": "Optional description or notes for the item."
        }
      ],
      "criteria_set_contract": {
        "allowed_criteria_ids": "any",
        "requires_explicit_criteria_set": true,
        "notes": "CriteriaSet must contain at least one criterion. Each criterion requires: id, name, description, and weight (percentage 0-100). All criterion weights must sum to exactly 100%."
      },
      "lens_support": {
        "requires_lens": false,
        "notes": "If a lens is provided, the tool only scores items within that lens. All items in the lens are scored against all criteria in the CriteriaSet."
      },
      "tool_config": {
        "fields": [
          {
            "name": "scoring_scale_min",
            "type": "integer",
            "allowed_values": null,
            "default": 1,
            "description": "Minimum value for scoring scale (typically 1)."
          },
          {
            "name": "scoring_scale_max",
            "type": "integer",
            "allowed_values": null,
            "default": 5,
            "description": "Maximum value for scoring scale (typically 3, 5, or 7)."
          },
          {
            "name": "higher_is_better",
            "type": "boolean",
            "allowed_values": null,
            "default": true,
            "description": "Whether higher scores indicate better performance. Always true for this tool; cost/effort criteria should be scored inversely (low effort = high score)."
          },
          {
            "name": "require_all_scores",
            "type": "boolean",
            "allowed_values": null,
            "default": true,
            "description": "If true, all items must have scores for all criteria before calculation can proceed."
          }
        ],
        "supports_defaults": true
      }
    },
    "algorithm": {
      "score_model_type": "weighted_sum",
      "formula_description": "weighted_total = Σ(criterion_score × criterion_weight / 100) for all criteria",
      "formula_variables": {
        "criterion_score": "numeric, between scoring_scale_min and scoring_scale_max",
        "criterion_weight": "numeric, percentage (0-100), all weights must sum to 100"
      },
      "scaling_and_normalization": {
        "normalizes_weights": "Weights are normalized to percentages (divided by 100) before multiplication. Platform must ensure weights sum to 100% before tool execution.",
        "normalizes_scores": "Scores must be within the configured scale range (min-max). No normalization applied; raw scores are used directly."
      },
      "handles_missing_values": {
        "strategy": "reject_item_or_prevent_run",
        "rules": [
          "If require_all_scores is true (default), the tool will not calculate results until all items have scores for all criteria.",
          "If an item is missing a score for any criterion, that item is excluded from the ScoreSet.",
          "Platform should validate completeness before tool execution."
        ]
      },
      "tie_breaking": {
        "primary": "higher weighted_total wins",
        "secondary": "alphabetical by item.label",
        "tertiary": "stable by original import order",
        "notes": "Ties in weighted totals are allowed in final output. Platform may offer optional tie-breaking rounds."
      },
      "determinism": true
    },
    "outputs": {
      "score_set_schema": {
        "per_item_fields": [
          {
            "name": "item_id",
            "type": "string"
          },
          {
            "name": "weighted_total",
            "type": "numeric",
            "description": "Sum of (criterion_score × criterion_weight / 100) for all criteria. Typically displayed with 2-3 decimal places."
          },
          {
            "name": "rank",
            "type": "integer",
            "description": "1 = highest priority (highest weighted_total). Ties may share the same rank."
          },
          {
            "name": "criterion_contributions",
            "type": "object",
            "description": "Per-criterion breakdown showing score × weight for each criterion. Keys are criterion_ids, values are { score: number, weighted_contribution: number }."
          }
        ],
        "annotations": [
          {
            "name": "tool_name",
            "type": "string",
            "value": "Simple Weighted Scoring"
          },
          {
            "name": "criteria_set_id",
            "type": "string",
            "description": "ID of the CriteriaSet used for this run."
          },
          {
            "name": "scoring_scale",
            "type": "object",
            "description": "{ min: number, max: number } indicating the scale used."
          }
        ]
      },
      "tool_run_metadata": {
        "records_raw_inputs": true,
        "raw_inputs_schema": {
          "item_scores": "object mapping item_id → { criterion_id: score }",
          "criteria_weights": "object mapping criterion_id → weight (percentage)",
          "scoring_scale": "{ min: number, max: number }"
        },
        "additional_fields": [
          {
            "name": "decision_context",
            "type": "object",
            "description": "Optional context: { name: string, success_definition: string, constraints?: string }"
          },
          {
            "name": "total_items_scored",
            "type": "integer",
            "description": "Number of items included in the final ScoreSet."
          },
          {
            "name": "total_criteria",
            "type": "integer",
            "description": "Number of criteria in the CriteriaSet."
          }
        ]
      }
    },
    "interaction_model": {
      "ui_pattern": "guided_workflow",
      "supports_inline_edit": true,
      "supports_batch_edit": false,
      "step_flow": [
        "define_context",
        "add_items",
        "define_criteria",
        "assign_weights",
        "choose_scale",
        "score_items",
        "review_results"
      ],
      "validation_behavior": {
        "on_invalid_item": "prevent_progression_with_error_message",
        "on_weight_sum_error": "highlight_error_and_offer_normalization",
        "on_missing_scores": "prevent_calculation_and_highlight_empty_cells",
        "on_any_error": "block_progression_until_resolved"
      },
      "workflow_notes": [
        "Context step is optional but recommended for team alignment.",
        "Weights must sum to exactly 100% before proceeding to scoring.",
        "All scores must be filled before calculation can proceed.",
        "Results view shows expandable details per item showing criterion contributions."
      ]
    },
    "constraints_and_assumptions": {
      "max_items_tested": 50,
      "recommended_max_items": 30,
      "max_criteria": 15,
      "performance_notes": "O(n × m) where n = items, m = criteria. Linear time complexity for calculation. UI may become unwieldy with >30 items.",
      "assumes_attribute_types": "Scores must be numeric and within the configured scale range. Weights must be numeric percentages.",
      "assumes_platform_validation": "Platform should validate weight sum = 100% and score completeness before tool execution.",
      "pseudo_quantitative_note": "This is a pseudo-quantitative method. The numbers reflect subjective judgments and weights chosen by users. Results should be used as structured input to decision-making, not as a replacement for judgment."
    },
    "test_vectors": [
      {
        "name": "simple_three_item_two_criteria",
        "items": [
          {
            "id": "item_a",
            "label": "Item A",
            "attributes": {}
          },
          {
            "id": "item_b",
            "label": "Item B",
            "attributes": {}
          },
          {
            "id": "item_c",
            "label": "Item C",
            "attributes": {}
          }
        ],
        "criteria_set": {
          "id": "test_criteria_1",
          "criteria": [
            {
              "id": "impact",
              "name": "Impact",
              "description": "How much will this impact each person?",
              "weight": 60
            },
            {
              "id": "effort",
              "name": "Effort",
              "description": "How much work is required?",
              "weight": 40
            }
          ]
        },
        "item_scores": {
          "item_a": {
            "impact": 5,
            "effort": 1
          },
          "item_b": {
            "impact": 3,
            "effort": 3
          },
          "item_c": {
            "impact": 2,
            "effort": 5
          }
        },
        "tool_config": {
          "scoring_scale_min": 1,
          "scoring_scale_max": 5,
          "higher_is_better": true,
          "require_all_scores": true
        },
        "expected_scores": [
          {
            "item_id": "item_a",
            "weighted_total": 3.4,
            "rank": 1,
            "criterion_contributions": {
              "impact": {
                "score": 5,
                "weighted_contribution": 3
              },
              "effort": {
                "score": 1,
                "weighted_contribution": 0.4
              }
            }
          },
          {
            "item_id": "item_b",
            "weighted_total": 3,
            "rank": 2,
            "criterion_contributions": {
              "impact": {
                "score": 3,
                "weighted_contribution": 1.8
              },
              "effort": {
                "score": 3,
                "weighted_contribution": 1.2
              }
            }
          },
          {
            "item_id": "item_c",
            "weighted_total": 3.2,
            "rank": 3,
            "criterion_contributions": {
              "impact": {
                "score": 2,
                "weighted_contribution": 1.2
              },
              "effort": {
                "score": 5,
                "weighted_contribution": 2
              }
            }
          }
        ],
        "notes": "Item A wins due to high impact (60% weight) despite low effort score. Item C ranks last despite high effort score because impact is weighted more heavily."
      },
      {
        "name": "rice_criteria_example",
        "items": [
          {
            "id": "feature_x",
            "label": "Feature X",
            "attributes": {}
          },
          {
            "id": "feature_y",
            "label": "Feature Y",
            "attributes": {}
          }
        ],
        "criteria_set": {
          "id": "rice_criteria",
          "criteria": [
            {
              "id": "reach",
              "name": "Reach",
              "description": "How many people will this affect?",
              "weight": 25
            },
            {
              "id": "impact",
              "name": "Impact",
              "description": "How much will this impact each person?",
              "weight": 25
            },
            {
              "id": "confidence",
              "name": "Confidence",
              "description": "How confident are we in our estimates?",
              "weight": 25
            },
            {
              "id": "effort",
              "name": "Effort",
              "description": "How much work is required? (inverted: low effort = high score)",
              "weight": 25
            }
          ]
        },
        "item_scores": {
          "feature_x": {
            "reach": 5,
            "impact": 4,
            "confidence": 3,
            "effort": 2
          },
          "feature_y": {
            "reach": 3,
            "impact": 5,
            "confidence": 4,
            "effort": 4
          }
        },
        "tool_config": {
          "scoring_scale_min": 1,
          "scoring_scale_max": 5,
          "higher_is_better": true,
          "require_all_scores": true
        },
        "expected_scores": [
          {
            "item_id": "feature_x",
            "weighted_total": 3.5,
            "rank": 1,
            "criterion_contributions": {
              "reach": {
                "score": 5,
                "weighted_contribution": 1.25
              },
              "impact": {
                "score": 4,
                "weighted_contribution": 1
              },
              "confidence": {
                "score": 3,
                "weighted_contribution": 0.75
              },
              "effort": {
                "score": 2,
                "weighted_contribution": 0.5
              }
            }
          },
          {
            "item_id": "feature_y",
            "weighted_total": 4,
            "rank": 2,
            "criterion_contributions": {
              "reach": {
                "score": 3,
                "weighted_contribution": 0.75
              },
              "impact": {
                "score": 5,
                "weighted_contribution": 1.25
              },
              "confidence": {
                "score": 4,
                "weighted_contribution": 1
              },
              "effort": {
                "score": 4,
                "weighted_contribution": 1
              }
            }
          }
        ],
        "notes": "Demonstrates using RICE-style criteria with equal weights. Feature Y wins due to balanced high scores across all criteria."
      }
    ]
  },
  {
    "tool_spec_version": "1.0",
    "identity": {
      "tool_name": "SPEED_BOAT",
      "short_label": "Speed Boat",
      "description": "Collaborative retrospective exercise where items are classified as engines (driving forward), anchors (holding back), rocks (risks to avoid), or life preservers (risk mitigations), then ranked within each category. Based on Luke Hohmann's Innovation Games.",
      "category": "collaborative_game",
      "status": "stable"
    },
    "semantics": {
      "purpose": "Arrange-phase retrospective and risk identification. Surfaces what is helping vs. hindering progress toward a goal. Produces a categorized, ranked output that can feed into prioritization decisions.",
      "requires_comparable_items": false,
      "notes_on_comparability": "Items represent factors affecting progress — not necessarily the same type of thing. Engines and anchors are ranked separately within their category."
    },
    "inputs": {
      "required_item_fields": [
        "id",
        "label"
      ],
      "required_item_attributes": [],
      "optional_item_attributes": [],
      "criteria_set_contract": {
        "allowed_criteria_ids": [],
        "requires_explicit_criteria_set": false,
        "notes": "No criteria set required. The four categories (engine, anchor, rock, preserver) are fixed. Items are categorized and ranked by user during the run."
      },
      "lens_support": {
        "requires_lens": false,
        "notes": "If a lens is provided, only items within the lens are imported. Imported items start as engines by default; type can be changed during the run."
      },
      "tool_config": {
        "fields": [],
        "supports_defaults": true
      }
    },
    "algorithm": {
      "score_model_type": "categorical_ranking",
      "formula_description": "Items are categorized (engine/anchor/rock/preserver) and ranked within each category by user. Output order: engines ranked 1..n, then preservers ranked 1..m. Anchors and rocks are listed but not included in the primary ranking.",
      "formula_variables": {
        "type": "categorical: engine, anchor, rock, preserver",
        "rank": "integer rank within each type category"
      },
      "handles_missing_values": {
        "strategy": "exclude_uncategorized",
        "rules": [
          "All items added to the boat have a type. No uncategorized state once launched."
        ]
      },
      "tie_breaking": {
        "primary": "engines first, then preservers",
        "secondary": "user-assigned rank within each category"
      },
      "determinism": true
    },
    "outputs": {
      "score_set_schema": {
        "per_item_fields": [
          {
            "name": "item_id",
            "type": "string"
          },
          {
            "name": "rank",
            "type": "integer",
            "description": "Global rank: engines first, then preservers."
          },
          {
            "name": "category",
            "type": "string",
            "description": "engine, anchor, rock, or preserver."
          }
        ],
        "annotations": [
          {
            "name": "tool_name",
            "type": "string",
            "value": "SpeedBoat"
          },
          {
            "name": "factorType",
            "type": "string",
            "description": "Original factor type: engine, anchor, rock, or preserver."
          },
          {
            "name": "originalRank",
            "type": "integer",
            "description": "Rank within the factor's own category."
          }
        ]
      },
      "tool_run_metadata": {
        "records_raw_inputs": true,
        "raw_inputs_schema": {
          "factors": "Array of {id, name, type, rank} — all factors added during the run"
        }
      }
    },
    "interaction_model": {
      "ui_pattern": "input_then_categorize",
      "supports_inline_edit": true,
      "supports_batch_edit": false,
      "step_flow": [
        "add_factors_with_initial_type",
        "launch_speed_boat",
        "review_and_rerank_within_categories",
        "save_results"
      ],
      "validation_behavior": {
        "on_invalid_item": "prevent_launch_if_no_factors",
        "on_any_error": "show_error_and_allow_correction"
      }
    },
    "constraints_and_assumptions": {
      "max_items_tested": 100,
      "performance_notes": "O(n) rendering. Practical limit is ~40 items for readable visualization.",
      "assumes_attribute_types": "No attribute requirements. Classification is fully user-driven.",
      "assumptions": [
        "Items represent discrete factors, initiatives, or forces affecting a goal",
        "Engines and preservers are the 'positive' output that feeds downstream prioritization",
        "Anchors and rocks are primarily diagnostic — they inform what to address, not what to sequence"
      ]
    },
    "toolRequirements": {
      "prerequisites": [
        "POOL_NONEMPTY"
      ],
      "requiredCriteria": [],
      "poolRequirements": {
        "minItems": 1,
        "requireLocked": false
      }
    },
    "test_vectors": [
      {
        "name": "basic_boat",
        "description": "Mixed factors across all four categories",
        "items": [
          {
            "id": "f-1",
            "label": "Strong team culture",
            "type": "engine",
            "rank": 1
          },
          {
            "id": "f-2",
            "label": "Technical debt",
            "type": "anchor",
            "rank": 1
          },
          {
            "id": "f-3",
            "label": "Regulatory changes",
            "type": "rock",
            "rank": 1
          },
          {
            "id": "f-4",
            "label": "Rollback plan",
            "type": "preserver",
            "rank": 1
          }
        ],
        "expected_output": [
          {
            "item_id": "f-1",
            "rank": 1,
            "category": "engine"
          },
          {
            "item_id": "f-4",
            "rank": 2,
            "category": "preserver"
          }
        ],
        "notes": "Only engines and preservers appear in ranked output. Anchors and rocks are diagnostic."
      }
    ]
  },
  {
    "tool_spec_version": "1.0",
    "identity": {
      "tool_name": "Stack Ranking",
      "short_label": "Stack Rank",
      "description": "Drag-and-drop stack ranking tool for team prioritization. Participants rank items through direct manipulation, and the tool aggregates rankings using Borda count and consensus scoring algorithms.",
      "category": "ranking_model",
      "status": "stable"
    },
    "semantics": {
      "purpose": "Arrange-phase prioritization through direct participant ranking. Forces explicit tradeoffs by requiring complete ordering. Aggregates multiple participant rankings into consensus scores.",
      "requires_comparable_items": true,
      "notes_on_comparability": "Items are compared through participant rankings. No explicit criteria are required, but participants implicitly evaluate items based on their own criteria."
    },
    "inputs": {
      "required_item_fields": [
        "id",
        "label"
      ],
      "required_item_attributes": [],
      "optional_item_attributes": [
        {
          "key": "description",
          "type": "string",
          "description": "Optional description to help participants understand the item."
        }
      ],
      "criteria_set_contract": {
        "allowed_criteria_ids": [],
        "requires_explicit_criteria_set": false,
        "notes": "Stack ranking does not use explicit criteria. Participants apply their own implicit criteria when ranking."
      },
      "lens_support": {
        "requires_lens": false,
        "notes": "If a lens is provided, only items within that lens are presented for ranking."
      },
      "tool_config": {
        "fields": [
          {
            "name": "participant_count",
            "type": "integer",
            "min": 1,
            "max": 100,
            "default": 1,
            "description": "Number of participants who will rank items."
          },
          {
            "name": "allow_anonymous_participants",
            "type": "boolean",
            "default": true,
            "description": "Whether participants can rank without providing names."
          },
          {
            "name": "require_complete_ranking",
            "type": "boolean",
            "default": false,
            "description": "If true, all items must be ranked. If false, participants can leave items unranked."
          },
          {
            "name": "consensus_algorithm",
            "type": "enum",
            "allowed_values": [
              "borda_count",
              "average_rank",
              "borda_with_consensus"
            ],
            "default": "borda_with_consensus",
            "description": "Algorithm for aggregating participant rankings."
          }
        ],
        "supports_defaults": true
      }
    },
    "algorithm": {
      "score_model_type": "aggregation",
      "formula_description": "Borda Count: For each participant ranking, item at position i (1-indexed) receives (n - i) points where n is total items. Final score is sum across all participants. Consensus score is calculated as 1 - (normalized_standard_deviation) where standard deviation measures rank variance across participants.",
      "formula_variables": {
        "participant_rankings": "Array of arrays, each inner array is a complete ordering of item IDs",
        "n": "Total number of items being ranked",
        "position": "1-indexed position in participant's ranking"
      },
      "scaling_and_normalization": {
        "normalizes_borda_scores": "Scores are summed across participants. No normalization needed as scores are relative.",
        "normalizes_consensus": "Consensus score is normalized to 0-1 range using max standard deviation across all items."
      },
      "handles_missing_values": {
        "strategy": "exclude_from_participant",
        "rules": [
          "If a participant does not rank an item, that item receives 0 points from that participant.",
          "Items not ranked by any participant are excluded from final results.",
          "Items ranked by some but not all participants are included with reduced total scores."
        ]
      },
      "tie_breaking": {
        "primary": "higher Borda score wins",
        "secondary": "lower average rank wins",
        "tertiary": "higher consensus score wins",
        "quaternary": "alphabetical by item.label"
      },
      "determinism": true
    },
    "outputs": {
      "score_set_schema": {
        "per_item_fields": [
          {
            "name": "item_id",
            "type": "string"
          },
          {
            "name": "score",
            "type": "numeric",
            "description": "Borda count score (sum of points across all participants)."
          },
          {
            "name": "rank",
            "type": "integer",
            "description": "1 = highest priority within the evaluated set, based on Borda score."
          },
          {
            "name": "average_rank",
            "type": "numeric",
            "description": "Average position across all participant rankings (1 = best)."
          },
          {
            "name": "consensus_score",
            "type": "numeric",
            "description": "Consensus score from 0-1, where 1 = perfect agreement across participants, 0 = high disagreement."
          },
          {
            "name": "standard_deviation",
            "type": "numeric",
            "description": "Standard deviation of ranks across participants, measuring variance."
          }
        ],
        "annotations": [
          {
            "name": "tool_name",
            "type": "string",
            "value": "Stack Ranking"
          },
          {
            "name": "participant_count",
            "type": "integer",
            "value": "Number of participants who provided rankings"
          },
          {
            "name": "overall_consensus",
            "type": "numeric",
            "value": "Average consensus score across all items (0-1)"
          }
        ]
      },
      "tool_run_metadata": {
        "records_raw_inputs": true,
        "raw_inputs_schema": {
          "participant_rankings": "Array of participant objects, each containing { participant_id, participant_name, ranking: [item_id, ...] }"
        },
        "additional_metadata": {
          "comparison_data": "Array of participant pair comparisons with agreement scores",
          "consensus_rankings": "Full consensus ranking with all calculated metrics"
        }
      }
    },
    "interaction_model": {
      "ui_pattern": "drag_and_drop_ranking",
      "supports_inline_edit": true,
      "supports_batch_edit": false,
      "step_flow": [
        "add_or_import_items",
        "add_participants",
        "each_participant_ranks_items_via_drag_drop",
        "view_individual_rankings",
        "view_consensus_results",
        "compare_participants",
        "export_results"
      ],
      "validation_behavior": {
        "on_invalid_item": "allow_unranked_items_if_config_allows",
        "on_any_error": "show_warnings_but_allow_proceeding"
      },
      "participant_flow": {
        "supports_parallel_ranking": true,
        "supports_sequential_ranking": false,
        "supports_anonymous_ranking": true,
        "supports_rank_revision": true
      }
    },
    "constraints_and_assumptions": {
      "max_items_tested": 100,
      "max_participants": 50,
      "performance_notes": "O(n * p) where n = items, p = participants. Borda count calculation is O(n * p). Consensus calculation is O(n * p).",
      "assumes_attribute_types": "Items must have stable IDs. Participant rankings are arrays of item IDs.",
      "scalability_notes": "Best for 5-30 items and 2-10 participants. Beyond that, UI becomes unwieldy and calculation time increases."
    },
    "toolRequirements": {
      "prerequisites": [
        "POOL_NONEMPTY"
      ],
      "requiredCriteria": [],
      "poolRequirements": {
        "minItems": 2,
        "requireLocked": true
      }
    },
    "test_vectors": [
      {
        "name": "two_participants_three_items_consensus",
        "items": [
          {
            "id": "A",
            "label": "Item A"
          },
          {
            "id": "B",
            "label": "Item B"
          },
          {
            "id": "C",
            "label": "Item C"
          }
        ],
        "tool_config": {
          "participant_count": 2,
          "consensus_algorithm": "borda_with_consensus"
        },
        "participant_rankings": [
          {
            "participant_id": "p1",
            "participant_name": "Participant 1",
            "ranking": [
              "A",
              "B",
              "C"
            ]
          },
          {
            "participant_id": "p2",
            "participant_name": "Participant 2",
            "ranking": [
              "A",
              "B",
              "C"
            ]
          }
        ],
        "expected_scores": [
          {
            "item_id": "A",
            "score": 4,
            "rank": 1,
            "average_rank": 1,
            "consensus_score": 1
          },
          {
            "item_id": "B",
            "score": 2,
            "rank": 2,
            "average_rank": 2,
            "consensus_score": 1
          },
          {
            "item_id": "C",
            "score": 0,
            "rank": 3,
            "average_rank": 3,
            "consensus_score": 1
          }
        ]
      },
      {
        "name": "two_participants_three_items_disagreement",
        "items": [
          {
            "id": "A",
            "label": "Item A"
          },
          {
            "id": "B",
            "label": "Item B"
          },
          {
            "id": "C",
            "label": "Item C"
          }
        ],
        "tool_config": {
          "participant_count": 2,
          "consensus_algorithm": "borda_with_consensus"
        },
        "participant_rankings": [
          {
            "participant_id": "p1",
            "participant_name": "Participant 1",
            "ranking": [
              "A",
              "B",
              "C"
            ]
          },
          {
            "participant_id": "p2",
            "participant_name": "Participant 2",
            "ranking": [
              "C",
              "B",
              "A"
            ]
          }
        ],
        "expected_scores": [
          {
            "item_id": "B",
            "score": 2,
            "rank": 1,
            "average_rank": 2,
            "consensus_score": 0
          },
          {
            "item_id": "A",
            "score": 2,
            "rank": 2,
            "average_rank": 2,
            "consensus_score": 0
          },
          {
            "item_id": "C",
            "score": 2,
            "rank": 3,
            "average_rank": 2,
            "consensus_score": 0
          }
        ]
      },
      {
        "name": "three_participants_four_items",
        "items": [
          {
            "id": "A",
            "label": "Item A"
          },
          {
            "id": "B",
            "label": "Item B"
          },
          {
            "id": "C",
            "label": "Item C"
          },
          {
            "id": "D",
            "label": "Item D"
          }
        ],
        "tool_config": {
          "participant_count": 3,
          "consensus_algorithm": "borda_with_consensus"
        },
        "participant_rankings": [
          {
            "participant_id": "p1",
            "participant_name": "Participant 1",
            "ranking": [
              "A",
              "B",
              "C",
              "D"
            ]
          },
          {
            "participant_id": "p2",
            "participant_name": "Participant 2",
            "ranking": [
              "A",
              "B",
              "C",
              "D"
            ]
          },
          {
            "participant_id": "p3",
            "participant_name": "Participant 3",
            "ranking": [
              "B",
              "A",
              "C",
              "D"
            ]
          }
        ],
        "expected_scores": [
          {
            "item_id": "A",
            "score": 9,
            "rank": 1,
            "average_rank": 1.33
          },
          {
            "item_id": "B",
            "score": 9,
            "rank": 2,
            "average_rank": 1.67
          },
          {
            "item_id": "C",
            "score": 3,
            "rank": 3,
            "average_rank": 3
          },
          {
            "item_id": "D",
            "score": 0,
            "rank": 4,
            "average_rank": 4
          }
        ]
      }
    ]
  },
  {
    "tool_spec_version": "1.0",
    "identity": {
      "tool_name": "Stack Ranking",
      "short_label": "Stack Rank",
      "description": "Drag-and-drop stack ranking tool for team prioritization. Participants rank items through direct manipulation, and the tool aggregates rankings using Borda count and consensus scoring algorithms.",
      "category": "ranking_model",
      "status": "stable"
    },
    "semantics": {
      "purpose": "Arrange-phase prioritization through direct participant ranking. Forces explicit tradeoffs by requiring complete ordering. Aggregates multiple participant rankings into consensus scores.",
      "requires_comparable_items": true,
      "notes_on_comparability": "Items are compared through participant rankings. No explicit criteria are required, but participants implicitly evaluate items based on their own criteria."
    },
    "inputs": {
      "required_item_fields": [
        "id",
        "label"
      ],
      "required_item_attributes": [],
      "optional_item_attributes": [
        {
          "key": "description",
          "type": "string",
          "description": "Optional description to help participants understand the item."
        }
      ],
      "criteria_set_contract": {
        "allowed_criteria_ids": [],
        "requires_explicit_criteria_set": false,
        "notes": "Stack ranking does not use explicit criteria. Participants apply their own implicit criteria when ranking."
      },
      "lens_support": {
        "requires_lens": false,
        "notes": "If a lens is provided, only items within that lens are presented for ranking."
      },
      "tool_config": {
        "fields": [
          {
            "name": "participant_count",
            "type": "integer",
            "min": 1,
            "max": 100,
            "default": 1,
            "description": "Number of participants who will rank items."
          },
          {
            "name": "allow_anonymous_participants",
            "type": "boolean",
            "default": true,
            "description": "Whether participants can rank without providing names."
          },
          {
            "name": "require_complete_ranking",
            "type": "boolean",
            "default": false,
            "description": "If true, all items must be ranked. If false, participants can leave items unranked."
          },
          {
            "name": "consensus_algorithm",
            "type": "enum",
            "allowed_values": [
              "borda_count",
              "average_rank",
              "borda_with_consensus"
            ],
            "default": "borda_with_consensus",
            "description": "Algorithm for aggregating participant rankings."
          }
        ],
        "supports_defaults": true
      }
    },
    "algorithm": {
      "score_model_type": "aggregation",
      "formula_description": "Borda Count: For each participant ranking, item at position i (1-indexed) receives (n - i) points where n is total items. Final score is sum across all participants. Consensus score is calculated as 1 - (normalized_standard_deviation) where standard deviation measures rank variance across participants.",
      "formula_variables": {
        "participant_rankings": "Array of arrays, each inner array is a complete ordering of item IDs",
        "n": "Total number of items being ranked",
        "position": "1-indexed position in participant's ranking"
      },
      "scaling_and_normalization": {
        "normalizes_borda_scores": "Scores are summed across participants. No normalization needed as scores are relative.",
        "normalizes_consensus": "Consensus score is normalized to 0-1 range using max standard deviation across all items."
      },
      "handles_missing_values": {
        "strategy": "exclude_from_participant",
        "rules": [
          "If a participant does not rank an item, that item receives 0 points from that participant.",
          "Items not ranked by any participant are excluded from final results.",
          "Items ranked by some but not all participants are included with reduced total scores."
        ]
      },
      "tie_breaking": {
        "primary": "higher Borda score wins",
        "secondary": "lower average rank wins",
        "tertiary": "higher consensus score wins",
        "quaternary": "alphabetical by item.label"
      },
      "determinism": true
    },
    "outputs": {
      "score_set_schema": {
        "per_item_fields": [
          {
            "name": "item_id",
            "type": "string"
          },
          {
            "name": "score",
            "type": "numeric",
            "description": "Borda count score (sum of points across all participants)."
          },
          {
            "name": "rank",
            "type": "integer",
            "description": "1 = highest priority within the evaluated set, based on Borda score."
          },
          {
            "name": "average_rank",
            "type": "numeric",
            "description": "Average position across all participant rankings (1 = best)."
          },
          {
            "name": "consensus_score",
            "type": "numeric",
            "description": "Consensus score from 0-1, where 1 = perfect agreement across participants, 0 = high disagreement."
          },
          {
            "name": "standard_deviation",
            "type": "numeric",
            "description": "Standard deviation of ranks across participants, measuring variance."
          }
        ],
        "annotations": [
          {
            "name": "tool_name",
            "type": "string",
            "value": "Stack Ranking"
          },
          {
            "name": "participant_count",
            "type": "integer",
            "value": "Number of participants who provided rankings"
          },
          {
            "name": "overall_consensus",
            "type": "numeric",
            "value": "Average consensus score across all items (0-1)"
          }
        ]
      },
      "tool_run_metadata": {
        "records_raw_inputs": true,
        "raw_inputs_schema": {
          "participant_rankings": "Array of participant objects, each containing { participant_id, participant_name, ranking: [item_id, ...] }"
        },
        "additional_metadata": {
          "comparison_data": "Array of participant pair comparisons with agreement scores",
          "consensus_rankings": "Full consensus ranking with all calculated metrics"
        }
      }
    },
    "interaction_model": {
      "ui_pattern": "drag_and_drop_ranking",
      "supports_inline_edit": true,
      "supports_batch_edit": false,
      "step_flow": [
        "add_or_import_items",
        "add_participants",
        "each_participant_ranks_items_via_drag_drop",
        "view_individual_rankings",
        "view_consensus_results",
        "compare_participants",
        "export_results"
      ],
      "validation_behavior": {
        "on_invalid_item": "allow_unranked_items_if_config_allows",
        "on_any_error": "show_warnings_but_allow_proceeding"
      },
      "participant_flow": {
        "supports_parallel_ranking": true,
        "supports_sequential_ranking": false,
        "supports_anonymous_ranking": true,
        "supports_rank_revision": true
      }
    },
    "constraints_and_assumptions": {
      "max_items_tested": 100,
      "max_participants": 50,
      "performance_notes": "O(n * p) where n = items, p = participants. Borda count calculation is O(n * p). Consensus calculation is O(n * p).",
      "assumes_attribute_types": "Items must have stable IDs. Participant rankings are arrays of item IDs.",
      "scalability_notes": "Best for 5-30 items and 2-10 participants. Beyond that, UI becomes unwieldy and calculation time increases."
    },
    "test_vectors": [
      {
        "name": "two_participants_three_items_consensus",
        "items": [
          {
            "id": "A",
            "label": "Item A"
          },
          {
            "id": "B",
            "label": "Item B"
          },
          {
            "id": "C",
            "label": "Item C"
          }
        ],
        "tool_config": {
          "participant_count": 2,
          "consensus_algorithm": "borda_with_consensus"
        },
        "participant_rankings": [
          {
            "participant_id": "p1",
            "participant_name": "Participant 1",
            "ranking": [
              "A",
              "B",
              "C"
            ]
          },
          {
            "participant_id": "p2",
            "participant_name": "Participant 2",
            "ranking": [
              "A",
              "B",
              "C"
            ]
          }
        ],
        "expected_scores": [
          {
            "item_id": "A",
            "score": 4,
            "rank": 1,
            "average_rank": 1,
            "consensus_score": 1
          },
          {
            "item_id": "B",
            "score": 2,
            "rank": 2,
            "average_rank": 2,
            "consensus_score": 1
          },
          {
            "item_id": "C",
            "score": 0,
            "rank": 3,
            "average_rank": 3,
            "consensus_score": 1
          }
        ]
      },
      {
        "name": "two_participants_three_items_disagreement",
        "items": [
          {
            "id": "A",
            "label": "Item A"
          },
          {
            "id": "B",
            "label": "Item B"
          },
          {
            "id": "C",
            "label": "Item C"
          }
        ],
        "tool_config": {
          "participant_count": 2,
          "consensus_algorithm": "borda_with_consensus"
        },
        "participant_rankings": [
          {
            "participant_id": "p1",
            "participant_name": "Participant 1",
            "ranking": [
              "A",
              "B",
              "C"
            ]
          },
          {
            "participant_id": "p2",
            "participant_name": "Participant 2",
            "ranking": [
              "C",
              "B",
              "A"
            ]
          }
        ],
        "expected_scores": [
          {
            "item_id": "B",
            "score": 2,
            "rank": 1,
            "average_rank": 2,
            "consensus_score": 0
          },
          {
            "item_id": "A",
            "score": 2,
            "rank": 2,
            "average_rank": 2,
            "consensus_score": 0
          },
          {
            "item_id": "C",
            "score": 2,
            "rank": 3,
            "average_rank": 2,
            "consensus_score": 0
          }
        ]
      },
      {
        "name": "three_participants_four_items",
        "items": [
          {
            "id": "A",
            "label": "Item A"
          },
          {
            "id": "B",
            "label": "Item B"
          },
          {
            "id": "C",
            "label": "Item C"
          },
          {
            "id": "D",
            "label": "Item D"
          }
        ],
        "tool_config": {
          "participant_count": 3,
          "consensus_algorithm": "borda_with_consensus"
        },
        "participant_rankings": [
          {
            "participant_id": "p1",
            "participant_name": "Participant 1",
            "ranking": [
              "A",
              "B",
              "C",
              "D"
            ]
          },
          {
            "participant_id": "p2",
            "participant_name": "Participant 2",
            "ranking": [
              "A",
              "B",
              "C",
              "D"
            ]
          },
          {
            "participant_id": "p3",
            "participant_name": "Participant 3",
            "ranking": [
              "B",
              "A",
              "C",
              "D"
            ]
          }
        ],
        "expected_scores": [
          {
            "item_id": "A",
            "score": 9,
            "rank": 1,
            "average_rank": 1.33
          },
          {
            "item_id": "B",
            "score": 9,
            "rank": 2,
            "average_rank": 1.67
          },
          {
            "item_id": "C",
            "score": 3,
            "rank": 3,
            "average_rank": 3
          },
          {
            "item_id": "D",
            "score": 0,
            "rank": 4,
            "average_rank": 4
          }
        ]
      }
    ]
  },
  {
    "tool_spec_version": "1.0",
    "identity": {
      "tool_name": "WSJF",
      "short_label": "Weighted Smallest Job First",
      "description": "Ranks items by dividing Cost of Delay (CoD) by Job Duration. Prioritizes work that delivers the most value in the shortest time.",
      "category": "scoring_model",
      "status": "stable"
    },
    "semantics": {
      "purpose": "Arrange-phase prioritization by converting item-level inputs (Cost of Delay and Job Duration) into a sortable score and rank. Focuses on delivering the most value quickly by favoring high-value, low-effort work.",
      "requires_comparable_items": true,
      "notes_on_comparability": "Items must share the WSJF criteria: cost_of_delay and job_duration. Both must be numeric values greater than zero."
    },
    "inputs": {
      "required_item_fields": [
        "id",
        "label"
      ],
      "required_item_attributes": [
        {
          "key": "cost_of_delay",
          "type": "numeric",
          "description": "The economic impact or value lost per unit of time if this job is delayed. Higher values indicate more urgent/valuable work. Can be relative (1-10 scale) or actual economic impact."
        },
        {
          "key": "job_duration",
          "type": "numeric",
          "description": "The estimated time or effort required to complete this job. Lower values indicate smaller, faster jobs. Must use consistent units (e.g., story points, days, hours)."
        }
      ],
      "optional_item_attributes": [],
      "criteria_set_contract": {
        "allowed_criteria_ids": [
          "cost_of_delay",
          "job_duration"
        ],
        "requires_explicit_criteria_set": false,
        "notes": "If a CriteriaSet is provided, its criteria ids must match these keys. The tool uses a fixed two-criterion model."
      },
      "lens_support": {
        "requires_lens": false,
        "notes": "If a lens is provided, the tool only scores items within that lens."
      },
      "tool_config": {
        "fields": [],
        "supports_defaults": true,
        "notes": "No configuration options currently required. All inputs use the same scale/units as provided."
      }
    },
    "algorithm": {
      "score_model_type": "formula",
      "formula_description": "WSJF = Cost of Delay / Job Duration",
      "formula_variables": {
        "cost_of_delay": "numeric, > 0",
        "job_duration": "numeric, > 0"
      },
      "scaling_and_normalization": {
        "normalizes_cost_of_delay": "none - uses values as provided",
        "normalizes_job_duration": "none - uses values as provided",
        "notes": "Both inputs should use consistent scales/units across all items. The tool does not normalize inputs, so users must ensure comparability."
      },
      "handles_missing_values": {
        "strategy": "reject_item",
        "rules": [
          "If cost_of_delay is missing, null, or <= 0, the item is excluded from scoring.",
          "If job_duration is missing, null, or <= 0, the item is excluded from scoring.",
          "Excluded items are not included in the ScoreSet output."
        ]
      },
      "tie_breaking": {
        "primary": "higher score wins",
        "secondary": "alphabetical by item.label",
        "tertiary": "stable by original import order"
      },
      "determinism": true,
      "notes": "The formula is deterministic and produces the same results for the same inputs. Division by zero is prevented by validation (job_duration must be > 0)."
    },
    "outputs": {
      "score_set_schema": {
        "per_item_fields": [
          {
            "name": "item_id",
            "type": "string"
          },
          {
            "name": "score",
            "type": "numeric",
            "description": "WSJF score = Cost of Delay / Job Duration. Higher scores indicate higher priority."
          },
          {
            "name": "rank",
            "type": "integer",
            "description": "1 = highest priority (highest WSJF score) within the evaluated set."
          }
        ],
        "annotations": [
          {
            "name": "tool_name",
            "type": "string",
            "value": "WSJF"
          },
          {
            "name": "cost_of_delay",
            "type": "numeric",
            "description": "Original Cost of Delay value used in calculation"
          },
          {
            "name": "job_duration",
            "type": "numeric",
            "description": "Original Job Duration value used in calculation"
          }
        ]
      },
      "tool_run_metadata": {
        "records_raw_inputs": true,
        "raw_inputs_schema": {
          "cost_of_delay": "numeric",
          "job_duration": "numeric"
        },
        "additional_fields": {
          "total_items_evaluated": "integer",
          "items_excluded": "integer",
          "exclusion_reasons": "array of strings"
        }
      }
    },
    "interaction_model": {
      "ui_pattern": "sequential_item_scoring",
      "supports_inline_edit": true,
      "supports_batch_edit": false,
      "step_flow": [
        "input_items",
        "score_cost_of_delay",
        "score_job_duration",
        "calculate_wsjf",
        "review_results"
      ],
      "validation_behavior": {
        "on_invalid_item": "prevent_advancement_until_valid",
        "on_any_error": "allow_run_with_warnings",
        "validation_rules": [
          "Cost of Delay must be a positive number",
          "Job Duration must be a positive number",
          "Both values must be provided before proceeding to next item"
        ]
      },
      "ui_notes": "The tool uses a sequential flow: for each item, user first enters Cost of Delay, then Job Duration. Progress is tracked across all items. After all items are scored, WSJF is calculated and results are displayed in ranked order."
    },
    "constraints_and_assumptions": {
      "max_items_tested": 1000,
      "performance_notes": "O(n) over number of items for scoring. Sequential UI flow means O(n) user interactions.",
      "assumes_attribute_types": "Numeric fields must already be parsed by platform. Both cost_of_delay and job_duration must be positive numbers.",
      "assumptions": [
        "Users will use consistent units/scales for all items",
        "Cost of Delay represents economic impact or relative value",
        "Job Duration represents effort/time in consistent units",
        "Smaller jobs (lower duration) with high value (high CoD) are preferred"
      ]
    },
    "toolRequirements": {
      "prerequisites": [
        "CRITERIA_LOCKED",
        "SCALES_DEFINED",
        "POOL_NONEMPTY",
        "VALUES_SUFFICIENT"
      ],
      "requiredCriteria": [
        {
          "tag": "term:cost_of_delay",
          "criterionTypes": [
            "numeric"
          ],
          "minCount": 1
        },
        {
          "tag": "term:job_duration",
          "criterionTypes": [
            "numeric"
          ],
          "minCount": 1
        }
      ],
      "poolRequirements": {
        "minItems": 2,
        "requireLocked": false
      }
    },
    "test_vectors": [
      {
        "name": "simple_three_item_example",
        "items": [
          {
            "id": "A",
            "label": "Feature A: Quick Win",
            "attributes": {
              "cost_of_delay": 10,
              "job_duration": 2
            }
          },
          {
            "id": "B",
            "label": "Feature B: Big Bet",
            "attributes": {
              "cost_of_delay": 20,
              "job_duration": 8
            }
          },
          {
            "id": "C",
            "label": "Feature C: High Value Fast",
            "attributes": {
              "cost_of_delay": 15,
              "job_duration": 1
            }
          }
        ],
        "tool_config": {},
        "expected_scores": [
          {
            "item_id": "C",
            "score": 15,
            "rank": 1
          },
          {
            "item_id": "A",
            "score": 5,
            "rank": 2
          },
          {
            "item_id": "B",
            "score": 2.5,
            "rank": 3
          }
        ],
        "notes": "Feature C wins because it has the highest WSJF (15/1 = 15), even though Feature B has higher absolute value. This demonstrates WSJF's preference for quick wins."
      },
      {
        "name": "edge_case_equal_scores",
        "items": [
          {
            "id": "D",
            "label": "Item D",
            "attributes": {
              "cost_of_delay": 10,
              "job_duration": 5
            }
          },
          {
            "id": "E",
            "label": "Item E",
            "attributes": {
              "cost_of_delay": 20,
              "job_duration": 10
            }
          }
        ],
        "tool_config": {},
        "expected_scores": [
          {
            "item_id": "D",
            "score": 2,
            "rank": 1
          },
          {
            "item_id": "E",
            "score": 2,
            "rank": 2
          }
        ],
        "notes": "Both items have WSJF = 2.0. Tie-breaking uses alphabetical order by label, so D ranks before E."
      },
      {
        "name": "missing_value_rejection",
        "items": [
          {
            "id": "F",
            "label": "Item F - Valid",
            "attributes": {
              "cost_of_delay": 8,
              "job_duration": 4
            }
          },
          {
            "id": "G",
            "label": "Item G - Missing CoD",
            "attributes": {
              "cost_of_delay": null,
              "job_duration": 3
            }
          },
          {
            "id": "H",
            "label": "Item H - Zero Duration",
            "attributes": {
              "cost_of_delay": 5,
              "job_duration": 0
            }
          }
        ],
        "tool_config": {},
        "expected_scores": [
          {
            "item_id": "F",
            "score": 2,
            "rank": 1
          }
        ],
        "excluded_items": [
          {
            "item_id": "G",
            "reason": "cost_of_delay is missing or invalid"
          },
          {
            "item_id": "H",
            "reason": "job_duration must be greater than zero"
          }
        ],
        "notes": "Items G and H are excluded from scoring due to missing/invalid values. Only Item F is scored."
      }
    ]
  },
  {
    "tool_spec_version": "1.0",
    "identity": {
      "tool_name": "WSJF",
      "short_label": "Weighted Smallest Job First",
      "description": "Ranks items by dividing Cost of Delay (CoD) by Job Duration. Prioritizes work that delivers the most value in the shortest time.",
      "category": "scoring_model",
      "status": "stable"
    },
    "semantics": {
      "purpose": "Arrange-phase prioritization by converting item-level inputs (Cost of Delay and Job Duration) into a sortable score and rank. Focuses on delivering the most value quickly by favoring high-value, low-effort work.",
      "requires_comparable_items": true,
      "notes_on_comparability": "Items must share the WSJF criteria: cost_of_delay and job_duration. Both must be numeric values greater than zero."
    },
    "inputs": {
      "required_item_fields": [
        "id",
        "label"
      ],
      "required_item_attributes": [
        {
          "key": "cost_of_delay",
          "type": "numeric",
          "description": "The economic impact or value lost per unit of time if this job is delayed. Higher values indicate more urgent/valuable work. Can be relative (1-10 scale) or actual economic impact."
        },
        {
          "key": "job_duration",
          "type": "numeric",
          "description": "The estimated time or effort required to complete this job. Lower values indicate smaller, faster jobs. Must use consistent units (e.g., story points, days, hours)."
        }
      ],
      "optional_item_attributes": [],
      "criteria_set_contract": {
        "allowed_criteria_ids": [
          "cost_of_delay",
          "job_duration"
        ],
        "requires_explicit_criteria_set": false,
        "notes": "If a CriteriaSet is provided, its criteria ids must match these keys. The tool uses a fixed two-criterion model."
      },
      "lens_support": {
        "requires_lens": false,
        "notes": "If a lens is provided, the tool only scores items within that lens."
      },
      "tool_config": {
        "fields": [],
        "supports_defaults": true,
        "notes": "No configuration options currently required. All inputs use the same scale/units as provided."
      }
    },
    "algorithm": {
      "score_model_type": "formula",
      "formula_description": "WSJF = Cost of Delay / Job Duration",
      "formula_variables": {
        "cost_of_delay": "numeric, > 0",
        "job_duration": "numeric, > 0"
      },
      "scaling_and_normalization": {
        "normalizes_cost_of_delay": "none - uses values as provided",
        "normalizes_job_duration": "none - uses values as provided",
        "notes": "Both inputs should use consistent scales/units across all items. The tool does not normalize inputs, so users must ensure comparability."
      },
      "handles_missing_values": {
        "strategy": "reject_item",
        "rules": [
          "If cost_of_delay is missing, null, or <= 0, the item is excluded from scoring.",
          "If job_duration is missing, null, or <= 0, the item is excluded from scoring.",
          "Excluded items are not included in the ScoreSet output."
        ]
      },
      "tie_breaking": {
        "primary": "higher score wins",
        "secondary": "alphabetical by item.label",
        "tertiary": "stable by original import order"
      },
      "determinism": true,
      "notes": "The formula is deterministic and produces the same results for the same inputs. Division by zero is prevented by validation (job_duration must be > 0)."
    },
    "outputs": {
      "score_set_schema": {
        "per_item_fields": [
          {
            "name": "item_id",
            "type": "string"
          },
          {
            "name": "score",
            "type": "numeric",
            "description": "WSJF score = Cost of Delay / Job Duration. Higher scores indicate higher priority."
          },
          {
            "name": "rank",
            "type": "integer",
            "description": "1 = highest priority (highest WSJF score) within the evaluated set."
          }
        ],
        "annotations": [
          {
            "name": "tool_name",
            "type": "string",
            "value": "WSJF"
          },
          {
            "name": "cost_of_delay",
            "type": "numeric",
            "description": "Original Cost of Delay value used in calculation"
          },
          {
            "name": "job_duration",
            "type": "numeric",
            "description": "Original Job Duration value used in calculation"
          }
        ]
      },
      "tool_run_metadata": {
        "records_raw_inputs": true,
        "raw_inputs_schema": {
          "cost_of_delay": "numeric",
          "job_duration": "numeric"
        },
        "additional_fields": {
          "total_items_evaluated": "integer",
          "items_excluded": "integer",
          "exclusion_reasons": "array of strings"
        }
      }
    },
    "interaction_model": {
      "ui_pattern": "sequential_item_scoring",
      "supports_inline_edit": true,
      "supports_batch_edit": false,
      "step_flow": [
        "input_items",
        "score_cost_of_delay",
        "score_job_duration",
        "calculate_wsjf",
        "review_results"
      ],
      "validation_behavior": {
        "on_invalid_item": "prevent_advancement_until_valid",
        "on_any_error": "allow_run_with_warnings",
        "validation_rules": [
          "Cost of Delay must be a positive number",
          "Job Duration must be a positive number",
          "Both values must be provided before proceeding to next item"
        ]
      },
      "ui_notes": "The tool uses a sequential flow: for each item, user first enters Cost of Delay, then Job Duration. Progress is tracked across all items. After all items are scored, WSJF is calculated and results are displayed in ranked order."
    },
    "constraints_and_assumptions": {
      "max_items_tested": 1000,
      "performance_notes": "O(n) over number of items for scoring. Sequential UI flow means O(n) user interactions.",
      "assumes_attribute_types": "Numeric fields must already be parsed by platform. Both cost_of_delay and job_duration must be positive numbers.",
      "assumptions": [
        "Users will use consistent units/scales for all items",
        "Cost of Delay represents economic impact or relative value",
        "Job Duration represents effort/time in consistent units",
        "Smaller jobs (lower duration) with high value (high CoD) are preferred"
      ]
    },
    "test_vectors": [
      {
        "name": "simple_three_item_example",
        "items": [
          {
            "id": "A",
            "label": "Feature A: Quick Win",
            "attributes": {
              "cost_of_delay": 10,
              "job_duration": 2
            }
          },
          {
            "id": "B",
            "label": "Feature B: Big Bet",
            "attributes": {
              "cost_of_delay": 20,
              "job_duration": 8
            }
          },
          {
            "id": "C",
            "label": "Feature C: High Value Fast",
            "attributes": {
              "cost_of_delay": 15,
              "job_duration": 1
            }
          }
        ],
        "tool_config": {},
        "expected_scores": [
          {
            "item_id": "C",
            "score": 15,
            "rank": 1
          },
          {
            "item_id": "A",
            "score": 5,
            "rank": 2
          },
          {
            "item_id": "B",
            "score": 2.5,
            "rank": 3
          }
        ],
        "notes": "Feature C wins because it has the highest WSJF (15/1 = 15), even though Feature B has higher absolute value. This demonstrates WSJF's preference for quick wins."
      },
      {
        "name": "edge_case_equal_scores",
        "items": [
          {
            "id": "D",
            "label": "Item D",
            "attributes": {
              "cost_of_delay": 10,
              "job_duration": 5
            }
          },
          {
            "id": "E",
            "label": "Item E",
            "attributes": {
              "cost_of_delay": 20,
              "job_duration": 10
            }
          }
        ],
        "tool_config": {},
        "expected_scores": [
          {
            "item_id": "D",
            "score": 2,
            "rank": 1
          },
          {
            "item_id": "E",
            "score": 2,
            "rank": 2
          }
        ],
        "notes": "Both items have WSJF = 2.0. Tie-breaking uses alphabetical order by label, so D ranks before E."
      },
      {
        "name": "missing_value_rejection",
        "items": [
          {
            "id": "F",
            "label": "Item F - Valid",
            "attributes": {
              "cost_of_delay": 8,
              "job_duration": 4
            }
          },
          {
            "id": "G",
            "label": "Item G - Missing CoD",
            "attributes": {
              "cost_of_delay": null,
              "job_duration": 3
            }
          },
          {
            "id": "H",
            "label": "Item H - Zero Duration",
            "attributes": {
              "cost_of_delay": 5,
              "job_duration": 0
            }
          }
        ],
        "tool_config": {},
        "expected_scores": [
          {
            "item_id": "F",
            "score": 2,
            "rank": 1
          }
        ],
        "excluded_items": [
          {
            "item_id": "G",
            "reason": "cost_of_delay is missing or invalid"
          },
          {
            "item_id": "H",
            "reason": "job_duration must be greater than zero"
          }
        ],
        "notes": "Items G and H are excluded from scoring due to missing/invalid values. Only Item F is scored."
      }
    ]
  }
];

/**
 * Metadata about when this file was generated.
 */
export const GENERATED_AT = 1793758476612;
export const GENERATED_FROM = ["src/pages/ahp/TOOL_SPEC.json","src/pages/buy-a-feature/TOOL_SPEC.json","src/pages/buy-a-feature/buy-a-feature - TOOL_SPEC.json","src/pages/cost-of-delay/TOOL_SPEC.json","src/pages/cost-of-delay/cost-of-delay - TOOL_SPEC.json","src/pages/dot-voting/TOOL_SPEC.json","src/pages/dot-voting/dot-voting - TOOL_SPEC.json","src/pages/eisenhower-matrix/TOOL_SPEC.json","src/pages/fast-vs-right/TOOL_SPEC.json","src/pages/fast-vs-right/fast-vs-right - TOOL_SPEC.json","src/pages/impact-effort-matrix/TOOL_SPEC.json","src/pages/impact-effort-matrix/impact-effort-matrix - TOOL_SPEC.json","src/pages/magic-prioritization/TOOL_SPEC.json","src/pages/max-priorities-pyramid/TOOL_SPEC.json","src/pages/max-priorities-pyramid/max-priorities-pyramid - TOOL_SPEC.json","src/pages/moscow-method/TOOL_SPEC.json","src/pages/moscow-method/moscow-method - TOOL_SPEC.json","src/pages/opportunity-solution-tree/TOOL_SPEC.json","src/pages/pairwise-ranking/TOOL_SPEC.json","src/pages/pairwise-ranking/pairwise-ranking - TOOL_SPEC.json","src/pages/pareto-analysis/TOOL_SPEC.json","src/pages/pareto-analysis/pareto-analysis - TOOL_SPEC.json","src/pages/prune-product-tree/TOOL_SPEC.json","src/pages/rice-scoring/TOOL_SPEC.json","src/pages/rice-scoring/rice-scoring - TOOL_SPEC.json","src/pages/simple-weighted-scoring/TOOL_SPEC.json","src/pages/simple-weighted-scoring/simple-weighted-scoring - TOOL_SPEC.json","src/pages/speed-boat/TOOL_SPEC.json","src/pages/stack-ranking/TOOL_SPEC.json","src/pages/stack-ranking/stack-ranking - TOOL_SPEC.json","src/pages/wsjf/TOOL_SPEC.json","src/pages/wsjf/wsjf - TOOL_SPEC.json"];
